
import { legacyApi, EstimateDetail, CalculateRequest, MaterialType, Unit, MouldType } from '../../../services/legacyApi';
import { api, authorizedFetch } from '../../../services/api';
import { API_BASE_URL } from '../../../config/api';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Eye, Link2, Loader2, MessageCircle, Search, Send, Snowflake, Trash2, Truck } from 'lucide-react';

import { LabourCostChartModal } from '../../../components/LabourCostChartModal';
import { DesignMixModal } from '../../../components/DesignMixModal';
import { toast } from 'sonner';
import { useConfirm } from '../../../hooks/use-confirm';
import { EstimateOrdersModal } from '../../../components/EstimateOrdersModal';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../../components/ui/accordion';
import { SendPdfModal } from '../../../components/SendPdfModal';
import { useAuth } from '../../../context/AuthContext';
import { openWhatsAppMessage } from '../utils/whatsapp';
import { OverheadCostManager } from '../../../pages/Settings/OverheadCostManager';
import { getEstimateBaseNo, getEstimateRef } from '../../../utils/estimateRefs';
import {
    applyInstallationLabourSplitToItems,
    calculateInstallationLineCost,
    computeInstallationLabourSplit,
    isInstallationLabourAutoRow,
    sumInstallationPartManhours,
} from './installationMath';


const normalizePercent = (val: any) => {
    const v = parseFloat(val);
    if (isNaN(v)) return 0;
    // Support both percent (5, 20) and fraction (0.05, 0.2) formats.
    // Return as a fraction (0..1) for calculations.
    return v > 1.0 ? v / 100 : v;
};

const formatPercent = (val: any) => (normalizePercent(val) * 100);
const formatCompactNumber = (val: any, allowWhole: boolean = false) => {
    const num = Number(val || 0);
    const whole = Math.abs(num - Math.round(num)) < 0.005;
    return num.toLocaleString(undefined, {
        minimumFractionDigits: allowWhole && whole ? 0 : 2,
        maximumFractionDigits: 2,
    });
};
const formatCurrency = (val: number, allowWhole: boolean = false) => formatCompactNumber(val, allowWhole);
const roundInstallationManhours = (val: any) => {
    const num = Number(val ?? 0);
    return Number.isFinite(num) ? Number(num.toFixed(2)) : 0;
};
const normalizeInstallationDraftItem = (item: any) => ({
    ...item,
    man_hours: roundInstallationManhours(item?.man_hours),
});
const toFractionPercent = (val: any) => {
    const v = parseFloat(val);
    if (isNaN(v)) return 0;
    return v >= 1.0 ? v / 100 : v;
};
const toWholePercent = (val: any) => {
    const v = parseFloat(val);
    if (isNaN(v)) return 0;
    return v <= 1.0 ? v * 100 : v;
};

const COST_BREAKDOWN_GROUPS_STORAGE_KEY = 'legacy-estimate-classic-costbreakdown-groups';
const DESIGN_PREMIUM_REVIEW_SECTION_ID = 'estimate-design-premium-review';
const COST_BREAKDOWN_WIDTH_BUDGET = {
    base: 1092,
    directBreakdown: 196,
    otherCosts: 250,
    buffer: 24,
};

export const LegacyEstimateDetailEnhanced: React.FC = () => {
    const { id } = useParams<{ id: string }>();

    const navigate = useNavigate();
    const { confirm } = useConfirm();
    const location = useLocation();
    const [estimate, setEstimate] = useState<EstimateDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [openModal, setOpenModal] = useState<'inputs' | 'costbreakdown' | 'overview' | 'parts' | 'tooling' | 'production' | 'bom' | 'installations' | 'pricing' | 'admin' | 'overheadsheet' | 'quotationpreview' | 'generate' | 'convert' | null>('inputs');
    const [inputsAccordionValues, setInputsAccordionValues] = useState<string[]>([]);
    const [showLabourModal, setShowLabourModal] = useState(false);
    const [showDesignModal, setShowDesignModal] = useState(false);
    const [showInstallationModal, setShowInstallationModal] = useState(false);
    const [showGRPMaterialsModal, setShowGRPMaterialsModal] = useState(false);
    const [selectedPartForMaterials, setSelectedPartForMaterials] = useState<number | null>(null);
    const [showBOMMaterialsModal, setShowBOMMaterialsModal] = useState(false);
    const [selectedPartForBOM, setSelectedPartForBOM] = useState<number | null>(null);
    const [loadingFactors, setLoadingFactors] = useState(false);
    const [ohSheetRatePerSqm, setOhSheetRatePerSqm] = useState<number | null>(null);
    // Removed unused convert state
    const [grpCatalog, setGrpCatalog] = useState<any[] | null>(null);
    const [grpDraft, setGrpDraft] = useState<any[] | null>(null);
    const [grpDraftLabourRate, setGrpDraftLabourRate] = useState<string>('');
    const [savingGrpDraft, setSavingGrpDraft] = useState(false);
    const [bomCatalog, setBomCatalog] = useState<any[] | null>(null);
    const [bomDraft, setBomDraft] = useState<any[] | null>(null);
    const [savingBomDraft, setSavingBomDraft] = useState(false);

    const [customers, setCustomers] = useState<any[]>([]);
    const [defaults, setDefaults] = useState<Record<string, string>>({});
    const [draftHeader, setDraftHeader] = useState<any>(null);
    const [draftParts, setDraftParts] = useState<any[] | null>(null);
    const [savingInputs, setSavingInputs] = useState(false);
    const [materialTypes, setMaterialTypes] = useState<MaterialType[]>([]);
    const [units, setUnits] = useState<Unit[]>([]);
    const [mouldTypes, setMouldTypes] = useState<MouldType[]>([]);
    const [grcPartMode, setGrcPartMode] = useState(false);
    const [showGrcOverrideModal, setShowGrcOverrideModal] = useState(false);
    const [selectedGrcOverridePartId, setSelectedGrcOverridePartId] = useState<number | null>(null);
    const [grcOverrideDraft, setGrcOverrideDraft] = useState<{ design_mix_model_id: string; grc_type_id: string; labour_rate_per_kg: string }>({
        design_mix_model_id: '',
        grc_type_id: '',
        labour_rate_per_kg: '',
    });
    const [designMixModels, setDesignMixModels] = useState<any[]>([]);
    const [grcTypes, setGrcTypes] = useState<any[]>([]);
    const [savingGrcOverride, setSavingGrcOverride] = useState(false);
    const costBreakdownRef = useRef<HTMLDivElement | null>(null);
    const quotationPreviewFrameRef = useRef<HTMLIFrameElement | null>(null);
    const [costBreakdownWidth, setCostBreakdownWidth] = useState<number>(COST_BREAKDOWN_WIDTH_BUDGET.base + COST_BREAKDOWN_WIDTH_BUDGET.directBreakdown + COST_BREAKDOWN_WIDTH_BUDGET.otherCosts + COST_BREAKDOWN_WIDTH_BUDGET.buffer);
    const [quotationPreviewHeight, setQuotationPreviewHeight] = useState<number>(1320);
    const [overheadSheetReturnState, setOverheadSheetReturnState] = useState<{
        modal: 'inputs' | 'admin';
        accordionValues: string[];
        syncEstimateWithDefault: boolean;
        previousSheetRate: number;
    } | null>(null);
    const [costBreakdownGroupPrefs, setCostBreakdownGroupPrefs] = useState<{ directBreakdown: boolean; otherCosts: boolean }>(() => {
        if (typeof window === 'undefined') return { directBreakdown: true, otherCosts: true };
        try {
            const raw = window.localStorage.getItem(COST_BREAKDOWN_GROUPS_STORAGE_KEY);
            if (!raw) return { directBreakdown: true, otherCosts: true };
            const parsed = JSON.parse(raw);
            return {
                directBreakdown: parsed?.directBreakdown !== false,
                otherCosts: parsed?.otherCosts !== false,
            };
        } catch {
            return { directBreakdown: true, otherCosts: true };
        }
    });
    const [costBreakdownManualGroups, setCostBreakdownManualGroups] = useState<boolean>(() => {
        if (typeof window === 'undefined') return false;
        try {
            return Boolean(window.localStorage.getItem(COST_BREAKDOWN_GROUPS_STORAGE_KEY));
        } catch {
            return false;
        }
    });
    const [draftInstallItems, setDraftInstallItems] = useState<any[] | null>(null);
    const [savingInstallItems, setSavingInstallItems] = useState(false);
    const adminInputClass = "w-full border rounded py-2 px-3 text-sm focus:ring-2 focus:ring-blue-500";

    const loadOverheadSummary = async () => {
        try {
            const summary = await api.overhead.getSummary();
            const nextRate = Number(summary?.total_rate_per_sqm || 0);
            const normalizedRate = Number.isFinite(nextRate) && nextRate > 0 ? nextRate : 0;
            setOhSheetRatePerSqm(normalizedRate);
            return normalizedRate;
        } catch (err) {
            console.error('Failed to load overhead summary', err);
            return 0;
        }
    };

    useEffect(() => {
        if (!id) return;
        legacyApi.estimates.getDetail(parseInt(id))
            .then((data: EstimateDetail) => {
                setEstimate(data);
                setLoading(false);
            })
            .catch((err: Error) => {
                console.error(err);
                setLoading(false);
            });
    }, [id]);

    useEffect(() => {
        legacyApi.customers.list().then(setCustomers).catch(console.error);
    }, []);

    useEffect(() => {
        let active = true;
        (async () => {
            const nextRate = await loadOverheadSummary();
            if (!active) return;
            setOhSheetRatePerSqm(nextRate);
        })();
        return () => {
            active = false;
        };
    }, []);

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const res = await fetch(`${API_BASE_URL}/system/settings/defaults`, { headers: { 'Content-Type': 'application/json' } });
                if (!res.ok) return;
                const data = await res.json();
                const dict = (data || []).reduce((acc: Record<string, string>, s: any) => {
                    acc[s.setting_name] = String(s.setting_value ?? '');
                    return acc;
                }, {});
                if (!active) return;
                setDefaults(dict);
            } catch (err) {
                console.error('Failed to fetch defaults', err);
            }
        })();
        return () => { active = false };
    }, []);

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const [types, unitList, mouldList] = await Promise.all([
                    legacyApi.masterData.listMaterialTypes(),
                    legacyApi.masterData.listUnits(),
                    legacyApi.masterData.listMouldTypes()
                ]);
                if (!active) return;
                setMaterialTypes(types);
                setUnits(unitList);
                setMouldTypes(mouldList);
            } catch (err) {
                console.error('Failed to load master lists', err);
            }
        })();
        return () => {
            active = false;
        };
    }, []);

    useEffect(() => {
        if (openModal !== 'quotationpreview') return;
        const handleResize = () => updateQuotationPreviewHeight();
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [openModal, estimate?.estimate_id]);

    useEffect(() => {
        if (!showGRPMaterialsModal || !selectedPartForMaterials) return;
        let cancelled = false;
        (async () => {
            try {
                const [catalog, production] = await Promise.all([
                    grpCatalog ? Promise.resolve(grpCatalog) : legacyApi.materials.listGrp(),
                    legacyApi.estimates.getGrpProduction(selectedPartForMaterials),
                ]);
                if (cancelled) return;
                if (!grpCatalog) setGrpCatalog(catalog);
                setGrpDraft(production?.items || []);
                setGrpDraftLabourRate(String(production?.labour_rate_per_m2 || ''));
            } catch (e) {
                console.error(e);
                if (!cancelled) {
                    setGrpDraft([]);
                    setGrpDraftLabourRate('');
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [showGRPMaterialsModal, selectedPartForMaterials, grpCatalog]);

    useEffect(() => {
        if (!showBOMMaterialsModal || !selectedPartForBOM || !estimate) return;
        let cancelled = false;
        (async () => {
            try {
                const [catalog] = await Promise.all([
                    bomCatalog ? Promise.resolve(bomCatalog) : legacyApi.bom.listCatalog(),
                ]);
                if (cancelled) return;
                if (!bomCatalog) setBomCatalog(catalog);
                const existing = estimate.parts.find(p => p.estimate_part_id === selectedPartForBOM)?.bom_items || [];
                setBomDraft(existing.map((item: any) => ({
                    bom_material_id: item.bom_material_id,
                    material_id: item.material_id ?? (catalog.find((m: any) => m.bom_material_id === item.bom_material_id)?.material_id ?? ''),
                    quantity: item.quantity,
                    unit_rate: item.unit_rate,
                    material_name: item.material_name
                })));
            } catch (e) {
                console.error(e);
                if (!cancelled) setBomDraft([]);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [showBOMMaterialsModal, selectedPartForBOM, estimate, bomCatalog]);

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const [mixes, types] = await Promise.all([
                    legacyApi.getDesignMixModels(),
                    legacyApi.getGRCTypes(),
                ]);
                if (!active) return;
                setDesignMixModels(mixes || []);
                setGrcTypes(types || []);
            } catch (err) {
                console.error('Failed to load GRC override catalogs', err);
            }
        })();
        return () => {
            active = false;
        };
    }, []);

    const [showOrdersModal, setShowOrdersModal] = useState(false);
    const [showSendPdfModal, setShowSendPdfModal] = useState(false);
    const { hasPermission } = useAuth();

    useEffect(() => {
        if (!estimate) return;
        // Keep a draft copy used for Inputs/Tooling/Production edits.
        setDraftHeader({
            customer_id: estimate.customer_id || '',
            profit_margin: estimate.profit_margin ?? 0,
            discount_percent: estimate.discount_percent ?? 0,
            vat_percent: estimate.vat_percent ?? 0,
            per_hour_labour_rate: estimate.per_hour_labour_rate ?? 50,
            oh_per_sqm: estimate.oh_per_sqm ?? 10,
            project_description: estimate.project_description ?? '',
            attention: estimate.attention ?? '',
            enquiry_ref: estimate.enquiry_ref ?? 'Mail Ref/Whatsapp Ref',
            customer_address: estimate.customer_address ?? '',
        });
        setDraftParts(
            (estimate.parts || []).map((p: any) => ({
                estimate_part_id: p.estimate_part_id,
                part_name: p.part_name || '',
                dimension: p.dimension || '',
                part_ref_code: p.part_ref_code || p.formatted_part_id || '',
                quantity: p.quantity ?? 0,
                area_m2: p.area_m2 ?? 0,
                thickness: p.thickness ?? 0,
                weight_kgs: p.weight_kgs ?? 0,
                man_hours_required: p.man_hours_required ?? 0,
                man_hours_per_unit: p.quantity ? (p.man_hours_required ?? 0) / p.quantity : 0,
                mould_area: p.mould_area ?? 0,
                mould_cost_m2: p.mould_cost_m2 ?? 0,
                mould_quantity: p.mould_quantity ?? 1,
                m2_per_hour_production: p.m2_per_hour_production ?? 0,
                design_premium_perc: p.design_premium_perc ?? 0,
                grp_labour_rate_per_m2: p.grp_labour_rate_per_m2 ?? 0,
                grc_design_mix_model_id: p.grc_design_mix_model_id ?? null,
                grc_type_id: p.grc_type_id ?? null,
                grc_labour_rate_per_kg: p.grc_labour_rate_per_kg ?? 0,
                bom_items: p.bom_items || [],
                material_type_id: p.material_type_id ?? defaultMaterialTypeId(),
                unit_id: p.unit_id ?? defaultUnitId(),
                mould_type_id: p.mould_type_id ?? defaultMouldTypeId(),
            }))
        );
    }, [estimate]);

    // Ensure Customer select is pre-populated when estimate has only customer_name (no customer_id)
    useEffect(() => {
        if (!estimate || !customers?.length || !draftHeader) return;
        const noCustomerSelected = draftHeader.customer_id === '' || draftHeader.customer_id === undefined || draftHeader.customer_id === null;
        if (noCustomerSelected && estimate.customer_name) {
            const match = customers.find((c: any) => (c.customer_name || '').toLowerCase() === (estimate.customer_name || '').toLowerCase());
            if (match?.customer_id) {
                setDraftHeader((prev: any) => ({ ...prev, customer_id: match.customer_id }));
            }
        }
    }, [estimate, customers, draftHeader]);

    useEffect(() => {
        if (!estimate) return;
        setDraftInstallItems((estimate.installation_items || []).map(normalizeInstallationDraftItem));
    }, [estimate?.installation_items]);

    useEffect(() => {
        if (!estimate) return;
        const fieldsToCheck = [
            estimate.profit_margin,
            estimate.discount_percent,
            estimate.vat_percent,
            estimate.logistics_packing_loading_perc,
            estimate.logistics_freight_cost_perc,
            estimate.admin_contigency_perc,
            estimate.admin_warranty_perc,
            estimate.admin_retention_perc,
            estimate.admin_banking_finance_perc,
            estimate.admin_other_cost_perc,
            estimate.skilled_manhours_perc,
            estimate.unskilled_manhours_perc,
        ];
        const allMissing = fieldsToCheck.every((val) => val === null || val === undefined || Number(val) === 0);
        if (!allMissing) return;

        const fetchDefaults = async () => {
            const res = await fetch(`${API_BASE_URL}/system/settings/defaults`, {
                headers: { 'Content-Type': 'application/json' },
            });
            if (!res.ok) return [];
            return res.json();
        };

        (async () => {
            try {
                const data = await fetchDefaults();
                const dict = (data || []).reduce((acc: Record<string, string>, s: any) => {
                    acc[s.setting_name] = String(s.setting_value ?? '');
                    return acc;
                }, {});

                const updates: any = {};
                const setIfMissing = (field: string, value: any) => {
                    const current = (estimate as any)?.[field];
                    if (current === null || current === undefined || Number(current) === 0) {
                        updates[field] = value;
                    }
                };

                setIfMissing('profit_margin', toFractionPercent(dict['Default Profit Margin %']));
                setIfMissing('discount_percent', toFractionPercent(dict['Default Discount %']));
                setIfMissing('vat_percent', toFractionPercent(dict['VAT Rate']));
                setIfMissing('oh_per_sqm', parseFloat(dict['Overhead Per SQM'] || '0') || 0);

                setIfMissing('logistics_packing_loading_perc', toFractionPercent(dict['Logistics Packing & Loading %']));
                setIfMissing('logistics_freight_cost_perc', toFractionPercent(dict['Logistics Freight %']));
                setIfMissing('admin_contigency_perc', toFractionPercent(dict['Admin Contingency %']));
                setIfMissing('admin_warranty_perc', toFractionPercent(dict['Admin Warranty %']));
                setIfMissing('admin_retention_perc', toFractionPercent(dict['Admin Retention %']));
                setIfMissing('admin_banking_finance_perc', toFractionPercent(dict['Admin Banking & Finance %']));
                setIfMissing('admin_other_cost_perc', toFractionPercent(dict['Admin Other Costs %']));

                setIfMissing('skilled_manhours_perc', toWholePercent(dict['Skilled Manhours %'] || '50'));
                setIfMissing('unskilled_manhours_perc', toWholePercent(dict['Unskilled Manhours %'] || '50'));

                if (!Object.keys(updates).length) return;
                await legacyApi.estimates.updateAdminControls(estimate.estimate_id, updates);
                const refreshed = await legacyApi.estimates.getDetail(estimate.estimate_id);
                setEstimate(refreshed);
            } catch (err) {
                console.error('Failed to seed admin defaults', err);
            }
        })();
    }, [estimate]);

    useEffect(() => {
        if (!estimate) return;
        const needsAnnexure =
            !estimate.validity || !estimate.annexure_a;
        if (!needsAnnexure) return;
        (async () => {
            try {
                const res = await fetch(`${API_BASE_URL}/system/settings/defaults`, {
                    headers: { 'Content-Type': 'application/json' },
                });
                if (!res.ok) return;
                const data = await res.json();
                const dict = (data || []).reduce((acc: Record<string, string>, s: any) => {
                    acc[s.setting_name] = String(s.setting_value ?? '');
                    return acc;
                }, {});

                const updates: any = {};
                if (!estimate.validity && dict['Validity']) updates.validity = dict['Validity'];
                if (!estimate.annexure_a && dict['Annexure A']) updates.annexure_a = dict['Annexure A'];
                if (!Object.keys(updates).length) return;
                await legacyApi.estimates.updateAdminControls(estimate.estimate_id, updates);
                const refreshed = await legacyApi.estimates.getDetail(estimate.estimate_id);
                setEstimate(refreshed);
            } catch (err) {
                console.error('Failed to seed annexure defaults', err);
            }
        })();
    }, [estimate?.estimate_id]);

    useEffect(() => {
        if (!estimate) return;
        const percentFields = [
            'profit_margin',
            'discount_percent',
            'vat_percent',
            'logistics_packing_loading_perc',
            'logistics_freight_cost_perc',
            'admin_contigency_perc',
            'admin_warranty_perc',
            'admin_retention_perc',
            'admin_banking_finance_perc',
            'admin_other_cost_perc',
        ];
        const updates: any = {};
        for (const field of percentFields) {
            const current = (estimate as any)?.[field];
            if (current !== null && current !== undefined) {
                const normalized = toFractionPercent(current);
                if (normalized !== current) updates[field] = normalized;
            }
        }
        if (!Object.keys(updates).length) return;
        (async () => {
            try {
                await legacyApi.estimates.updateAdminControls(estimate.estimate_id, updates);
                const refreshed = await legacyApi.estimates.getDetail(estimate.estimate_id);
                setEstimate(refreshed);
            } catch (err) {
                console.error('Failed to normalize admin percentages', err);
            }
        })();
    }, [estimate?.estimate_id]);

    useEffect(() => {
        if (!estimate) return;
        const needsDefaults =
            (estimate.direct_electricity_perc ?? 0) === 0 ||
            (estimate.direct_wastage_perc ?? 0) === 0;
        if (!needsDefaults) return;
        legacyApi.estimates
            .updateProductionSettings(estimate.estimate_id, {
                direct_electricity_perc: (estimate.direct_electricity_perc ?? 0) || 0.06,
                direct_wastage_perc: (estimate.direct_wastage_perc ?? 0) || 0.10,
            })
            .then(() => legacyApi.estimates.getDetail(estimate.estimate_id))
            .then(setEstimate)
            .catch((err) => console.error('Failed to seed production defaults', err));
    }, [estimate?.estimate_id]);

    useEffect(() => {
        if (!estimate || !ohSheetRatePerSqm || Number(estimate.oh_per_sqm || 0) > 0) return;
        setEstimate((prev) => {
            if (!prev || Number(prev.oh_per_sqm || 0) > 0) return prev;
            return { ...prev, oh_per_sqm: ohSheetRatePerSqm };
        });
    }, [estimate, ohSheetRatePerSqm]);

    useEffect(() => {
        // Support deep-linking to a specific tab (e.g. ?tab=inputs) and legacy /edit links.
        const params = new URLSearchParams(location.search || '');
        const tab = params.get('tab');
        const requested = (location.pathname || '').endsWith('/edit') ? 'inputs' : tab;
        if (!requested) return;
        const allowed = new Set(['inputs', 'costbreakdown', 'overview', 'tooling', 'production', 'bom', 'installations', 'pricing', 'admin', 'overheadsheet', 'quotationpreview', 'generate', 'convert']);
        if (allowed.has(requested)) setOpenModal(requested as any);
    }, [location.pathname, location.search]);

    useEffect(() => {
        const node = costBreakdownRef.current;
        if (!node || typeof ResizeObserver === 'undefined') return;
        setCostBreakdownWidth(node.getBoundingClientRect().width);

        const observer = new ResizeObserver((entries) => {
            const entry = entries[0];
            if (!entry) return;
            setCostBreakdownWidth(entry.contentRect.width);
        });
        observer.observe(node);

        return () => observer.disconnect();
    }, [openModal]);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        window.localStorage.setItem(COST_BREAKDOWN_GROUPS_STORAGE_KEY, JSON.stringify(costBreakdownGroupPrefs));
    }, [costBreakdownGroupPrefs]);

    const canEdit = !!estimate && !estimate.is_freezed && estimate.approval_status_id !== 4 && estimate.approval_status_id !== 5;

    const defaultMaterialTypeId = () => (estimate?.project_type_id === 2 ? 2 : 1);
    const defaultUnitId = () => (units[0]?.unit_id ?? 2);
    const defaultMouldTypeId = () => (mouldTypes[0]?.mould_type_id ?? 1);
    const getMouldTypeCost = (typeId?: number) => mouldTypes.find(mt => mt.mould_type_id === typeId)?.mould_cost ?? 0;

    const getMaterialDensityKgPerM3 = (projectTypeId?: number): number | null => {
        // Use density only from Defaults settings.
        try {
            if (projectTypeId === 2) {
                const v = defaults['GRP/FRP Material Density'] || defaults['GRP/FRP Density'];
                return v ? parseFloat(v) : null;
            }
            const v = defaults['GRC Material Density'] || defaults['GRP/FRP Material Density'];
            return v ? parseFloat(v) : null;
        } catch (e) {
            return null;
        }
    };

    const recomputeLineWeight = (row: any) => {
        const area = parseFloat(row.area_m2 || 0);
        const thick = parseFloat(row.thickness || 0);
        const qty = parseFloat(row.quantity || 0);
        if (area > 0 && thick > 0 && qty > 0) {
            const density = getMaterialDensityKgPerM3(estimate?.project_type_id);
            // Only compute when density is explicitly configured in Defaults.
            if (density !== null && !isNaN(density)) {
                return parseFloat((area * (thick / 1000) * density * qty).toFixed(2));
            }
        }
        return row.weight_kgs || 0;
    };

    const resolvePartUnitLabel = (part: any) => {
        const selectedUnitId = parseInt(part.unit_id || defaultUnitId(), 10);
        return units.find((unit) => Number(unit.unit_id) === Number(selectedUnitId))?.unit || part.unit_label || part.unit_name || 'Nos.';
    };


    const buildUpdatePayload = (headerDraft: any, partsDraft: any[]): CalculateRequest => {
        const installationSplit = computeInstallationLabourSplit(
            sumInstallationPartManhours(partsDraft),
            headerDraft.skilled_manhours_perc ?? (estimate as any)?.skilled_manhours_perc ?? 0,
            headerDraft.unskilled_manhours_perc ?? (estimate as any)?.unskilled_manhours_perc ?? 0,
            headerDraft.non_working_hours_per_week ?? (estimate as any)?.non_working_hours_per_week ?? 8,
        );
        const installationItems = applyInstallationLabourSplitToItems(
            draftInstallItems || estimate?.installation_items || [],
            installationSplit,
        );

        return {
            header: {
                customer_id: headerDraft.customer_id ? parseInt(headerDraft.customer_id) : undefined,
                customer_name: customers.find(c => c.customer_id == headerDraft.customer_id)?.customer_name || estimate?.customer_name,
                project_type_id: estimate?.project_type_id || 1,
                project_description: headerDraft.project_description || estimate?.project_description,
                attention: headerDraft.attention ?? estimate?.attention,
                enquiry_ref: headerDraft.enquiry_ref ?? estimate?.enquiry_ref,
                customer_address: headerDraft.customer_address ?? estimate?.customer_address,
                validity: headerDraft.validity ?? estimate?.validity,
                annexure_a: headerDraft.annexure_a ?? estimate?.annexure_a,
                annexure_b: headerDraft.annexure_b ?? estimate?.annexure_b,
                labour_rate_per_hour: parseFloat(headerDraft.labour_rate_per_hour ?? headerDraft.per_hour_labour_rate ?? 0),
                overhead_per_sqm: parseFloat(headerDraft.overhead_per_sqm ?? headerDraft.oh_per_sqm ?? 0),
                oh_per_sqm: parseFloat(headerDraft.overhead_per_sqm ?? headerDraft.oh_per_sqm ?? 0),
                profit_margin: parseFloat(headerDraft.profit_margin ?? 0),
                discount_percent: parseFloat(headerDraft.discount_percent ?? 0),
                vat_percent: parseFloat(headerDraft.vat_percent ?? 0),
                scope_reviewed: Boolean(headerDraft.scope_reviewed ?? estimate?.scope_reviewed),
                admin_reviewed: Boolean(headerDraft.admin_reviewed ?? estimate?.admin_reviewed),
                design_premium_reviewed: Boolean(headerDraft.design_premium_reviewed ?? estimate?.design_premium_reviewed),

                tooling_consumable_perc: Number(headerDraft.tooling_consumable_perc ?? (estimate as any)?.tooling_consumable_perc ?? 0),
                tooling_jigs_fixtures_perc: Number(headerDraft.tooling_jigs_fixtures_perc ?? (estimate as any)?.tooling_jigs_fixtures_perc ?? 0),
                tooling_misc_perc: Number(headerDraft.tooling_misc_perc ?? (estimate as any)?.tooling_misc_perc ?? 0),
                logistics_freight_cost_perc: parseFloat(headerDraft.logistics_freight_cost_perc ?? (estimate as any)?.logistics_freight_cost_perc ?? 0),
                logistics_packing_loading_perc: parseFloat(headerDraft.logistics_packing_loading_perc ?? (estimate as any)?.logistics_packing_loading_perc ?? 0),
                skilled_manhours_perc: parseFloat(headerDraft.skilled_manhours_perc ?? (estimate as any)?.skilled_manhours_perc ?? 0),
                unskilled_manhours_perc: parseFloat(headerDraft.unskilled_manhours_perc ?? (estimate as any)?.unskilled_manhours_perc ?? 0),
                non_working_hours_per_week: parseFloat(headerDraft.non_working_hours_per_week ?? (estimate as any)?.non_working_hours_per_week ?? 8),
                admin_contigency_perc: Number(headerDraft.admin_contigency_perc ?? (estimate as any)?.admin_contigency_perc ?? 0),
                admin_warranty_perc: Number(headerDraft.admin_warranty_perc ?? (estimate as any)?.admin_warranty_perc ?? 0),
                admin_retention_perc: Number(headerDraft.admin_retention_perc ?? (estimate as any)?.admin_retention_perc ?? 0),
                admin_banking_finance_perc: Number(headerDraft.admin_banking_finance_perc ?? (estimate as any)?.admin_banking_finance_perc ?? 0),
                admin_other_cost_perc: Number(headerDraft.admin_other_cost_perc ?? (estimate as any)?.admin_other_cost_perc ?? 0),
                prod_aed_per_m2_grp: Number(headerDraft.prod_aed_per_m2_grp ?? (estimate as any)?.prod_aed_per_m2_grp ?? 0),
                grp_misc_consumables_perc: Number(headerDraft.grp_misc_consumables_perc ?? (estimate as any)?.grp_misc_consumables_perc ?? 0),
                prod_aed_per_kg: Number(headerDraft.prod_aed_per_kg ?? (estimate as any)?.prod_aed_per_kg ?? 0),
                apply_auxiliary_to_grc: headerDraft.apply_auxiliary_to_grc ?? (estimate as any)?.apply_auxiliary_to_grc ?? true,
                apply_auxiliary_to_grp: headerDraft.apply_auxiliary_to_grp ?? (estimate as any)?.apply_auxiliary_to_grp ?? true,
                direct_wastage_perc: Number(headerDraft.direct_wastage_perc ?? (estimate as any)?.direct_wastage_perc ?? 0),
                direct_painting_labour_perc: Number(headerDraft.direct_painting_labour_perc ?? (estimate as any)?.direct_painting_labour_perc ?? 0),
                direct_electricity_perc: Number(headerDraft.direct_electricity_perc ?? (estimate as any)?.direct_electricity_perc ?? 0),
                direct_acid_wash_labour_perc: Number(headerDraft.direct_acid_wash_labour_perc ?? (estimate as any)?.direct_acid_wash_labour_perc ?? 0),
                direct_styro_foam: Number(headerDraft.direct_styro_foam ?? (estimate as any)?.direct_styro_foam ?? 0),
                direct_production_accessories: Number(headerDraft.direct_production_accessories ?? (estimate as any)?.direct_production_accessories ?? 0),

                pm_design_cost: Number(headerDraft.pm_design_cost ?? (estimate as any)?.pm_design_cost ?? 0),
                pm_project_manager_cost: Number(headerDraft.pm_project_manager_cost ?? (estimate as any)?.pm_project_manager_cost ?? 0),
                pm_site_intervention_cost: Number(headerDraft.pm_site_intervention_cost ?? (estimate as any)?.pm_site_intervention_cost ?? 0),
                pm_documentation_cost: Number(headerDraft.pm_documentation_cost ?? (estimate as any)?.pm_documentation_cost ?? 0),
                pm_travel_cost: Number(headerDraft.pm_travel_cost ?? (estimate as any)?.pm_travel_cost ?? 0),
            },
            parts: partsDraft.map((p: any) => ({
                estimate_part_id: p.estimate_part_id,
                part_name: p.part_name,
                dimension: p.dimension || '',
                part_ref_code: p.part_ref_code || p.formatted_part_id || undefined,
                quantity: parseFloat(p.quantity || 0),
                area_m2: parseFloat(p.area_m2 || 0),
                thickness: parseFloat(p.thickness || 0),
                weight_kgs: recomputeLineWeight(p),
                mould_area: parseFloat(p.mould_area || 0),
                mould_quantity: parseFloat(p.mould_quantity || 1),
                mould_cost_m2: parseFloat(p.mould_cost_m2 || 0),
                m2_per_hour_production: parseFloat(p.m2_per_hour_production || 0),
                man_hours_required: parseFloat(p.man_hours_required || 0),
                material_type_id: parseInt(p.material_type_id || defaultMaterialTypeId(), 10),
                unit_id: parseInt(p.unit_id || defaultUnitId(), 10),
                unit_label: resolvePartUnitLabel(p),
                mould_type_id: parseInt(p.mould_type_id || defaultMouldTypeId(), 10),
                bom_cost: 0,
                installation_cost: 0,
                packing_cost: 0,
                freight_cost: 0,
                project_fee: 0,
                admin_cost: 0,
                design_premium_perc: parseFloat(p.design_premium_perc || 0),
                grp_labour_rate_per_m2: parseFloat(p.grp_labour_rate_per_m2 || 0),
                grc_design_mix_model_id: p.grc_design_mix_model_id ? parseInt(p.grc_design_mix_model_id, 10) : undefined,
                grc_type_id: p.grc_type_id ? parseInt(p.grc_type_id, 10) : undefined,
                grc_labour_rate_per_kg: parseFloat(p.grc_labour_rate_per_kg || 0),
                bom_items: (p.bom_items || []).map((bi: any) => ({
                    bom_material_id: parseInt(bi.bom_material_id, 10),
                    quantity: parseFloat(bi.quantity || 0),
                    unit_rate: parseFloat(bi.unit_rate || 0),
                })),
            })),
            installation_items: installationItems.map((item: any) => ({
                estimate_inst_id: item.estimate_inst_id,
                main_category: item.main_category,
                sub_category: item.sub_category,
                quantity: parseFloat(item.quantity || 0),
                unit: item.unit,
                unit_rate: parseFloat(item.unit_rate || 0),
                man_hours: parseFloat(item.man_hours || 0),
                cost: calculateInstallationLineCost(item.man_hours, item.quantity, item.unit_rate),
            })),
        };
    };

    const handleInputsSave = async (partsOverride?: any[]) => {
        if (!estimate || !draftHeader || !draftParts) return;
        if (!canEdit) return;
        try {
            setSavingInputs(true);

            const payload = buildUpdatePayload(draftHeader, partsOverride || draftParts);

            await legacyApi.estimates.update(estimate.estimate_id, payload);
            const updated = await legacyApi.estimates.getDetail(estimate.estimate_id);
            setEstimate(updated);
            setDraftInstallItems((updated.installation_items || []).map(normalizeInstallationDraftItem));
            setDraftParts(
                (updated.parts || []).map((p: any) => ({
                    estimate_part_id: p.estimate_part_id,
                    part_name: p.part_name || '',
                    dimension: p.dimension || '',
                    part_ref_code: p.part_ref_code || p.formatted_part_id || '',
                    quantity: p.quantity ?? 0,
                    area_m2: p.area_m2 ?? 0,
                    thickness: p.thickness ?? 0,
                    weight_kgs: p.weight_kgs ?? 0,
                    man_hours_required: p.man_hours_required ?? 0,
                    man_hours_per_unit: p.quantity ? (p.man_hours_required ?? 0) / p.quantity : 0,
                    mould_area: p.mould_area ?? 0,
                    mould_cost_m2: p.mould_cost_m2 ?? 0,
                    mould_quantity: p.mould_quantity ?? 1,
                    m2_per_hour_production: p.m2_per_hour_production ?? 0,
                    design_premium_perc: p.design_premium_perc ?? 0,
                    grp_labour_rate_per_m2: p.grp_labour_rate_per_m2 ?? 0,
                    grc_design_mix_model_id: p.grc_design_mix_model_id ?? null,
                    grc_type_id: p.grc_type_id ?? null,
                    grc_labour_rate_per_kg: p.grc_labour_rate_per_kg ?? 0,
                    bom_items: p.bom_items || [],
                    material_type_id: p.material_type_id ?? defaultMaterialTypeId(),
                    unit_id: p.unit_id ?? defaultUnitId(),
                    mould_type_id: p.mould_type_id ?? defaultMouldTypeId(),
                }))
            );
            toast.success('Estimate inputs saved successfully');
            // setOpenModal('costbreakdown');
        } catch (err: any) {
            toast.error(err?.message || 'Failed to save inputs');
            console.error(err);
        } finally {
            setSavingInputs(false);
        }
    };

    const handleSaveProductionSettings = async () => {
        if (!estimate) return;
        if (!canEdit) return;
        try {
            await legacyApi.estimates.updateProductionSettings(estimate.estimate_id, {
                prod_aed_per_kg: estimate.prod_aed_per_kg || 0,
                prod_aed_per_m2_grp: estimate.prod_aed_per_m2_grp || 0,
                grp_misc_consumables_perc: estimate.grp_misc_consumables_perc || 0,
                apply_auxiliary_to_grc: estimate.apply_auxiliary_to_grc !== false,
                apply_auxiliary_to_grp: estimate.apply_auxiliary_to_grp !== false,
                direct_wastage_perc: estimate.direct_wastage_perc || 0,
                direct_painting_labour_perc: estimate.direct_painting_labour_perc || 0,
                direct_electricity_perc: estimate.direct_electricity_perc || 0,
                direct_acid_wash_labour_perc: estimate.direct_acid_wash_labour_perc || 0,
                direct_styro_foam: estimate.direct_styro_foam || 0,
                direct_production_accessories: estimate.direct_production_accessories || 0
            });
            const updated = await legacyApi.estimates.getDetail(estimate.estimate_id);
            setEstimate(updated);
            toast.success("Production settings saved successfully");
        } catch (err: any) {
            toast.error(err?.message || 'Failed to save production settings');
        }
    };

    const handleSaveAdminControls = async () => {
        if (!estimate) return;
        if (!canEdit) return;
        setBusy(true);
        try {
            const payload = buildUpdatePayload(
                {
                    ...estimate,
                    labour_rate_per_hour: estimate.per_hour_labour_rate,
                    per_hour_labour_rate: estimate.per_hour_labour_rate,
                    overhead_per_sqm: estimate.oh_per_sqm,
                    oh_per_sqm: estimate.oh_per_sqm,
                },
                estimate.parts || [],
            );
            await legacyApi.estimates.update(estimate.estimate_id, payload);
            const updated = await legacyApi.estimates.getDetail(estimate.estimate_id);
            setEstimate(updated);
            toast.success("Administrative and pricing controls saved successfully.");
        } catch (e: any) {
            toast.error("Failed to save: " + e.message);
        } finally {
            setBusy(false);
        }
    };

    const handleSaveInstallationDistribution = async () => {
        if (!estimate) return;
        if (!canEdit) return;
        setBusy(true);
        try {
            await legacyApi.estimates.updateAdminControls(estimate.estimate_id, {
                skilled_manhours_perc: Number(estimate.skilled_manhours_perc || 0),
                unskilled_manhours_perc: Number(estimate.unskilled_manhours_perc || 0),
                non_working_hours_per_week: Number((estimate as any).non_working_hours_per_week || 0),
            });
            const updated = await legacyApi.estimates.getDetail(estimate.estimate_id);
            setEstimate(updated);
            toast.success("Installation labour split saved successfully.");
        } catch (err: any) {
            toast.error(err?.message || 'Failed to save installation labour split');
        } finally {
            setBusy(false);
        }
    };

    const handleApplyOhSheetValue = () => {
        if (!estimate || !ohSheetRatePerSqm) return;
        setEstimate({ ...estimate, oh_per_sqm: ohSheetRatePerSqm });
    };

    const handleOpenOverheadSheet = () => {
        if (!estimate) return;

        const currentSheetRate = Number(ohSheetRatePerSqm || 0);
        const currentEstimateOh = Number(estimate.oh_per_sqm || 0);
        const syncEstimateWithDefault =
            currentEstimateOh <= 0 || (currentSheetRate > 0 && Math.abs(currentEstimateOh - currentSheetRate) < 0.005);

        setOverheadSheetReturnState({
            modal: openModal === 'admin' ? 'admin' : 'inputs',
            accordionValues: inputsAccordionValues,
            syncEstimateWithDefault,
            previousSheetRate: currentSheetRate,
        });
        setOpenModal('overheadsheet');
    };

    const handleCloseOverheadSheet = async () => {
        const returnState = overheadSheetReturnState;
        const nextRate = await loadOverheadSummary();

        if (returnState?.syncEstimateWithDefault) {
            setEstimate((prev) => {
                if (!prev) return prev;
                const currentOh = Number(prev.oh_per_sqm || 0);
                const previousRate = Number(returnState.previousSheetRate || 0);
                const shouldSync =
                    currentOh <= 0 ||
                    (previousRate > 0 && Math.abs(currentOh - previousRate) < 0.005);

                if (!shouldSync) return prev;
                return { ...prev, oh_per_sqm: nextRate > 0 ? nextRate : currentOh };
            });
        }

        if (returnState?.modal === 'admin') {
            setOpenModal('admin');
        } else {
            const restoredAccordionValues = Array.from(new Set([...(returnState?.accordionValues || []), 'admin']));
            setInputsAccordionValues(restoredAccordionValues);
            setOpenModal('inputs');
        }

        setOverheadSheetReturnState(null);
    };

    const updateQuotationPreviewHeight = () => {
        const iframe = quotationPreviewFrameRef.current;
        if (!iframe) return;
        try {
            const doc = iframe.contentDocument || iframe.contentWindow?.document;
            if (!doc?.body || !doc.documentElement) return;
            const footer = doc.getElementById('footer');
            const footerHeight = footer ? Math.ceil(footer.getBoundingClientRect().height) : 0;

            doc.body.style.overflow = 'hidden';
            doc.documentElement.style.overflow = 'hidden';

            const nextHeight = Math.max(
                doc.body.scrollHeight,
                doc.body.offsetHeight,
                doc.documentElement.scrollHeight,
                doc.documentElement.offsetHeight,
                doc.documentElement.clientHeight
            );

            if (nextHeight > 0) {
                setQuotationPreviewHeight(Math.max(nextHeight + footerHeight + 32, 960));
            }
        } catch (error) {
            console.debug('Quotation preview height sync unavailable', error);
            setQuotationPreviewHeight((current) => Math.max(current, 1320));
        }
    };

    const handleQuotationPreviewLoad = () => {
        updateQuotationPreviewHeight();
        window.setTimeout(updateQuotationPreviewHeight, 150);
        window.setTimeout(updateQuotationPreviewHeight, 600);
        window.setTimeout(updateQuotationPreviewHeight, 1400);
    };

    const openGrcOverrideModal = (part: any) => {
        setSelectedGrcOverridePartId(part.estimate_part_id);
        setGrcOverrideDraft({
            design_mix_model_id: part.grc_design_mix_model_id ? String(part.grc_design_mix_model_id) : '',
            grc_type_id: part.grc_type_id ? String(part.grc_type_id) : '',
            labour_rate_per_kg: part.grc_labour_rate_per_kg ? String(part.grc_labour_rate_per_kg) : '',
        });
        setShowGrcOverrideModal(true);
    };

    const handleSaveGrcOverride = async () => {
        if (!estimate || !selectedGrcOverridePartId || !canEdit) return;
        try {
            setSavingGrcOverride(true);
            await legacyApi.estimates.updateGrcProduction(selectedGrcOverridePartId, {
                design_mix_model_id: grcOverrideDraft.design_mix_model_id ? parseInt(grcOverrideDraft.design_mix_model_id, 10) : null,
                grc_type_id: grcOverrideDraft.grc_type_id ? parseInt(grcOverrideDraft.grc_type_id, 10) : null,
                labour_rate_per_kg: grcOverrideDraft.labour_rate_per_kg ? parseFloat(grcOverrideDraft.labour_rate_per_kg) : null,
            });
            const updated = await legacyApi.estimates.getDetail(estimate.estimate_id);
            setEstimate(updated);
            setDraftParts(
                (updated.parts || []).map((p: any) => ({
                    estimate_part_id: p.estimate_part_id,
                    part_name: p.part_name || '',
                    dimension: p.dimension || '',
                    part_ref_code: p.part_ref_code || p.formatted_part_id || '',
                    quantity: p.quantity ?? 0,
                    area_m2: p.area_m2 ?? 0,
                    thickness: p.thickness ?? 0,
                    weight_kgs: p.weight_kgs ?? 0,
                    man_hours_required: p.man_hours_required ?? 0,
                    man_hours_per_unit: p.quantity ? (p.man_hours_required ?? 0) / p.quantity : 0,
                    mould_area: p.mould_area ?? 0,
                    mould_cost_m2: p.mould_cost_m2 ?? 0,
                    mould_quantity: p.mould_quantity ?? 1,
                    m2_per_hour_production: p.m2_per_hour_production ?? 0,
                    design_premium_perc: p.design_premium_perc ?? 0,
                    grc_design_mix_model_id: p.grc_design_mix_model_id ?? null,
                    grc_type_id: p.grc_type_id ?? null,
                    grc_labour_rate_per_kg: p.grc_labour_rate_per_kg ?? 0,
                    bom_items: p.bom_items || [],
                    material_type_id: p.material_type_id ?? defaultMaterialTypeId(),
                    unit_id: p.unit_id ?? defaultUnitId(),
                    mould_type_id: p.mould_type_id ?? defaultMouldTypeId(),
                }))
            );
            toast.success('GRC part override saved.');
            setShowGrcOverrideModal(false);
            setSelectedGrcOverridePartId(null);
        } catch (err: any) {
            toast.error(err?.message || 'Failed to save GRC override');
        } finally {
            setSavingGrcOverride(false);
        }
    };

    const handleSaveInstallationItems = async () => {
        if (!estimate || !effectiveDraftInstallItems) return;
        if (!canEdit) return;
        try {
            setSavingInstallItems(true);
            await legacyApi.estimates.updateInstallations(
                estimate.estimate_id,
                effectiveDraftInstallItems.map((it: any) => ({
                    estimate_inst_id: it.estimate_inst_id,
                    quantity: parseFloat(it.quantity || 0),
                    man_hours: parseFloat(it.man_hours || 0),
                    unit_rate: parseFloat(it.unit_rate || 0),
                }))
            );
            const updated = await legacyApi.estimates.getDetail(estimate.estimate_id);
            setEstimate(updated);
            setDraftInstallItems((updated.installation_items || []).map(normalizeInstallationDraftItem));
            toast.success('Installation items saved successfully');
        } catch (e: any) {
            toast.error(e.message || 'Failed to save installation items');
        } finally {
            setSavingInstallItems(false);
        }
    };



    const handleConvert = () => {
        setShowOrdersModal(true);
    };



    const handleCreateRevision = async () => {
        if (!estimate) return;
        if (!(await confirm(
            `Create a new revision (Rev ${estimate.revision_no + 1})? This will freeze the current estimate.`,
            {
                title: "Create Revision",
                confirmText: "Create Revision"
            }
        ))) return;

        try {
            const newRevision = await legacyApi.estimates.revise(estimate.estimate_id);
            toast.success(`Revision ${newRevision.revision_no} created successfully!`);
            navigate(`/legacy/estimates/${newRevision.estimate_id}?tab=inputs`);
        } catch (err: any) {
            toast.error(err.message || 'Failed to create revision');
            console.error(err);
        }
    };

    const handleMarkSent = async () => {
        if (!estimate) return;
        if (!(await confirm(
            "Mark this estimate as SENT? This will freeze the estimate and prevent further changes.",
            {
                title: "Mark as Sent",
                confirmText: "Mark Sent",
                variant: 'default'
            }
        ))) return;

        try {
            setBusy(true);
            await legacyApi.estimates.markSent(estimate.estimate_id);
            const updated = await legacyApi.estimates.getDetail(estimate.estimate_id);
            setEstimate(updated);
            toast.success('Estimate marked as SENT');
        } catch (err: any) {
            toast.error(err.message || 'Failed to mark as sent');
        } finally {
            setBusy(false);
        }
    };





    const handleCreateWorkOrder = async () => {
        if (!estimate) return;
            const remarks = prompt("Enter remarks for this Work Order (Optional):", `Production for ${estimateNumber}`);
        if (remarks === null) return;

        if (!(await confirm(
            "Generate Work Order for this Estimate? This will allow production tracking.",
            {
                title: "Generate Work Order",
                confirmText: "Generate Work Order"
            }
        ))) return;

        try {
            setBusy(true);
            const res = await fetch(`${API_BASE_URL}/manufacturing/work-orders`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    estimate_id: estimate.estimate_id,
                    remarks: remarks
                })
            });

            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.detail || 'Failed to create Work Order');
            }

            const wo = await res.json();
            if (await confirm(
                `Work Order ${wo.wo_number} Created! View it now?`,
                {
                    title: "Work Order Created",
                    confirmText: "View Work Order",
                    cancelText: "Stay Here"
                }
            )) {
                navigate('/manufacturing');
            }
        } catch (e: any) {
            toast.error(e.message);
        } finally {
            setBusy(false);
        }
    };

    const handleLoadInstallationFactors = async () => {
        if (!estimate) return;
        setLoadingFactors(true);
        try {
            if (draftHeader && draftParts) {
                const payload = buildUpdatePayload(draftHeader, draftParts);
                await legacyApi.estimates.update(estimate.estimate_id, payload);
            }
            await legacyApi.estimates.updateAdminControls(estimate.estimate_id, {
                skilled_manhours_perc: Number(estimate.skilled_manhours_perc || 0),
                unskilled_manhours_perc: Number(estimate.unskilled_manhours_perc || 0),
                non_working_hours_per_week: Number((estimate as any).non_working_hours_per_week || 0),
            });
            const res = await authorizedFetch(`${API_BASE_URL}/estimates/${estimate.estimate_id}/load-installation-factors`, {
                method: 'POST'
            });
            let loadPayload: any = null;
            try {
                loadPayload = await res.json();
            } catch {
                loadPayload = null;
            }
            if (!res.ok) {
                throw new Error(loadPayload?.detail || loadPayload?.message || 'Failed to load installation factors');
            }
            // Refresh the estimate data
            const updated = await legacyApi.estimates.getDetail(estimate.estimate_id);
            setEstimate(updated);
            setDraftInstallItems((updated.installation_items || []).map(normalizeInstallationDraftItem));
            if ((updated.installation_items || []).length === 0) {
                throw new Error('No installation rows were generated. Check part manhours and try again.');
            }
            const changedCount = Number(loadPayload?.inserted_count || 0) + Number(loadPayload?.updated_count || 0);
            toast.success(changedCount > 0 ? `Installation factors loaded successfully (${changedCount} rows).` : 'Installation factors loaded successfully!');
        } catch (err: any) {
            toast.error(err?.message || 'Failed to load installation factors');
            console.error(err);
        } finally {
            setLoadingFactors(false);
        }
    };


    const handleDelete = async () => {
        if (!estimate) return;
        if (!(await confirm(
            "Are you sure you want to delete this estimate? This action cannot be undone.",
            {
                title: "Delete Estimate",
                confirmText: "Delete",
                variant: "destructive"
            }
        ))) return;

        try {
            setBusy(true);
            await legacyApi.estimates.delete(estimate.estimate_id);
            toast.success('Estimate deleted successfully');
            navigate('/legacy/estimates');
        } catch (err) {
            toast.error('Failed to delete estimate');
            console.error(err);
        } finally {
            setBusy(false);
        }
    };

    const handleFreeze = async () => {
        if (!estimate) return;

        // Prevent unfreezing if converted (Status 4)
        if (estimate.approval_status_id === 4 && estimate.is_freezed) {
            toast.error("Cannot unfreeze: This estimate has been converted to a Sale Order.");
            return;
        }

        try {
            setBusy(true);
            const updated = await legacyApi.estimates.freeze(estimate.estimate_id, !estimate.is_freezed);
            setEstimate({ ...estimate, is_freezed: updated.is_freezed });
            toast.success(updated.is_freezed ? 'Estimate frozen' : 'Estimate unfrozen');
        } catch (err: any) {
            toast.error(err?.message || 'Failed to update freeze state');
            console.error(err);
        } finally {
            setBusy(false);
        }
    };

    const handleDesignPremiumPercentChange = (estimatePartId: number, rawValue: string) => {
        const parsed = parseFloat(rawValue);
        const nextPercent = Number.isFinite(parsed) ? parsed / 100 : 0;
        setEstimate((prev) => {
            if (!prev) return prev;
            return {
                ...prev,
                parts: (prev.parts || []).map((part) => {
                    if (part.estimate_part_id !== estimatePartId) return part;
                    const areaTotal = Number(part.area_total ?? ((part.area_m2 || 0) * (part.quantity || 0)));
                    const basePerSqm = Number(part.base_price_per_sqm ?? 0);
                    const totalCost = Number(part.total_cost ?? 0);
                    const revisedBasePricePerSqm = basePerSqm * (1 + nextPercent);
                    const designPremiumAmount = areaTotal > 0
                        ? Math.max((revisedBasePricePerSqm * areaTotal) - totalCost, 0)
                        : 0;
                    return {
                        ...part,
                        design_premium_perc: nextPercent,
                        revised_base_price_per_sqm: revisedBasePricePerSqm,
                        design_premium_amount: designPremiumAmount,
                    };
                }),
            };
        });
    };

    const costRows = useMemo(() => {
        if (!estimate) return [];

        return estimate.parts.map(part => {
            const areaTotal = Number(part.area_total ?? ((part.area_m2 || 0) * (part.quantity || 0)));
            const qty = Number(part.quantity || 0);
            const patternMould = Number(part.pattern_mould_cost ?? 0);
            const production = Number(part.production_cost ?? 0);
            const bomCost = Number(part.bom_cost ?? (part.bom_items || []).reduce((s, b) => s + Number(b.total_cost || 0), 0));
            const installation = Number(part.installation_cost_calc ?? 0);
            const packing = Number(part.packing_loading_cost ?? 0);
            const freight = Number(part.freight_cost_calc ?? 0);
            const project = Number(part.proj_eng_cost ?? 0);
            const admin = Number(part.admin_cost_calc ?? 0);
            const overhead = Number(part.overhead_cost ?? 0);
            const totalCost = Number(part.total_cost ?? 0);
            const basePerSqm = Number(part.base_price_per_sqm ?? 0);
            const designPremiumPerc = normalizePercent(part.design_premium_perc ?? 0);
            const designPremiumAmount = Number(part.design_premium_amount ?? 0);
            const revisedBasePerSqm = Number(part.revised_base_price_per_sqm ?? 0);
            const sellingPerUnit = Number(part.selling_price_per_unit ?? 0);
            const sellingPriceTotal = Number(part.selling_price_total ?? 0);
            const roundOff = Number(part.round_off ?? sellingPriceTotal);

            return {
                part,
                areaTotal,
                patternMould,
                production,
                productionBase: production,
                productionOverheads: 0,
                grpLabourCost: Number(part.labour_cost_calc ?? 0),
                bomCost,
                installation,
                packing,
                freight,
                project,
                admin,
                overhead,
                totalCost,
                basePerSqm,
                designPremiumPerc,
                designPremiumAmount,
                revisedPerUnit: revisedBasePerSqm,
                sellingPerUnit,
                sellingPriceTotal,
                roundOff,
                unit: part.unit_name || 'Nos.',
                materialType: part.material_type_name || (part.material_type_id === 2 ? 'GRP' : part.material_type_id === 3 ? 'GRG' : 'GRC'),
                totalWeight: Number(part.weight_kgs || 0),
                quantity: qty,
            };
        });
    }, [estimate]);

    const totalInstallationManhours = useMemo(
        () => sumInstallationPartManhours((draftParts || estimate?.parts || []) as any[]),
        [draftParts, estimate?.parts]
    );
    const installationLabourSplitPreview = useMemo(
        () =>
            computeInstallationLabourSplit(
                totalInstallationManhours,
                estimate?.skilled_manhours_perc || 0,
                estimate?.unskilled_manhours_perc || 0,
                (estimate as any)?.non_working_hours_per_week || 0,
            ),
        [estimate, totalInstallationManhours]
    );
    const effectiveDraftInstallItems = useMemo(
        () => applyInstallationLabourSplitToItems(draftInstallItems, installationLabourSplitPreview),
        [draftInstallItems, installationLabourSplitPreview]
    );

    // ... (rest of the file until rendering GRP panel) ...
    // Note: I will use a separate replace call for the UI if needed, but I can try to find where the GRP panel ends and insert the new panel there.
    // The previous tool call view showed the GRP panel rendering around line 1950-2050.
    // I can't modify both the hook (line 716) and the JSX (line 2045) in standard replace_file_content as they are far apart.
    // I will split this into two operations. First, update the hook.


    const totals = useMemo(() => {
        return costRows.reduce(
            (acc, r) => {
                acc.patternMould += r.patternMould;
                acc.production += r.production;
                acc.labourCost += r.grpLabourCost;
                acc.bomCost += r.bomCost;
                acc.install += r.installation;
                acc.packing += r.packing;
                acc.freight += r.freight;
                acc.project += r.project;
                acc.admin += r.admin;
                acc.overhead += r.overhead;
                acc.totalCost += r.totalCost;
                acc.designPremiumAmount += r.designPremiumAmount;
                acc.selling += r.sellingPriceTotal;
                acc.roundOff += r.roundOff;
                acc.areaTotal += r.areaTotal;
                acc.totalWeight += r.totalWeight;
                acc.totalQuantity += r.quantity;
                return acc;
            },
            {
                patternMould: 0,
                production: 0,
                labourCost: 0,
                bomCost: 0,
                install: 0,
                packing: 0,
                freight: 0,
                project: 0,
                admin: 0,
                overhead: 0,
                totalCost: 0,
                designPremiumAmount: 0,
                selling: 0,
                roundOff: 0,
                areaTotal: 0,
                totalWeight: 0,
                totalQuantity: 0,
            }
        );
    }, [costRows]);

    const designPremiumReviewRows = useMemo(() => {
        return costRows.map((row) => ({
            estimatePartId: row.part.estimate_part_id,
            itemDescription: row.part.part_name || '-',
            orderQty: row.quantity,
            finalSqmPrice: row.revisedPerUnit,
            designPremiumPercent: formatPercent(row.designPremiumPerc),
            designPremiumAmount: row.designPremiumAmount,
        }));
    }, [costRows]);

    const averageDesignPremiumPercent = useMemo(() => {
        if (!totals.areaTotal) return 0;
        return costRows.reduce((sum, row) => sum + (row.designPremiumPerc * row.areaTotal), 0) / totals.areaTotal;
    }, [costRows, totals.areaTotal]);

    const renderDesignPremiumReviewCard = () => (
        <div id={DESIGN_PREMIUM_REVIEW_SECTION_ID} className="rounded-lg border bg-white shadow-sm">
            <div className="flex flex-wrap items-start justify-between gap-3 border-b bg-muted px-4 py-3">
                <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">Design Premium Review</div>
                    <div className="mt-1 text-sm font-semibold text-slate-900">Finalize item-wise DP before concluding the quote.</div>
                    <div className={`mt-1 text-[11px] font-medium ${resolveSectionReviewState('designpremium') ? 'text-emerald-700' : 'text-amber-700'}`}>
                        {resolveSectionReviewState('designpremium') ? 'Reviewed' : 'Pending review'}
                    </div>
                    <div className="mt-1 text-xs text-slate-500">Review item description, order qty, and final SQM price here, then enter the per-part DP value.</div>
                </div>
                <div className="flex flex-wrap gap-3">
                    <button
                        onClick={() => handleMarkSectionReviewed('designpremium')}
                        disabled={!canEdit || busy}
                        className={`rounded border px-4 py-2 text-xs font-bold uppercase tracking-widest ${getSectionCellClass(resolveSectionReviewState('designpremium'))} disabled:opacity-50`}
                    >
                        Mark Reviewed
                    </button>
                    <button
                        onClick={handleSaveAdminControls}
                        disabled={busy || estimate?.is_freezed}
                        className="bg-primary text-primary-foreground px-4 py-2 rounded-md font-bold text-xs uppercase tracking-widest hover:bg-primary/90 transition-all shadow-md disabled:opacity-50"
                    >
                        {busy ? 'SAVING...' : 'SAVE DP'}
                    </button>
                </div>
            </div>
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                    <thead className="bg-slate-50">
                        <tr className="text-left text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">
                            <th className="px-4 py-3">Item Description</th>
                            <th className="px-4 py-3 text-right">Order Qty</th>
                            <th className="px-4 py-3 text-right">Final SQM Price</th>
                            <th className="px-4 py-3 text-right">Design Premium (%)</th>
                            <th className="px-4 py-3 text-right">DP Amount</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {designPremiumReviewRows.length === 0 ? (
                            <tr>
                                <td colSpan={5} className="px-4 py-6 text-center text-sm text-slate-500">
                                    Add parts first to review design premium.
                                </td>
                            </tr>
                        ) : designPremiumReviewRows.map((row) => (
                            <tr key={row.estimatePartId} className="align-middle">
                                <td className="px-4 py-3 font-medium text-slate-900">{row.itemDescription}</td>
                                <td className="px-4 py-3 text-right font-mono tabular-nums">{formatCompactNumber(row.orderQty, true)}</td>
                                <td className="px-4 py-3 text-right font-mono tabular-nums">{formatCurrency(row.finalSqmPrice)} AED</td>
                                <td className="px-4 py-3">
                                    <div className="flex justify-end">
                                        <div className="relative w-32">
                                            <input
                                                type="number"
                                                className={`${adminInputClass} pr-7 text-right font-bold tabular-nums`}
                                                value={row.designPremiumPercent.toFixed(2)}
                                                onChange={(e) => handleDesignPremiumPercentChange(row.estimatePartId, e.target.value)}
                                                disabled={!canEdit}
                                            />
                                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400">%</span>
                                        </div>
                                    </div>
                                </td>
                                <td className="px-4 py-3 text-right font-mono tabular-nums text-slate-700">{formatCurrency(row.designPremiumAmount)} AED</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="border-t bg-slate-50 px-4 py-3 text-xs text-slate-500">
                Save DP here to refresh the synced cost breakdown, detailed overview, and final commercial totals.
            </div>
        </div>
    );

    const persistedCommercials = useMemo(() => {
        if (!estimate) {
            return {
                totalCost: 0,
                sellingPrice: 0,
                grandTotal: 0,
                netProfit: 0,
            };
        }
        return {
            totalCost: (estimate.parts || []).reduce((sum, part) => sum + Number(part.total_cost || 0), 0),
            sellingPrice: Number(estimate.total_price || 0),
            grandTotal: Number(estimate.total_price_incl_vat || 0),
            netProfit: Number(estimate.net_profit || 0),
        };
    }, [estimate]);

    const effectiveCostBreakdownGroups = useMemo(() => {
        const effective = { ...costBreakdownGroupPrefs };
        if (costBreakdownManualGroups) {
            return effective;
        }
        let requiredWidth = COST_BREAKDOWN_WIDTH_BUDGET.base + COST_BREAKDOWN_WIDTH_BUDGET.buffer;
        if (effective.directBreakdown) requiredWidth += COST_BREAKDOWN_WIDTH_BUDGET.directBreakdown;
        if (effective.otherCosts) requiredWidth += COST_BREAKDOWN_WIDTH_BUDGET.otherCosts;

        if (requiredWidth > costBreakdownWidth && effective.otherCosts) {
            effective.otherCosts = false;
            requiredWidth -= COST_BREAKDOWN_WIDTH_BUDGET.otherCosts;
        }
        if (requiredWidth > costBreakdownWidth && effective.directBreakdown) {
            effective.directBreakdown = false;
        }

        return effective;
    }, [costBreakdownGroupPrefs, costBreakdownWidth, costBreakdownManualGroups]);

    const costBreakdownMode = effectiveCostBreakdownGroups.directBreakdown && effectiveCostBreakdownGroups.otherCosts ? 'full' : 'collapsed';
    const showDirectBreakdown = effectiveCostBreakdownGroups.directBreakdown;
    const showOtherCosts = effectiveCostBreakdownGroups.otherCosts;
    const directBreakdownAutoHidden = costBreakdownGroupPrefs.directBreakdown && !showDirectBreakdown;
    const otherCostsAutoHidden = costBreakdownGroupPrefs.otherCosts && !showOtherCosts;

    const detailedOverview = useMemo(() => {
        if (!estimate) return null;

        const totalArea = totals.areaTotal;
        const totalWeight = totals.totalWeight;
        const directTotal = totals.patternMould + totals.production + totals.bomCost + totals.install;
        const logisticsTotal = totals.packing + totals.freight;
        const costSubtotal = totals.totalCost;
        const roundedSellingTotal = totals.roundOff;
        const amountAfterDiscount = persistedCommercials.sellingPrice;
        const rawDiscountAmount = roundedSellingTotal - amountAfterDiscount;
        const discountAmount = Math.abs(rawDiscountAmount) > 0.01
            ? rawDiscountAmount
            : roundedSellingTotal * normalizePercent(estimate.discount_percent);
        const vatAmount = persistedCommercials.grandTotal > 0
            ? Math.max(persistedCommercials.grandTotal - amountAfterDiscount, 0)
            : amountAfterDiscount * normalizePercent(estimate.vat_percent);
        const grandTotal = amountAfterDiscount + vatAmount;
        const netProfit = persistedCommercials.netProfit;
        const averageBasePerSqm = totalArea > 0 ? costSubtotal / totalArea : 0;
        const averageSellingPerSqm = totalArea > 0 ? amountAfterDiscount / totalArea : 0;
        const averageRoundedPerSqm = totalArea > 0 ? roundedSellingTotal / totalArea : 0;
        const averageDesignPremiumPercent = totalArea > 0
            ? costRows.reduce((sum, row) => sum + (row.designPremiumPerc * row.areaTotal), 0) / totalArea
            : 0;
        const averageRevisedBasePerSqm = totalArea > 0
            ? costRows.reduce((sum, row) => sum + (row.revisedPerUnit * row.areaTotal), 0) / totalArea
            : 0;
        const averageSellingPerUnit = totals.totalQuantity > 0
            ? costRows.reduce((sum, row) => sum + (row.sellingPerUnit * row.quantity), 0) / totals.totalQuantity
            : 0;
        const projectFixedComponents = [
            { label: 'Design & Engineering', value: Number(estimate.pm_design_cost || 0) },
            { label: 'Project Manager Cost', value: Number(estimate.pm_project_manager_cost || 0) },
            { label: 'Site Intervention', value: Number(estimate.pm_site_intervention_cost || 0) },
            { label: 'Documentation / Drawings', value: Number(estimate.pm_documentation_cost || 0) },
            { label: 'Travel Expenses', value: Number(estimate.pm_travel_cost || 0) },
        ];
        const projectFixedTotal = projectFixedComponents.reduce((sum, item) => sum + item.value, 0);

        return {
            totalArea,
            totalWeight,
            totalQuantity: totals.totalQuantity,
            partCount: costRows.length,
            tooling: totals.patternMould,
            production: totals.production,
            labourCost: totals.labourCost,
            bom: totals.bomCost,
            installation: totals.install,
            directTotal,
            packing: totals.packing,
            freight: totals.freight,
            logisticsTotal,
            engg: totals.project,
            admin: totals.admin,
            overhead: totals.overhead,
            costSubtotal,
            designPremiumAmount: totals.designPremiumAmount,
            roundedSellingTotal,
            discountAmount,
            amountAfterDiscount,
            vatAmount,
            grandTotal,
            netProfit,
            averageBasePerSqm,
            averageSellingPerSqm,
            averageRoundedPerSqm,
            averageDesignPremiumPercent,
            averageRevisedBasePerSqm,
            averageSellingPerUnit,
            projectFixedTotal,
            projectFixedComponents,
            shareOfCost: (amount: number) => (costSubtotal > 0 ? (amount / costSubtotal) * 100 : 0),
        };
    }, [estimate, totals, persistedCommercials, costRows]);

    const overviewCardMetrics = useMemo(() => {
        if (!detailedOverview) return [];
        return [
            { label: 'Total Parts', value: formatCompactNumber(detailedOverview.partCount, true), accent: 'text-slate-900' },
            { label: 'Total Qty', value: formatCompactNumber(detailedOverview.totalQuantity, true), accent: 'text-slate-900' },
            { label: 'Total Area', value: `${formatCurrency(detailedOverview.totalArea)} m2`, accent: 'text-slate-900' },
            { label: 'Total Weight', value: `${formatCurrency(detailedOverview.totalWeight)} kg`, accent: 'text-slate-900' },
            { label: 'Tooling Total', value: `${formatCurrency(detailedOverview.tooling)} AED`, accent: 'text-slate-900' },
            { label: 'Production Total', value: `${formatCurrency(detailedOverview.production)} AED`, accent: 'text-slate-900' },
            { label: 'Direct Cost', value: `${formatCurrency(detailedOverview.directTotal)} AED`, accent: 'text-primary' },
            { label: 'BOM Total', value: `${formatCurrency(detailedOverview.bom)} AED`, accent: 'text-slate-900' },
            { label: 'Installation Total', value: `${formatCurrency(detailedOverview.installation)} AED`, accent: 'text-slate-900' },
            { label: 'Packing Total', value: `${formatCurrency(detailedOverview.packing)} AED`, accent: 'text-slate-900' },
            { label: 'Freight Total', value: `${formatCurrency(detailedOverview.freight)} AED`, accent: 'text-slate-900' },
            { label: 'Engg Total', value: `${formatCurrency(detailedOverview.engg)} AED`, accent: 'text-slate-900' },
            { label: 'Admin Total', value: `${formatCurrency(detailedOverview.admin)} AED`, accent: 'text-slate-900' },
            { label: 'OH Total', value: `${formatCurrency(detailedOverview.overhead)} AED`, accent: 'text-slate-900' },
            { label: 'Cost Subtotal', value: `${formatCurrency(detailedOverview.costSubtotal)} AED`, accent: 'text-slate-900' },
            { label: 'Design Premium', value: `${formatCurrency(detailedOverview.designPremiumAmount)} AED`, accent: 'text-slate-900' },
            { label: 'Rounded Sell Total', value: `${formatCurrency(detailedOverview.roundedSellingTotal)} AED`, accent: 'text-blue-700' },
            { label: 'Discount Amount', value: `${formatCurrency(detailedOverview.discountAmount)} AED`, accent: 'text-rose-700' },
            { label: 'Amount After Discount', value: `${formatCurrency(detailedOverview.amountAfterDiscount)} AED`, accent: 'text-blue-700' },
            { label: 'VAT Amount', value: `${formatCurrency(detailedOverview.vatAmount)} AED`, accent: 'text-slate-900' },
            { label: 'Grand Total', value: `${formatCurrency(detailedOverview.grandTotal)} AED`, accent: 'text-blue-700' },
            { label: 'Net Profit', value: `${formatCurrency(detailedOverview.netProfit)} AED`, accent: detailedOverview.netProfit >= 0 ? 'text-emerald-700' : 'text-rose-700' },
            { label: 'Avg Base / SQM', value: `${formatCurrency(detailedOverview.averageBasePerSqm)} AED`, accent: 'text-slate-900' },
            { label: 'Avg Sell / SQM', value: `${formatCurrency(detailedOverview.averageSellingPerSqm)} AED`, accent: 'text-blue-700' },
        ];
    }, [detailedOverview]);

    const handleDownloadPDF = async () => {
        if (!estimate) return;
        try {
            await legacyApi.estimates.downloadPDF(estimate.estimate_id);
        } catch (err) {
            toast.error('Failed to download PDF');
            console.error(err);
        }
    };

    const handlePreviewQuotation = () => {
        if (!estimate) return;
        const previewWindow = window.open(`${API_BASE_URL}/estimates/${estimate.estimate_id}/print`, '_blank');
        if (!previewWindow) {
            toast.error('Pop-up blocked. Please allow pop-ups and try again.');
            return;
        }
        previewWindow.focus();
    };

    const ohSourceStatus = useMemo(() => {
        if (!estimate) return null;
        const currentOh = Number(estimate.oh_per_sqm || 0);
        const sheetOh = Number(ohSheetRatePerSqm || 0);

        if (sheetOh > 0) {
            const matchesSheet = Math.abs(currentOh - sheetOh) < 0.005;
            if (matchesSheet) {
                return {
                    label: 'Using Default Rate',
                    detail: `This estimate is currently using the overhead sheet default of ${formatCurrency(sheetOh)} AED / SQM. You can keep this value or enter a custom OH rate for this estimate.`,
                    badgeClass: 'border-emerald-200 bg-emerald-50 text-emerald-700',
                };
            }
            return {
                label: 'Estimate Custom Rate',
                detail: `This estimate is using a custom OH rate. Use OH Sheet Value any time if you want to reset it back to the current default of ${formatCurrency(sheetOh)} AED / SQM.`,
                badgeClass: 'border-amber-200 bg-amber-50 text-amber-700',
            };
        }

        if (currentOh > 0) {
            return {
                label: 'Estimate Custom Rate',
                detail: 'This estimate is using a custom OH value. The overhead sheet is unavailable right now, so no default reference could be loaded.',
                badgeClass: 'border-slate-200 bg-slate-50 text-slate-700',
            };
        }

        return {
            label: 'Pending OH Value',
            detail: 'Enter an OH value for this estimate or open the overhead sheet to review the current default rate.',
            badgeClass: 'border-slate-200 bg-slate-50 text-slate-700',
        };
    }, [estimate, ohSheetRatePerSqm]);

    const handleSendPDF = async (payload: { to: string; cc?: string; subject?: string; body?: string }) => {
        if (!estimate) return;
        const result = await legacyApi.estimates.emailPdf(estimate.estimate_id, payload);
        toast.success(result.message || 'Estimate PDF emailed successfully');
    };

    const handleShareLink = async () => {
        if (!estimate) return;
        try {
            const result = await legacyApi.estimates.createShareLink(estimate.estimate_id);
            try {
                await navigator.clipboard.writeText(result.share_url);
                toast.success(result.is_new ? 'Quotation share link created and copied.' : 'Quotation share link copied.');
            } catch {
                window.prompt('Copy quotation share link', result.share_url);
            }
        } catch (err: any) {
            toast.error(err?.message || 'Failed to create quotation share link');
        }
    };

    const handleShareWhatsApp = async () => {
        if (!estimate) return;
        try {
            const result = await legacyApi.estimates.createShareLink(estimate.estimate_id, { channel: 'WHATSAPP' });
            const message = `Please find quotation ${estimateNumber}: ${result.share_url}`;
            const recipient = customers.find((customer: any) => Number(customer.customer_id) === Number(estimate.customer_id));
            const resolved = openWhatsAppMessage(message, recipient);
            if (resolved.normalizedNumber) {
                toast.success(result.is_new ? 'Quotation share link created for WhatsApp.' : 'Quotation share link opened in WhatsApp.');
            } else {
                toast.warning('No valid customer WhatsApp number found. Opened WhatsApp without a selected recipient.');
            }
        } catch (err: any) {
            toast.error(err?.message || 'Failed to open WhatsApp share');
        }
    };

    if (loading) return <div className="p-8">Loading Estimate...</div>;
    if (!estimate) return <div className="p-8 text-red-600">Estimate not found.</div>;

    const estimateNumber = getEstimateRef(estimate) || getEstimateBaseNo(estimate) || `HC/EST/${String(estimate.estimate_id).padStart(4, '0')}`;
    const estimateRecipientEmail = customers.find((customer: any) => Number(customer.customer_id) === Number(estimate.customer_id))?.email_address || '';
    const estimateAttachmentName = `${estimateNumber.replace(/\//g, '_')}.pdf`;

    const getStatusMeta = (approvalStatusId?: number) => {
        // Keep this aligned with backend `constants/statuses.py` (ESTIMATE_STATUS)
        switch (approvalStatusId) {
            case 2:
                return { label: 'PENDING', className: 'bg-slate-100 text-slate-800' };
            case 3:
                return { label: 'SENT', className: 'bg-blue-100 text-blue-800' };
            case 4:
                return { label: 'APPROVED', className: 'bg-emerald-100 text-emerald-800' };
            case 5:
                return { label: 'ORDERED', className: 'bg-green-600 text-white' };
            case 6:
                return { label: 'REJECTED', className: 'bg-rose-100 text-rose-800' };
            case 7:
                return { label: 'SUPERSEDED', className: 'bg-amber-100 text-amber-800' };
            case 1:
            default:
                return { label: 'DRAFT', className: 'bg-slate-100 text-slate-800' };
        }
    };

    const statusMeta = getStatusMeta(estimate.approval_status_id);

    const resolvedEstimateProjectTypeId = Number(estimate.project_type_id ?? 1);
    const resolvedInquiryProjectTypeId =
        estimate.inquiry_project_type_id != null
            ? Number(estimate.inquiry_project_type_id)
            : resolvedEstimateProjectTypeId;

    const showInstallationColumn = estimate.installation_in_scope !== false;
    const sectionTitles: Record<string, string> = {
        costbreakdown: 'Cost Breakdown',
        tooling: 'Mould Cost',
        production: 'Production',
        bom: 'BOM',
        installations: 'Installation',
        admin: 'Administrative Controls',
        pricing: 'Pricing Controls',
        designpremium: 'Design Premium Review',
        overheadsheet: 'Overhead Sheet',
        quotationpreview: 'Quotation Preview',
        generate: 'Generate',
        overview: 'Overview',
    };
    const materialTypeLabel = (part: any) => {
        const match = materialTypes.find((mt) => mt.material_type_id === Number(part.material_type_id || 0));
        return match?.material_type || (Number(part.material_type_id || resolvedEstimateProjectTypeId) === 2 ? 'GRP' : Number(part.material_type_id || resolvedEstimateProjectTypeId) === 3 ? 'GRG' : 'GRC');
    };
    const resolveSectionReviewState = (section: 'tooling' | 'production' | 'bom' | 'installations' | 'admin' | 'pricing' | 'designpremium') =>
        section === 'tooling'
            ? Boolean((estimate as any).tooling_reviewed)
            : section === 'production'
                ? Boolean((estimate as any).production_reviewed)
                : section === 'bom'
                    ? Boolean((estimate as any).bom_reviewed)
                    : section === 'installations'
                        ? Boolean(estimate.installation_reviewed)
                        : section === 'admin'
                            ? Boolean((estimate as any).admin_reviewed)
                            : section === 'designpremium'
                                ? Boolean((estimate as any).design_premium_reviewed)
                                : Boolean((estimate as any).scope_reviewed);
    const getSectionCellClass = (reviewed: boolean) =>
        reviewed
            ? 'bg-emerald-100 text-emerald-800 border-emerald-200 hover:bg-emerald-200'
            : 'bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100';
    const openInputsAccordionSection = (section: 'pricing-controls' | 'administrative-controls', scrollTargetId?: string) => {
        setOpenModal('inputs');
        setInputsAccordionValues((prev) => (prev.includes(section) ? prev : [...prev, section]));
        if (typeof window !== 'undefined') {
            window.setTimeout(() => {
                document.getElementById(scrollTargetId || `estimate-inputs-${section}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 80);
        }
    };
    const handleOpenPartSection = (_part: any, section: 'tooling' | 'production' | 'bom' | 'installations') => {
        setOpenModal(section);
    };
    const handleMarkSectionReviewed = async (section: 'tooling' | 'production' | 'bom' | 'installations' | 'admin' | 'pricing' | 'designpremium') => {
        if (!estimate?.estimate_id) return;
        try {
            setBusy(true);
            const payload =
                section === 'tooling'
                    ? { tooling_reviewed: true }
                    : section === 'production'
                        ? { production_reviewed: true }
                        : section === 'bom'
                            ? { bom_reviewed: true }
                            : section === 'installations'
                                ? { installation_reviewed: true }
                                : section === 'admin'
                                    ? { admin_reviewed: true }
                                    : section === 'designpremium'
                                        ? { design_premium_reviewed: true }
                                        : { scope_reviewed: true };
            await legacyApi.estimates.updateReviewState(estimate.estimate_id, payload);
            const updated = await legacyApi.estimates.getDetail(estimate.estimate_id);
            setEstimate(updated);
            toast.success(`${sectionTitles[section]} marked reviewed.`);
        } catch (err: any) {
            toast.error(err?.message || `Failed to mark ${sectionTitles[section]} reviewed`);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="h-full flex flex-col bg-gray-50 overflow-hidden">
            {/* Status Banners */}
            {estimate && estimate.approval_status_id === 7 && (
                <div className="bg-amber-100 border-b border-amber-200 px-4 py-2 text-amber-800 text-sm flex justify-center items-center gap-2">
                    <span className="font-bold">SUPERSEDED</span>
                    <span>This estimate has been superseded by a newer revision.</span>
                    {/* Future: Link to newer revision if available */}
                </div>
            )}
            {estimate && estimate.approval_status_id === 3 && (
                <div className="bg-blue-100 border-b border-blue-200 px-4 py-2 text-blue-800 text-sm flex justify-center items-center gap-2">
                    <span className="font-bold">SENT</span>
                    <span>This estimate has been marked as Sent and is Read-Only.</span>
                </div>
            )}
            {estimate && (estimate.approval_status_id === 4 || estimate.approval_status_id === 5) && (
                <div className="bg-green-100 border-b border-green-200 px-4 py-2 text-green-800 text-sm flex justify-center items-center gap-2">
                    <span className="font-bold">{estimate.approval_status_id === 5 ? 'ORDERED' : 'APPROVED'}</span>
                    <span>This estimate has been {estimate.approval_status_id === 5 ? 'converted to a Sale Order' : 'approved by the customer'}.</span>
                </div>
            )}
            {estimate && estimate.approval_status_id === 6 && (
                <div className="bg-rose-100 border-b border-rose-200 px-4 py-2 text-rose-800 text-sm flex justify-center items-center gap-2">
                    <span className="font-bold">REJECTED</span>
                    <span>This estimate has been rejected.</span>
                </div>
            )}

            {/* Header Toolbar */}
            <div className="bg-white border-b border-gray-200 p-4 flex items-center justify-between shadow-sm flex-shrink-0">
                <div className="flex items-center space-x-4">
                    <button onClick={() => navigate('/legacy/estimates')} className="text-gray-500 hover:text-blue-700 transition-colors">
                        <ArrowLeft size={20} />
                    </button>
                    <div>
                        <div className="flex items-center gap-3">
                            <h1 className="text-xl font-bold text-legacy-blue">
                                {estimate ? `Estimate ${estimateNumber}` : 'Loading...'}
                            </h1>
                            {estimate && (
                                <div className="flex items-center gap-2">
                                    <span className="text-sm font-normal text-gray-500">Rev {estimate.revision_no}</span>
                                    <span className={`px-2 py-0.5 text-xs font-bold rounded-full ${statusMeta.className}`}>
                                        {statusMeta.label}
                                    </span>
                                    {estimate.is_freezed && (
                                        <span className="px-2 py-0.5 text-xs font-bold rounded-full bg-red-100 text-red-800">
                                            LOCKED
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="text-xs text-gray-500">
                            {estimate?.customer_name} | {estimate?.date_created ? new Date(estimate.date_created).toLocaleDateString() : ''}
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    {/* 1. Freeze Toggle: The Gatekeeper */}
                    {estimate && estimate.approval_status_id !== 4 && estimate.approval_status_id !== 5 && estimate.approval_status_id !== 7 && (
                        <button
                            onClick={handleFreeze}
                            className={`px-3 py-1 text-sm border rounded flex items-center shadow-sm font-medium transition-colors ${estimate.is_freezed
                                ? 'bg-amber-100 text-amber-800 border-amber-300 hover:bg-amber-200'
                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                }`}
                            disabled={busy}
                            title={estimate.is_freezed ? "Unlock Estimate" : "Lock Estimate to Enable Actions"}
                        >
                            <Snowflake size={14} className="mr-1" />
                            {estimate.is_freezed ? 'Unfreeze' : 'Freeze'}
                        </button>
                    )}

                    {/* 2. Mark Sent: Visible ONLY if Frozen & Draft/Pending (Status 1/2) */}
                    {estimate && estimate.is_freezed && (estimate.approval_status_id === 1 || estimate.approval_status_id === 2) && (
                        <button
                            onClick={handleMarkSent}
                            className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 shadow-sm font-medium transition-colors"
                            disabled={busy}
                        >
                            Mark as Sent
                        </button>
                    )}

                    {/* 3. Create Revision: Visible ONLY if Frozen & Sent (Status 3) */}
                    {estimate && estimate.is_freezed && estimate.approval_status_id === 3 && (
                        <button
                            onClick={handleCreateRevision}
                            className="px-3 py-1 text-sm bg-indigo-50 text-indigo-700 border border-indigo-200 rounded hover:bg-indigo-100 flex items-center font-medium shadow-sm transition-colors"
                            disabled={busy}
                        >
                            <span className="mr-1">📦</span> Create Revision
                        </button>
                    )}

                    {/* 4. Convert to Order: Visible ONLY if Frozen & Sent (Status 3) */}
                    {estimate && estimate.is_freezed && estimate.approval_status_id === 3 && (
                        <button
                            onClick={handleConvert}
                            className="px-3 py-1 text-sm bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50 shadow-sm font-medium transition-colors"
                            disabled={busy}
                        >
                            Convert to Order
                        </button>
                    )}

                    <button
                        onClick={handlePreviewQuotation}
                        className="px-3 py-1 text-sm bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 flex items-center shadow-sm"
                    >
                        <Eye size={14} className="mr-1" />
                        Preview
                    </button>

                    <button
                        onClick={handleDownloadPDF}
                        className="px-3 py-1 text-sm bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 flex items-center shadow-sm"
                    >
                        <Download size={14} className="mr-1" />
                        PDF
                    </button>

                    {hasPermission('sales.estimate.email') && (
                        <button
                            onClick={() => setShowSendPdfModal(true)}
                            className="px-3 py-1 text-sm bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 flex items-center shadow-sm"
                        >
                            <Send size={14} className="mr-1" />
                            Send
                        </button>
                    )}

                    {hasPermission('sales.estimate.share') && (
                        <button
                            onClick={handleShareLink}
                            className="px-3 py-1 text-sm bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 flex items-center shadow-sm"
                        >
                            <Link2 size={14} className="mr-1" />
                            Share Link
                        </button>
                    )}

                    {hasPermission('sales.estimate.share') && (
                        <button
                            onClick={handleShareWhatsApp}
                            className="px-3 py-1 text-sm bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 flex items-center shadow-sm"
                        >
                            <MessageCircle size={14} className="mr-1" />
                            WhatsApp
                        </button>
                    )}

                    <button
                        onClick={handleDelete}
                        className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200 flex items-center"
                        disabled={busy}
                        title="Delete Estimate"
                    >
                        <Trash2 size={14} className="mr-1" />
                        Delete
                    </button>

                    {estimate && estimate.approval_status_id === 4 && (
                        <button
                            onClick={handleCreateWorkOrder}
                            className="px-3 py-1 text-sm bg-emerald-600 text-white rounded hover:bg-emerald-700 flex items-center shadow-sm disabled:opacity-50"
                            disabled={busy}
                        >
                            <span className="mr-1">âš™ï¸ </span> WO
                        </button>
                    )}
                </div>
            </div>
            <div className="flex-1 overflow-auto bg-gray-50 p-4">
                <SendPdfModal
                    open={showSendPdfModal}
                    onOpenChange={setShowSendPdfModal}
                    documentLabel="Quotation"
                    documentNumber={estimateNumber}
                    attachmentName={estimateAttachmentName}
                    defaultTo={estimateRecipientEmail}
                    defaultSubject={`Quotation ${estimateNumber}`}
                    defaultBody={`Dear Customer,\n\nPlease find attached quotation ${estimateNumber} for your reference.\n\nRegards,\nHADAF COMPOSITES`}
                    onSend={handleSendPDF}
                />
                {openModal !== 'inputs' && openModal !== 'quotationpreview' && openModal !== 'overheadsheet' && (
                    <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded border bg-white px-4 py-3 shadow-sm">
                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => setOpenModal('inputs')}
                                className="rounded border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
                            >
                                Back to Parts Workspace
                            </button>
                            <div>
                                <div className="text-sm font-semibold text-slate-900">{(openModal ? sectionTitles[openModal] : undefined) || 'Estimate Section'}</div>
                                <div className="text-xs text-slate-500">Using the existing classic section editor with all parts visible.</div>
                            </div>
                        </div>
                    </div>
                )}
                {openModal === 'quotationpreview' && (
                    <div className="mb-3 flex items-center">
                        <button
                            onClick={() => setOpenModal('inputs')}
                            className="rounded border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
                        >
                            Back to Parts Workspace
                        </button>
                    </div>
                )}
                {openModal === 'overheadsheet' && (
                    <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded border bg-white px-4 py-3 shadow-sm">
                        <div>
                            <div className="text-sm font-semibold text-slate-900">Overhead Sheet</div>
                            <div className="text-xs text-slate-500">Update the global overhead defaults here. Returning will refresh the default OH rate for this estimate.</div>
                        </div>
                        <button
                            onClick={handleCloseOverheadSheet}
                            className="rounded border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
                        >
                            Back to Administrative Controls
                        </button>
                    </div>
                )}
                {/* Inputs Tab - Legacy-style entry (Overview + Parts) */}
                {
                    openModal === 'inputs' && estimate && draftHeader && draftParts && (
                        <div className="space-y-4">
                            <div className="bg-white border rounded shadow-sm p-4">
                                <div className="flex flex-wrap items-center justify-between gap-4">
                                    <div>
                                        <h2 className="text-lg font-bold text-gray-900">Parts Workspace</h2>
                                        <p className="text-sm text-gray-500">
                                            Feed the parts list first, then use the section-status cells to open the classic mould cost, production, BOM, installation, pricing, and administrative control pages.
                                        </p>
                                    </div>
                                    <div className="flex flex-wrap items-center gap-2">
                                        <button
                                            onClick={() => setOpenModal('costbreakdown')}
                                            className="px-4 py-2 border border-slate-300 bg-white text-slate-700 rounded hover:bg-slate-50"
                                            title="Open the original classic cost breakdown table"
                                        >
                                            Cost Breakdown
                                        </button>
                                        <button
                                            onClick={() => setOpenModal('overview')}
                                            className="px-4 py-2 border border-slate-300 bg-white text-slate-700 rounded hover:bg-slate-50"
                                            title="Open the detailed overview table"
                                        >
                                            Detailed Overview
                                        </button>
                                        <button
                                            onClick={() => handleInputsSave()}
                                            disabled={!canEdit || savingInputs}
                                            className="px-4 py-2 bg-legacy-blue text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                                            title={!canEdit ? 'Unfreeze and ensure estimate is not converted to edit' : 'Save Inputs'}
                                        >
                                            {savingInputs ? <Loader2 size={16} className="mr-2 animate-spin" /> : null}
                                            Save Inputs
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white border rounded shadow-sm overflow-hidden">
                                <div className="p-4 border-b">
                                    <h3 className="text-base font-bold text-gray-900">Parts List</h3>
                                    <p className="mt-1 text-sm text-gray-500">Total area is auto-calculated. The cost cells open the classic section pages, and each section has one shared reviewed state for the whole estimate.</p>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="w-full border-collapse text-xs">
                                        <thead>
                                            <tr className="bg-gray-50 border-b text-[10px] uppercase tracking-[0.14em] text-gray-600">
                                                <th className="px-2 py-2 text-left min-w-[118px] whitespace-nowrap">Part Code</th>
                                                <th className="px-2 py-2 text-left min-w-[128px] whitespace-nowrap">Material Type</th>
                                                <th className="px-2 py-2 text-left min-w-[230px] whitespace-nowrap">Item Description</th>
                                                <th className="px-2 py-2 text-left min-w-[170px] whitespace-nowrap">Dimension</th>
                                                <th className="px-2 py-2 text-right min-w-[76px] whitespace-nowrap">Qty</th>
                                                <th className="px-2 py-2 text-left min-w-[82px] whitespace-nowrap">UOM</th>
                                                <th className="px-2 py-2 text-right min-w-[94px] whitespace-nowrap">Area M2</th>
                                                <th className="px-2 py-2 text-right min-w-[108px] whitespace-nowrap">Total Area M2</th>
                                                <th className="px-2 py-2 text-right min-w-[92px] whitespace-nowrap">Thickness</th>
                                                <th className="px-2 py-2 text-right min-w-[92px] whitespace-nowrap">Weight</th>
                                                <th className="px-2 py-2 text-center w-[44px]"></th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y">
                                            {draftParts.map((p: any, idx: number) => {
                                                const totalArea = (parseFloat(p.area_m2 || 0) * parseFloat(p.quantity || 0)) || 0;
                                                const materialType = materialTypeLabel(p);
                                                const weight = materialType === 'GRP' ? null : recomputeLineWeight(p);
                                                const partCode = p.part_ref_code || p.formatted_part_id || '-';
                                                return (
                                                    <tr key={p.estimate_part_id ?? `new-${idx}`} className="hover:bg-gray-50">
                                                        <td className="px-2 py-2 text-[11px] font-medium text-gray-700 whitespace-nowrap">
                                                            {partCode}
                                                        </td>
                                                        <td className="px-2 py-2">
                                                            <select
                                                                className="h-8 w-full rounded border px-2 text-xs"
                                                                value={p.material_type_id ?? ''}
                                                                onChange={(e) => setDraftParts(prev => prev!.map((row: any, i: number) => i === idx ? ({ ...row, material_type_id: parseInt(e.target.value || '0', 10) || undefined }) : row))}
                                                                disabled={!canEdit}
                                                            >
                                                                <option value="">Select material</option>
                                                                {materialTypes.map(mt => (
                                                                    <option key={mt.material_type_id} value={mt.material_type_id}>
                                                                        {mt.material_type}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        </td>
                                                        <td className="px-2 py-2">
                                                            <input
                                                                className="h-8 w-full rounded border px-2 text-xs"
                                                                value={p.part_name}
                                                                onChange={(e) => setDraftParts(prev => prev!.map((row: any, i: number) => i === idx ? ({ ...row, part_name: e.target.value }) : row))}
                                                                disabled={!canEdit}
                                                                placeholder="Item description"
                                                                title={p.dimension ? `Dimensions: ${p.dimension}` : 'Item description'}
                                                            />
                                                        </td>
                                                        <td className="px-2 py-2">
                                                            <input
                                                                className="h-8 w-full rounded border px-2 text-xs"
                                                                value={p.dimension || ''}
                                                                onChange={(e) => setDraftParts(prev => prev!.map((row: any, i: number) => i === idx ? ({ ...row, dimension: e.target.value }) : row))}
                                                                disabled={!canEdit}
                                                                placeholder="e.g. 100x50x20"
                                                            />
                                                        </td>
                                                        <td className="px-2 py-2 text-right">
                                                            <input
                                                                type="number"
                                                                className="h-8 w-full rounded border px-2 text-right text-xs tabular-nums"
                                                                value={p.quantity}
                                                                onChange={(e) => setDraftParts(prev => prev!.map((row: any, i: number) => i === idx ? ({ ...row, quantity: e.target.value }) : row))}
                                                                disabled={!canEdit}
                                                            />
                                                        </td>
                                                        <td className="px-2 py-2">
                                                            <select
                                                                className="h-8 w-full rounded border px-2 text-xs"
                                                                value={p.unit_id ?? ''}
                                                                onChange={(e) => setDraftParts(prev => prev!.map((row: any, i: number) => i === idx ? ({ ...row, unit_id: parseInt(e.target.value || '0', 10) || undefined }) : row))}
                                                                disabled={!canEdit}
                                                            >
                                                                <option value="">Select unit</option>
                                                                {units.map(unit => (
                                                                    <option key={unit.unit_id} value={unit.unit_id}>
                                                                        {unit.unit}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        </td>
                                                        <td className="px-2 py-2 text-right">
                                                            <input
                                                                type="number"
                                                                className="h-8 w-full rounded border px-2 text-right text-xs tabular-nums"
                                                                value={p.area_m2}
                                                                onChange={(e) => setDraftParts(prev => prev!.map((row: any, i: number) => i === idx ? ({ ...row, area_m2: e.target.value }) : row))}
                                                                disabled={!canEdit}
                                                            />
                                                        </td>
                                                        <td className="px-2 py-2 text-right tabular-nums font-medium text-gray-700">
                                                            {totalArea.toFixed(2)}
                                                        </td>
                                                        <td className="px-2 py-2 text-right">
                                                            <input
                                                                type="number"
                                                                className="h-8 w-full rounded border px-2 text-right text-xs tabular-nums"
                                                                value={p.thickness}
                                                                onChange={(e) => setDraftParts(prev => prev!.map((row: any, i: number) => i === idx ? ({ ...row, thickness: e.target.value }) : row))}
                                                                disabled={!canEdit}
                                                            />
                                                        </td>
                                                        <td className="px-2 py-2 text-right tabular-nums font-medium text-gray-700">
                                                            {weight === null ? 'N/A' : Number(weight || 0).toFixed(2)}
                                                        </td>
                                                        <td className="px-2 py-2 text-center">
                                                            <button
                                                                onClick={() => {
                                                                    if (!canEdit) return;
                                                                    setDraftParts(prev => prev!.filter((_: any, i: number) => i !== idx));
                                                                }}
                                                                className="text-red-600 hover:text-red-800 disabled:opacity-50"
                                                                disabled={!canEdit}
                                                                title="Remove part"
                                                            >
                                                                <Trash2 size={16} />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                            {draftParts.length === 0 && (
                                                <tr>
                                                    <td colSpan={10} className="px-4 py-6 text-center text-gray-500">
                                                        No parts. Click "Add Part" to add one.
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                                <div className="border-t p-4 space-y-4">
                                    <div className="flex justify-end">
                                        <button
                                            onClick={() => {
                                                if (!canEdit) return;
                                                setDraftParts(prev => ([
                                                    ...(prev || []),
                                                    {
                                                        estimate_part_id: undefined,
                                                        part_name: '',
                                                        dimension: '',
                                                        quantity: 1,
                                                        area_m2: 0,
                                                        thickness: 0,
                                                        weight_kgs: 0,
                                                        mould_area: 0,
                                                        mould_cost_m2: getMouldTypeCost(defaultMouldTypeId()),
                                                        m2_per_hour_production: 0,
                                                        design_premium_perc: 0,
                                                        bom_items: [],
                                                        material_type_id: defaultMaterialTypeId(),
                                                        unit_id: defaultUnitId(),
                                                        mould_type_id: defaultMouldTypeId(),
                                                    }
                                                ]));
                                            }}
                                            disabled={!canEdit}
                                            className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            + Add Part
                                        </button>
                                    </div>
                                    <div className="rounded border bg-slate-50 px-4 py-3">
                                        <div className="mb-3 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Cost Sections</div>
                                        <div className={`grid gap-2 ${showInstallationColumn ? 'grid-cols-1 md:grid-cols-2 xl:grid-cols-7' : 'grid-cols-1 md:grid-cols-2 xl:grid-cols-6'}`}>
                                            <button
                                                type="button"
                                                onClick={() => setOpenModal('tooling')}
                                                className={`w-full rounded border px-3 py-2 text-center text-xs font-semibold ${getSectionCellClass(resolveSectionReviewState('tooling'))}`}
                                            >
                                                Mould Cost · {resolveSectionReviewState('tooling') ? 'Reviewed' : 'Pending'}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setOpenModal('production')}
                                                className={`w-full rounded border px-3 py-2 text-center text-xs font-semibold ${getSectionCellClass(resolveSectionReviewState('production'))}`}
                                            >
                                                Production · {resolveSectionReviewState('production') ? 'Reviewed' : 'Pending'}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setOpenModal('bom')}
                                                className={`w-full rounded border px-3 py-2 text-center text-xs font-semibold ${getSectionCellClass(resolveSectionReviewState('bom'))}`}
                                            >
                                                BOM · {resolveSectionReviewState('bom') ? 'Reviewed' : 'Pending'}
                                            </button>
                                            {showInstallationColumn && (
                                                <button
                                                    type="button"
                                                    onClick={() => setOpenModal('installations')}
                                                    className={`w-full rounded border px-3 py-2 text-center text-xs font-semibold ${getSectionCellClass(resolveSectionReviewState('installations'))}`}
                                                >
                                                    Installation · {resolveSectionReviewState('installations') ? 'Reviewed' : 'Pending'}
                                                </button>
                                            )}
                                            <button
                                                type="button"
                                                onClick={() => openInputsAccordionSection('pricing-controls')}
                                                className={`w-full rounded border px-3 py-2 text-center text-xs font-semibold ${getSectionCellClass(resolveSectionReviewState('pricing'))}`}
                                            >
                                                Pricing Controls · {resolveSectionReviewState('pricing') ? 'Reviewed' : 'Pending'}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => openInputsAccordionSection('pricing-controls', DESIGN_PREMIUM_REVIEW_SECTION_ID)}
                                                className={`w-full rounded border px-3 py-2 text-center text-xs font-semibold ${getSectionCellClass(resolveSectionReviewState('designpremium'))}`}
                                            >
                                                Design Premium Review · {resolveSectionReviewState('designpremium') ? 'Reviewed' : 'Pending'}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => openInputsAccordionSection('administrative-controls')}
                                                className={`w-full rounded border px-3 py-2 text-center text-xs font-semibold ${getSectionCellClass(resolveSectionReviewState('admin'))}`}
                                            >
                                                Administrative Controls · {resolveSectionReviewState('admin') ? 'Reviewed' : 'Pending'}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white border rounded shadow-sm overflow-hidden">
                                <Accordion type="multiple" className="w-full" value={inputsAccordionValues} onValueChange={setInputsAccordionValues}>
                                    <AccordionItem value="project-scope" className="border-b">
                                        <AccordionTrigger className="px-4 text-left hover:no-underline">
                                            <div>
                                                <div className="text-sm font-semibold text-slate-900">Project Scope</div>
                                                <div className="text-xs text-slate-500">Customer, inquiry reference, project type, and project description.</div>
                                            </div>
                                        </AccordionTrigger>
                                        <AccordionContent className="px-4 pb-4">
                                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Customer</label>
                                                    <select className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 outline-none bg-white" value={draftHeader.customer_id} onChange={(e) => setDraftHeader((prev: any) => ({ ...prev, customer_id: e.target.value }))} disabled={!canEdit}>
                                                        <option value="">Select customer...</option>
                                                        {customers.map((c: any) => <option key={c.customer_id} value={c.customer_id}>{c.customer_name}</option>)}
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Inquiry</label>
                                                    <input className="w-full px-3 py-2 border rounded bg-gray-50 text-gray-700" value={(estimate as any).inquiry_id ? `HC/INQ/${String((estimate as any).inquiry_id).padStart(4, '0')}` : '-'} readOnly />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Estimate Project Type</label>
                                                    <input className="w-full px-3 py-2 border rounded bg-gray-50 text-gray-700" value={resolvedEstimateProjectTypeId === 2 ? 'GRP' : resolvedEstimateProjectTypeId === 3 ? 'GRG' : 'GRC'} readOnly />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Inquiry Project Type</label>
                                                    <input className="w-full px-3 py-2 border rounded bg-gray-50 text-gray-700" value={resolvedInquiryProjectTypeId === 2 ? 'GRP' : resolvedInquiryProjectTypeId === 3 ? 'GRG' : 'GRC'} readOnly />
                                                </div>
                                                <div className="md:col-span-2">
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Project Description</label>
                                                    <textarea className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 outline-none" value={draftHeader.project_description || ''} onChange={(e) => setDraftHeader((prev: any) => ({ ...prev, project_description: e.target.value }))} disabled={!canEdit} rows={2} />
                                                </div>
                                            </div>
                                        </AccordionContent>
                                    </AccordionItem>

                                    <AccordionItem value="proposal-content" className="border-b">
                                        <AccordionTrigger className="px-4 text-left hover:no-underline">
                                            <div>
                                                <div className="text-sm font-semibold text-slate-900">Proposal Content</div>
                                                <div className="text-xs text-slate-500">Attention, enquiry reference, and quotation address details.</div>
                                            </div>
                                        </AccordionTrigger>
                                        <AccordionContent className="px-4 pb-4">
                                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Attention</label>
                                                    <input className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 outline-none" value={draftHeader.attention || ''} onChange={(e) => setDraftHeader((prev: any) => ({ ...prev, attention: e.target.value }))} placeholder="POC name" disabled={!canEdit} />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Enquiry Ref</label>
                                                    <input className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 outline-none" value={draftHeader.enquiry_ref || ''} onChange={(e) => setDraftHeader((prev: any) => ({ ...prev, enquiry_ref: e.target.value }))} placeholder="Mail Ref/Whatsapp Ref" disabled={!canEdit} />
                                                </div>
                                                <div className="md:col-span-2">
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Customer Detailed Address</label>
                                                    <textarea className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 outline-none" value={draftHeader.customer_address || ''} onChange={(e) => setDraftHeader((prev: any) => ({ ...prev, customer_address: e.target.value }))} placeholder="Customer address for quotation..." disabled={!canEdit} rows={2} />
                                                </div>
                                            </div>
                                        </AccordionContent>
                                    </AccordionItem>

                                    <AccordionItem value="pricing-controls" id="estimate-inputs-pricing-controls" className="border-b">
                                        <AccordionTrigger className="px-4 text-left hover:no-underline">
                                            <div>
                                                <div className="text-sm font-semibold text-slate-900">Pricing Controls</div>
                                                <div className="text-xs text-slate-500">Profit margin, VAT, discount, and late-stage design premium review before quote conclusion.</div>
                                                <div className={`mt-1 text-[11px] font-medium ${resolveSectionReviewState('pricing') ? 'text-emerald-700' : 'text-amber-700'}`}>
                                                    {resolveSectionReviewState('pricing') ? 'Reviewed' : 'Pending review'}
                                                </div>
                                            </div>
                                        </AccordionTrigger>
                                        <AccordionContent className="px-4 pb-4">
                                            <div className="flex flex-wrap justify-end gap-3 pb-4">
                                                <button
                                                    onClick={() => handleMarkSectionReviewed('pricing')}
                                                    disabled={!canEdit || busy}
                                                    className={`rounded border px-4 py-2 text-xs font-bold uppercase tracking-widest ${getSectionCellClass(resolveSectionReviewState('pricing'))} disabled:opacity-50`}
                                                >
                                                    Mark Reviewed
                                                </button>
                                                <button onClick={handleSaveAdminControls} disabled={busy || estimate.is_freezed} className="bg-primary text-primary-foreground px-4 py-2 rounded-md font-bold text-xs uppercase tracking-widest hover:bg-primary/90 transition-all shadow-md disabled:opacity-50">
                                                    {busy ? 'SAVING...' : 'SAVE CONTROLS'}
                                                </button>
                                            </div>
                                            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                                                <div className="space-y-1">
                                                    <label className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">Profit Margin (%)</label>
                                                    <input type="number" className={`${adminInputClass} font-bold text-primary tabular-nums`} value={formatPercent(estimate.profit_margin || 0).toFixed(2)} onChange={(e) => setEstimate({ ...estimate, profit_margin: (parseFloat(e.target.value) || 0) / 100 })} disabled={!canEdit} />
                                                </div>
                                                <div className="space-y-1">
                                                    <label className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">VAT Rate (%)</label>
                                                    <input type="number" className={`${adminInputClass} font-bold tabular-nums`} value={formatPercent(estimate.vat_percent || 0).toFixed(2)} onChange={(e) => setEstimate({ ...estimate, vat_percent: (parseFloat(e.target.value) || 0) / 100 })} disabled={!canEdit} />
                                                </div>
                                                <div className="space-y-1">
                                                    <label className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">Discount (%)</label>
                                                    <input type="number" className={`${adminInputClass} font-bold tabular-nums`} value={formatPercent(estimate.discount_percent || 0).toFixed(2)} onChange={(e) => setEstimate({ ...estimate, discount_percent: (parseFloat(e.target.value) || 0) / 100 })} disabled={!canEdit} />
                                                </div>
                                            </div>
                                            <div className="mt-6">
                                                {renderDesignPremiumReviewCard()}
                                            </div>
                                        </AccordionContent>
                                    </AccordionItem>

                                    <AccordionItem value="administrative-controls" id="estimate-inputs-administrative-controls" className="border-b">
                                        <AccordionTrigger className="px-4 text-left hover:no-underline">
                                            <div>
                                                <div className="text-sm font-semibold text-slate-900">Administrative Controls</div>
                                                <div className="text-xs text-slate-500">Logistics, labour distribution, overheads, fixed project costs, validity, and annexures.</div>
                                                <div className={`mt-1 text-[11px] font-medium ${resolveSectionReviewState('admin') ? 'text-emerald-700' : 'text-amber-700'}`}>
                                                    {resolveSectionReviewState('admin') ? 'Reviewed' : 'Pending review'}
                                                </div>
                                            </div>
                                        </AccordionTrigger>
                                        <AccordionContent className="px-4 pb-4">
                                            <div className="flex flex-wrap justify-end gap-3 pb-4">
                                                <button
                                                    onClick={() => handleMarkSectionReviewed('admin')}
                                                    disabled={!canEdit || busy}
                                                    className={`rounded border px-4 py-2 text-xs font-bold uppercase tracking-widest ${getSectionCellClass(resolveSectionReviewState('admin'))} disabled:opacity-50`}
                                                >
                                                    Mark Reviewed
                                                </button>
                                                <button onClick={handleSaveAdminControls} disabled={busy || estimate.is_freezed} className="bg-primary text-primary-foreground px-4 py-2 rounded-md font-bold text-xs uppercase tracking-widest hover:bg-primary/90 transition-all shadow-md disabled:opacity-50">
                                                    {busy ? 'SAVING...' : 'SAVE CONTROLS'}
                                                </button>
                                            </div>
                                            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                                                <div className="space-y-6">
                                                    <div className="rounded-lg border p-4">
                                                        <div className="mb-3 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Logistics & Allowances</div>
                                                        <div className="grid grid-cols-2 gap-4">
                                                            <div className="space-y-1">
                                                                <label className="text-[10px] font-bold text-muted-foreground uppercase">Packing & Loading (%)</label>
                                                                <input type="number" className={adminInputClass} value={formatPercent(estimate.logistics_packing_loading_perc || 0).toFixed(2)} onChange={(e) => setEstimate({ ...estimate, logistics_packing_loading_perc: (parseFloat(e.target.value) || 0) / 100 })} disabled={!canEdit} />
                                                            </div>
                                                            <div className="space-y-1">
                                                                <label className="text-[10px] font-bold text-muted-foreground uppercase">Freight Cost (%)</label>
                                                                <input type="number" className={adminInputClass} value={formatPercent(estimate.logistics_freight_cost_perc || 0).toFixed(2)} onChange={(e) => setEstimate({ ...estimate, logistics_freight_cost_perc: (parseFloat(e.target.value) || 0) / 100 })} disabled={!canEdit} />
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="rounded-lg border p-4">
                                                        <div className="mb-3 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Project Management (Fixed Costs)</div>
                                                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                                            {[
                                                                { label: 'Design & Engineering', field: 'pm_design_cost' },
                                                                { label: 'Project Manager Cost', field: 'pm_project_manager_cost' },
                                                                { label: 'Site Intervention', field: 'pm_site_intervention_cost' },
                                                                { label: 'Documentation / Drawings', field: 'pm_documentation_cost' },
                                                                { label: 'Travel Expenses', field: 'pm_travel_cost' },
                                                            ].map(item => (
                                                                <div key={item.field} className="space-y-1">
                                                                    <label className="text-[10px] font-bold text-muted-foreground uppercase">{item.label}</label>
                                                                    <input type="number" className={adminInputClass} value={(estimate as any)[item.field] || 0} onChange={(e) => setEstimate({ ...estimate, [item.field]: parseFloat(e.target.value) || 0 })} disabled={!canEdit} />
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="space-y-6">
                                                    <div className="rounded-lg border p-4">
                                                        <div className="mb-3 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Administrative Overheads</div>
                                                        <div className="grid grid-cols-2 gap-4">
                                                            <div className="space-y-1">
                                                                <label className="text-[10px] font-bold text-muted-foreground uppercase">Contingency (%)</label>
                                                                <input type="number" className={adminInputClass} value={formatPercent(estimate.admin_contigency_perc || 0).toFixed(2)} onChange={(e) => setEstimate({ ...estimate, admin_contigency_perc: (parseFloat(e.target.value) || 0) / 100 })} disabled={!canEdit} />
                                                            </div>
                                                            <div className="space-y-1">
                                                                <label className="text-[10px] font-bold text-muted-foreground uppercase">Warranty (%)</label>
                                                                <input type="number" className={adminInputClass} value={formatPercent(estimate.admin_warranty_perc || 0).toFixed(2)} onChange={(e) => setEstimate({ ...estimate, admin_warranty_perc: (parseFloat(e.target.value) || 0) / 100 })} disabled={!canEdit} />
                                                            </div>
                                                            <div className="space-y-1">
                                                                <label className="text-[10px] font-bold text-muted-foreground uppercase">Retention (%)</label>
                                                                <input type="number" className={adminInputClass} value={formatPercent(estimate.admin_retention_perc || 0).toFixed(2)} onChange={(e) => setEstimate({ ...estimate, admin_retention_perc: (parseFloat(e.target.value) || 0) / 100 })} disabled={!canEdit} />
                                                            </div>
                                                            <div className="space-y-1">
                                                                <label className="text-[10px] font-bold text-muted-foreground uppercase">Banking (%)</label>
                                                                <input type="number" className={adminInputClass} value={formatPercent(estimate.admin_banking_finance_perc || 0).toFixed(2)} onChange={(e) => setEstimate({ ...estimate, admin_banking_finance_perc: (parseFloat(e.target.value) || 0) / 100 })} disabled={!canEdit} />
                                                            </div>
                                                            <div className="space-y-1">
                                                                <label className="text-[10px] font-bold text-muted-foreground uppercase">Other Costs (%)</label>
                                                                <input type="number" className={adminInputClass} value={formatPercent(estimate.admin_other_cost_perc || 0).toFixed(2)} onChange={(e) => setEstimate({ ...estimate, admin_other_cost_perc: (parseFloat(e.target.value) || 0) / 100 })} disabled={!canEdit} />
                                                            </div>
                                                            <div className="col-span-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-3">
                                                                <div className="space-y-3">
                                                                    <div className="flex flex-wrap items-end justify-between gap-3">
                                                                        <div className="flex min-w-[220px] flex-1 flex-col gap-2">
                                                                            <div className="flex flex-wrap items-center gap-2">
                                                                                <label className="text-[10px] font-bold text-muted-foreground uppercase">OH / SQM (AED)</label>
                                                                                {ohSourceStatus && (
                                                                                    <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] ${ohSourceStatus.badgeClass}`}>
                                                                                        {ohSourceStatus.label}
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                            <input
                                                                                type="number"
                                                                                className={`${adminInputClass} w-full max-w-[220px] tabular-nums`}
                                                                                value={estimate.oh_per_sqm || 0}
                                                                                onChange={(e) => setEstimate({ ...estimate, oh_per_sqm: parseFloat(e.target.value) || 0 })}
                                                                                disabled={!canEdit}
                                                                            />
                                                                        </div>
                                                                        <div className="flex flex-wrap gap-2">
                                                                            <button
                                                                                type="button"
                                                                                onClick={handleOpenOverheadSheet}
                                                                                className="rounded border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                                                                            >
                                                                                Open Overhead Sheet
                                                                            </button>
                                                                            <button
                                                                                type="button"
                                                                                onClick={handleApplyOhSheetValue}
                                                                                disabled={!canEdit || !ohSheetRatePerSqm}
                                                                                className="rounded border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                                                                            >
                                                                                Use OH Sheet Value
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                    <div className="space-y-1 text-xs text-slate-500">
                                                                        <div>
                                                                            Overhead sheet default: {ohSheetRatePerSqm ? `${formatCurrency(ohSheetRatePerSqm)} AED / SQM` : 'Unavailable'}
                                                                        </div>
                                                                        {ohSourceStatus && (
                                                                            <div className="max-w-3xl">
                                                                                {ohSourceStatus.detail}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="rounded-lg border p-4">
                                                        <div className="mb-3 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Validity & Annexures</div>
                                                        <div className="space-y-4">
                                                            <div className="space-y-1">
                                                                <label className="text-[10px] font-bold text-muted-foreground uppercase">Validity</label>
                                                                <input type="text" className={adminInputClass} value={estimate.validity || ''} onChange={(e) => setEstimate({ ...estimate, validity: e.target.value })} disabled={!canEdit} />
                                                            </div>
                                                            <div className="space-y-1">
                                                                <label className="text-[10px] font-bold text-muted-foreground uppercase">Technical Annexure (Annexure A)</label>
                                                                <textarea className={`${adminInputClass} min-h-[110px]`} value={estimate.annexure_a || ''} onChange={(e) => setEstimate({ ...estimate, annexure_a: e.target.value })} disabled={!canEdit} />
                                                            </div>
                                                            <div className="space-y-1">
                                                                <label className="text-[10px] font-bold text-muted-foreground uppercase">Terms & Conditions (Annexure B)</label>
                                                                <textarea className={`${adminInputClass} min-h-[110px]`} value={estimate.annexure_b || ''} onChange={(e) => setEstimate({ ...estimate, annexure_b: e.target.value })} disabled={!canEdit} />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </AccordionContent>
                                    </AccordionItem>
                                </Accordion>
                            </div>
                        </div>
                    )
                }

                {/* Cost Breakdown Tab - Default View */}
                {
                    openModal === 'costbreakdown' && (
                        <div ref={costBreakdownRef} className="bg-white border rounded shadow-sm overflow-hidden">
                            <div className="border-b bg-muted/20 px-4 py-3">
                                <div className="flex flex-wrap items-center gap-2">
                                    <div className="mr-2 text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">Columns</div>
                                    <button
                                        type="button"
                                        className={`rounded border px-3 py-1 text-xs font-medium transition-colors ${costBreakdownGroupPrefs.directBreakdown ? 'border-primary bg-primary/10 text-primary' : 'border-border bg-white text-muted-foreground'}`}
                                        title={directBreakdownAutoHidden ? 'Auto hidden due to width' : 'Toggle direct breakdown columns'}
                                        onClick={() => {
                                            setCostBreakdownManualGroups(true);
                                            setCostBreakdownGroupPrefs((prev) => ({ ...prev, directBreakdown: !prev.directBreakdown }));
                                        }}
                                    >
                                        Direct Breakdown{directBreakdownAutoHidden ? ' · Auto hidden' : ''}
                                    </button>
                                    <button
                                        type="button"
                                        className={`rounded border px-3 py-1 text-xs font-medium transition-colors ${costBreakdownGroupPrefs.otherCosts ? 'border-primary bg-primary/10 text-primary' : 'border-border bg-white text-muted-foreground'}`}
                                        title={otherCostsAutoHidden ? 'Auto hidden due to width' : 'Toggle other cost columns'}
                                        onClick={() => {
                                            setCostBreakdownManualGroups(true);
                                            setCostBreakdownGroupPrefs((prev) => ({ ...prev, otherCosts: !prev.otherCosts }));
                                        }}
                                    >
                                        Other Costs{otherCostsAutoHidden ? ' · Auto hidden' : ''}
                                    </button>
                                </div>
                            </div>
                            {detailedOverview && (
                                <div className="border-b bg-slate-50/80 px-4 py-4">
                                    <div className="mb-3 flex items-center justify-between gap-3">
                                        <div>
                                            <div className="text-sm font-semibold text-slate-900">Commercial Overview</div>
                                            <div className="text-xs text-slate-500">At-a-glance totals aligned to the classic estimate breakdown.</div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setOpenModal('overview')}
                                            className="rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                                        >
                                            Open Detailed Overview
                                        </button>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-6">
                                        {overviewCardMetrics.map((metric) => (
                                            <div key={metric.label} className="rounded-lg border bg-white px-3 py-3 shadow-sm">
                                                <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">{metric.label}</div>
                                                <div className={`mt-2 text-lg font-semibold tabular-nums ${metric.accent}`}>{metric.value}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            <div className="overflow-x-auto">
                                {costBreakdownMode === 'full' ? (
                                <table
                                    className="w-full table-fixed border-collapse text-xs text-left leading-tight"
                                    style={{ width: 'max(100%, 1520px)' }}
                                >
                                    <colgroup>
                                        <col className="w-[160px]" />
                                        <col className="w-[72px]" />
                                        <col className="w-[54px]" />
                                        <col className="w-[50px]" />
                                        <col className="w-[64px]" />
                                        <col className="w-[68px]" />
                                        <col className="w-[68px]" />
                                        <col className="w-[50px]" />
                                        <col className="w-[50px]" />
                                        <col className="w-[48px]" />
                                        <col className="w-[48px]" />
                                        <col className="w-[52px]" />
                                        <col className="w-[50px]" />
                                        <col className="w-[48px]" />
                                        <col className="w-[48px]" />
                                        <col className="w-[52px]" />
                                        <col className="w-[48px]" />
                                        <col className="w-[74px]" />
                                        <col className="w-[74px]" />
                                        <col className="w-[54px]" />
                                        <col className="w-[78px]" />
                                        <col className="w-[74px]" />
                                        <col className="w-[74px]" />
                                        <col className="w-[78px]" />
                                    </colgroup>
                                    <thead>
                                        <tr className="bg-muted/50 text-muted-foreground border-b uppercase tracking-[0.08em] font-semibold text-[10px]">
                                            <th rowSpan={2} title="Part Description" className="px-2 py-1.5 border-r text-center whitespace-nowrap">Part</th>
                                            <th rowSpan={2} title="Material Type" className="px-1 py-1.5 border-r text-center whitespace-nowrap">Material</th>
                                            <th rowSpan={2} title="Unit of Measure" className="px-1 py-1.5 border-r text-center whitespace-nowrap">UOM</th>
                                            <th rowSpan={2} title="Quantity" className="px-1 py-1.5 text-center border-r whitespace-nowrap">Qty</th>
                                            <th rowSpan={2} title="Area (m2)" className="px-1 py-1.5 text-center border-r whitespace-nowrap">Area</th>
                                            <th rowSpan={2} title="Total Area (m2)" className="px-1 py-1.5 text-center border-r whitespace-nowrap">Total Area</th>
                                            <th rowSpan={2} title="Weight (kg)" className="px-1 py-1.5 text-center border-r whitespace-nowrap">Weight</th>
                                            <th colSpan={10} className="px-2 py-1 text-center border-b border-r bg-primary text-primary-foreground tracking-[0.06em]">Calculation Breakdown (AED)</th>
                                            <th rowSpan={2} title="Total Cost" className="px-1 py-1.5 text-center border-r whitespace-nowrap">Total Cost</th>
                                            <th rowSpan={2} title="Base Price / SQM" className="px-1 py-1.5 text-center border-r whitespace-nowrap">Base/SQM</th>
                                            <th rowSpan={2} title="Design Premium" className="px-1 py-1.5 text-center border-r whitespace-nowrap">DP</th>
                                            <th rowSpan={2} title="Revised Base Price" className="px-1 py-1.5 text-center border-r whitespace-nowrap">Rev Base</th>
                                            <th rowSpan={2} title="Selling / Unit" className="px-1 py-1.5 text-center border-r whitespace-nowrap">Sell/Unit</th>
                                            <th rowSpan={2} title="Selling Price" className="px-1 py-1.5 text-center border-r whitespace-nowrap">Sell</th>
                                            <th rowSpan={2} title="Round Off" className="px-1 py-1.5 text-center whitespace-nowrap">Round Off</th>
                                        </tr>
                                        <tr className="bg-muted text-muted-foreground border-b text-[10px] font-medium">
                                            <th title="Pattern / Mould" className="px-1 py-1 text-center border-r whitespace-nowrap">Pattern</th>
                                            <th title="Production" className="px-1 py-1 text-center border-r whitespace-nowrap">Prod.</th>
                                            <th title="BOM" className="px-1 py-1 text-center border-r whitespace-nowrap">BOM</th>
                                            <th title="Installation" className="px-1 py-1 text-center border-r whitespace-nowrap">Install</th>
                                            <th title="Direct Cost" className="px-1 py-1 text-center border-r bg-primary/5 font-bold text-primary whitespace-nowrap">Direct</th>
                                            <th title="Packing" className="px-1 py-1 text-center border-r whitespace-nowrap">Packing</th>
                                            <th title="Freight" className="px-1 py-1 text-center border-r whitespace-nowrap">Freight</th>
                                            <th title="Project" className="px-1 py-1 text-center border-r whitespace-nowrap">Proj</th>
                                            <th title="Administrative Cost" className="px-1 py-1 text-center border-r whitespace-nowrap">Admin</th>
                                            <th title="Overhead" className="px-1 py-1 text-center border-r whitespace-nowrap">OH</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y">
                                        {costRows.map(r => (
                                            <tr key={r.part.estimate_part_id} className="hover:bg-muted/30 transition-colors text-[11px]">
                                                <td title={r.part.part_name} className="px-2 py-1.5 text-left font-medium border-r">
                                                    <div className="truncate whitespace-nowrap">{r.part.part_name}</div>
                                                </td>
                                                <td title={r.materialType} className="px-1.5 py-1.5 text-left border-r bg-muted/10 text-muted-foreground font-mono whitespace-nowrap">{r.materialType}</td>
                                                <td title={r.unit} className="px-1.5 py-1.5 text-left border-r text-muted-foreground whitespace-nowrap">{r.unit}</td>
                                                <td className="px-1.5 py-1.5 text-right border-r tabular-nums whitespace-nowrap">{formatCompactNumber(r.part.quantity, true)}</td>
                                                <td className="px-1.5 py-1.5 text-right border-r tabular-nums whitespace-nowrap">{formatCompactNumber(r.part.area_m2)}</td>
                                                <td className="px-1.5 py-1.5 text-right border-r tabular-nums whitespace-nowrap">{formatCompactNumber(r.areaTotal)}</td>
                                                <td className="px-1.5 py-1.5 text-right border-r tabular-nums whitespace-nowrap">{formatCompactNumber(r.totalWeight)}</td>

                                                <td className="px-1 py-1.5 text-right border-r tabular-nums whitespace-nowrap">{formatCompactNumber(r.patternMould)}</td>
                                                <td className="px-1 py-1.5 text-right border-r tabular-nums whitespace-nowrap">{formatCompactNumber(r.production)}</td>
                                                <td className="px-1 py-1.5 text-right border-r tabular-nums whitespace-nowrap">{formatCompactNumber(r.bomCost)}</td>
                                                <td className="px-1 py-1.5 text-right border-r tabular-nums whitespace-nowrap">{formatCompactNumber(r.installation)}</td>
                                                <td className="px-1 py-1.5 text-right border-r bg-primary/5 font-bold tabular-nums text-primary whitespace-nowrap">{formatCompactNumber(r.patternMould + r.production + r.bomCost + r.installation)}</td>

                                                <td className="px-1 py-1.5 text-right border-r tabular-nums whitespace-nowrap">{formatCompactNumber(r.packing)}</td>
                                                <td className="px-1 py-1.5 text-right border-r tabular-nums whitespace-nowrap">{formatCompactNumber(r.freight)}</td>
                                                <td className="px-1 py-1.5 text-right border-r tabular-nums whitespace-nowrap">{formatCompactNumber(r.project)}</td>
                                                <td className="px-1 py-1.5 text-right border-r tabular-nums whitespace-nowrap">{formatCompactNumber(r.admin)}</td>
                                                <td className="px-1 py-1.5 text-right border-r tabular-nums whitespace-nowrap">{formatCompactNumber(r.overhead)}</td>

                                                <td className="px-1.5 py-1.5 text-right border-r font-bold text-primary tabular-nums whitespace-nowrap">{formatCompactNumber(r.totalCost)}</td>
                                                <td className="px-1.5 py-1.5 text-right border-r tabular-nums whitespace-nowrap">{formatCompactNumber(r.basePerSqm)}</td>
                                                <td className="px-1 py-1.5 text-center border-r">
                                                    <span className="inline-flex rounded bg-slate-100 px-2 py-0.5 text-[11px] font-bold text-slate-700 whitespace-nowrap">
                                                        {formatPercent(r.designPremiumPerc).toFixed(0)}%
                                                    </span>
                                                </td>
                                                <td className="px-1.5 py-1.5 text-right border-r tabular-nums whitespace-nowrap">{formatCompactNumber(r.revisedPerUnit)}</td>
                                                <td className="px-1.5 py-1.5 text-right border-r tabular-nums whitespace-nowrap">{formatCompactNumber(r.sellingPerUnit, true)}</td>
                                                <td className="px-1.5 py-1.5 text-right border-r tabular-nums whitespace-nowrap">{formatCompactNumber(r.sellingPriceTotal, true)}</td>
                                                <td className="px-1.5 py-1.5 text-right font-bold bg-muted/20 tabular-nums text-green-700 whitespace-nowrap">{formatCompactNumber(r.roundOff, true)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot className="bg-muted/50 font-bold border-t-2 text-[11px]">
                                        <tr>
                                            <td className="px-2 py-2 text-left border-r uppercase tracking-[0.08em] text-muted-foreground whitespace-nowrap" colSpan={5}>Grand Totals</td>
                                            <td className="px-1.5 py-2 text-right border-r tabular-nums whitespace-nowrap">{formatCompactNumber(totals.areaTotal)}</td>
                                            <td className="px-1.5 py-2 text-right border-r tabular-nums whitespace-nowrap">{formatCompactNumber(totals.totalWeight)}</td>

                                            <td className="px-1 py-2 text-right border-r tabular-nums whitespace-nowrap">{formatCompactNumber(totals.patternMould)}</td>
                                            <td className="px-1 py-2 text-right border-r tabular-nums whitespace-nowrap">{formatCompactNumber(totals.production)}</td>
                                            <td className="px-1 py-2 text-right border-r tabular-nums whitespace-nowrap">{formatCompactNumber(totals.bomCost)}</td>
                                            <td className="px-1 py-2 text-right border-r tabular-nums whitespace-nowrap">{formatCompactNumber(totals.install)}</td>
                                            <td className="px-1 py-2 text-right border-r bg-primary/10 font-bold tabular-nums text-primary whitespace-nowrap">{formatCompactNumber(totals.patternMould + totals.production + totals.bomCost + totals.install)}</td>

                                            <td className="px-1 py-2 text-right border-r tabular-nums whitespace-nowrap">{formatCompactNumber(totals.packing)}</td>
                                            <td className="px-1 py-2 text-right border-r tabular-nums whitespace-nowrap">{formatCompactNumber(totals.freight)}</td>
                                            <td className="px-1 py-2 text-right border-r tabular-nums whitespace-nowrap">{formatCompactNumber(totals.project)}</td>
                                            <td className="px-1 py-2 text-right border-r tabular-nums whitespace-nowrap">{formatCompactNumber(totals.admin)}</td>
                                            <td className="px-1 py-2 text-right border-r tabular-nums whitespace-nowrap">{formatCompactNumber(totals.overhead)}</td>

                                            <td className="px-1.5 py-2 text-right border-r text-primary font-bold tabular-nums whitespace-nowrap">{formatCompactNumber(persistedCommercials.totalCost)}</td>
                                                <td className="px-1.5 py-2 border-r" />
                                                <td className="px-1 py-2 text-center border-r">
                                                <span className="text-muted-foreground text-[10px] whitespace-nowrap">
                                                    Avg: {formatCompactNumber(formatPercent(averageDesignPremiumPercent))}%
                                                </span>
                                                </td>
                                            <td className="px-1.5 py-2 border-r" />
                                            <td className="px-1.5 py-2 border-r" />
                                            <td className="px-1.5 py-2 text-right border-r font-bold tabular-nums text-primary whitespace-nowrap">{formatCompactNumber(persistedCommercials.sellingPrice, true)}</td>
                                            <td className="px-1.5 py-2 text-right font-bold bg-primary/5 text-green-700 tabular-nums whitespace-nowrap">{formatCompactNumber(totals.roundOff, true)}</td>
                                        </tr>
                                    </tfoot>
                                </table>
                                ) : (
                                    <table
                                        className="w-full table-fixed border-collapse text-xs text-left leading-tight"
                                        style={{ width: 'max(100%, 1260px)' }}
                                    >
                                        <colgroup>
                                            <col className="w-[160px]" />
                                            <col className="w-[72px]" />
                                            <col className="w-[54px]" />
                                            <col className="w-[50px]" />
                                            <col className="w-[64px]" />
                                            <col className="w-[68px]" />
                                            <col className="w-[68px]" />
                                            {showDirectBreakdown && (
                                                <>
                                                    <col className="w-[50px]" />
                                                    <col className="w-[50px]" />
                                                    <col className="w-[48px]" />
                                                    <col className="w-[48px]" />
                                                </>
                                            )}
                                            <col className="w-[52px]" />
                                            {showOtherCosts && (
                                                <>
                                                    <col className="w-[52px]" />
                                                    <col className="w-[50px]" />
                                                    <col className="w-[48px]" />
                                                    <col className="w-[48px]" />
                                                    <col className="w-[52px]" />
                                                </>
                                            )}
                                            <col className="w-[74px]" />
                                            <col className="w-[74px]" />
                                            <col className="w-[54px]" />
                                            <col className="w-[78px]" />
                                            <col className="w-[74px]" />
                                            <col className="w-[74px]" />
                                            <col className="w-[78px]" />
                                        </colgroup>
                                        <thead>
                                            <tr className="bg-muted/50 text-muted-foreground border-b uppercase tracking-[0.08em] font-semibold text-[10px]">
                                                <th title="Part Description" className="px-2 py-1.5 border-r text-center whitespace-nowrap">Part</th>
                                                <th title="Material Type" className="px-1 py-1.5 border-r text-center whitespace-nowrap">Material</th>
                                                <th title="Unit of Measure" className="px-1 py-1.5 border-r text-center whitespace-nowrap">UOM</th>
                                                <th title="Quantity" className="px-1 py-1.5 text-center border-r whitespace-nowrap">Qty</th>
                                                <th title="Area (m2)" className="px-1 py-1.5 text-center border-r whitespace-nowrap">Area</th>
                                                <th title="Total Area (m2)" className="px-1 py-1.5 text-center border-r whitespace-nowrap">Total Area</th>
                                                <th title="Weight (kg)" className="px-1 py-1.5 text-center border-r whitespace-nowrap">Weight</th>
                                                {showDirectBreakdown && (
                                                    <>
                                                        <th title="Pattern / Mould" className="px-1 py-1.5 text-center border-r whitespace-nowrap">Pattern</th>
                                                        <th title="Production" className="px-1 py-1.5 text-center border-r whitespace-nowrap">Prod.</th>
                                                        <th title="BOM" className="px-1 py-1.5 text-center border-r whitespace-nowrap">BOM</th>
                                                        <th title="Installation" className="px-1 py-1.5 text-center border-r whitespace-nowrap">Install</th>
                                                    </>
                                                )}
                                                <th title="Direct Cost" className="px-1 py-1.5 text-center border-r bg-primary/5 font-bold text-primary whitespace-nowrap">Direct</th>
                                                {showOtherCosts && (
                                                    <>
                                                        <th title="Packing" className="px-1 py-1.5 text-center border-r whitespace-nowrap">Packing</th>
                                                        <th title="Freight" className="px-1 py-1.5 text-center border-r whitespace-nowrap">Freight</th>
                                                        <th title="Project" className="px-1 py-1.5 text-center border-r whitespace-nowrap">Proj</th>
                                                        <th title="Administrative Cost" className="px-1 py-1.5 text-center border-r whitespace-nowrap">Admin</th>
                                                        <th title="Overhead" className="px-1 py-1.5 text-center border-r whitespace-nowrap">OH</th>
                                                    </>
                                                )}
                                                <th title="Total Cost" className="px-1 py-1.5 text-center border-r whitespace-nowrap">Total Cost</th>
                                                <th title="Base Price / SQM" className="px-1 py-1.5 text-center border-r whitespace-nowrap">Base/SQM</th>
                                                <th title="Design Premium" className="px-1 py-1.5 text-center border-r whitespace-nowrap">DP</th>
                                                <th title="Revised Base Price" className="px-1 py-1.5 text-center border-r whitespace-nowrap">Rev Base</th>
                                                <th title="Selling / Unit" className="px-1 py-1.5 text-center border-r whitespace-nowrap">Sell/Unit</th>
                                                <th title="Selling Price" className="px-1 py-1.5 text-center border-r whitespace-nowrap">Sell</th>
                                                <th title="Round Off" className="px-1 py-1.5 text-center whitespace-nowrap">Round Off</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y">
                                            {costRows.map((r) => (
                                                <tr key={r.part.estimate_part_id} className="hover:bg-muted/30 transition-colors text-[11px]">
                                                    <td title={r.part.part_name} className="px-2 py-1.5 text-left font-medium border-r">
                                                        <div className="truncate whitespace-nowrap">{r.part.part_name}</div>
                                                    </td>
                                                    <td title={r.materialType} className="px-1.5 py-1.5 text-left border-r bg-muted/10 text-muted-foreground font-mono whitespace-nowrap">{r.materialType}</td>
                                                    <td title={r.unit} className="px-1.5 py-1.5 text-left border-r text-muted-foreground whitespace-nowrap">{r.unit}</td>
                                                    <td className="px-1.5 py-1.5 text-right border-r tabular-nums whitespace-nowrap">{formatCompactNumber(r.part.quantity, true)}</td>
                                                    <td className="px-1.5 py-1.5 text-right border-r tabular-nums whitespace-nowrap">{formatCompactNumber(r.part.area_m2)}</td>
                                                    <td className="px-1.5 py-1.5 text-right border-r tabular-nums whitespace-nowrap">{formatCompactNumber(r.areaTotal)}</td>
                                                    <td className="px-1.5 py-1.5 text-right border-r tabular-nums whitespace-nowrap">{formatCompactNumber(r.totalWeight)}</td>
                                                    {showDirectBreakdown && (
                                                        <>
                                                            <td className="px-1 py-1.5 text-right border-r tabular-nums whitespace-nowrap">{formatCompactNumber(r.patternMould)}</td>
                                                            <td className="px-1 py-1.5 text-right border-r tabular-nums whitespace-nowrap">{formatCompactNumber(r.production)}</td>
                                                            <td className="px-1 py-1.5 text-right border-r tabular-nums whitespace-nowrap">{formatCompactNumber(r.bomCost)}</td>
                                                            <td className="px-1 py-1.5 text-right border-r tabular-nums whitespace-nowrap">{formatCompactNumber(r.installation)}</td>
                                                        </>
                                                    )}
                                                    <td className="px-1.5 py-1.5 text-right border-r bg-primary/5 font-bold tabular-nums text-primary whitespace-nowrap">{formatCompactNumber(r.patternMould + r.production + r.bomCost + r.installation)}</td>
                                                    {showOtherCosts && (
                                                        <>
                                                            <td className="px-1 py-1.5 text-right border-r tabular-nums whitespace-nowrap">{formatCompactNumber(r.packing)}</td>
                                                            <td className="px-1 py-1.5 text-right border-r tabular-nums whitespace-nowrap">{formatCompactNumber(r.freight)}</td>
                                                            <td className="px-1 py-1.5 text-right border-r tabular-nums whitespace-nowrap">{formatCompactNumber(r.project)}</td>
                                                            <td className="px-1 py-1.5 text-right border-r tabular-nums whitespace-nowrap">{formatCompactNumber(r.admin)}</td>
                                                            <td className="px-1 py-1.5 text-right border-r tabular-nums whitespace-nowrap">{formatCompactNumber(r.overhead)}</td>
                                                        </>
                                                    )}
                                                    <td className="px-1.5 py-1.5 text-right border-r font-bold text-primary tabular-nums whitespace-nowrap">{formatCompactNumber(r.totalCost)}</td>
                                                    <td className="px-1.5 py-1.5 text-right border-r tabular-nums whitespace-nowrap">{formatCompactNumber(r.basePerSqm)}</td>
                                                    <td className="px-1 py-1.5 text-center border-r">
                                                        <span className="inline-flex rounded bg-slate-100 px-2 py-0.5 text-[11px] font-bold text-slate-700 whitespace-nowrap">
                                                            {formatPercent(r.designPremiumPerc).toFixed(0)}%
                                                        </span>
                                                    </td>
                                                    <td className="px-1.5 py-1.5 text-right border-r tabular-nums whitespace-nowrap">{formatCompactNumber(r.revisedPerUnit)}</td>
                                                    <td className="px-1.5 py-1.5 text-right border-r tabular-nums whitespace-nowrap">{formatCompactNumber(r.sellingPerUnit, true)}</td>
                                                    <td className="px-1.5 py-1.5 text-right border-r tabular-nums whitespace-nowrap">{formatCompactNumber(r.sellingPriceTotal, true)}</td>
                                                    <td className="px-1.5 py-1.5 text-right font-bold bg-muted/20 tabular-nums text-green-700 whitespace-nowrap">{formatCompactNumber(r.roundOff, true)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot className="bg-muted/50 font-bold border-t-2 text-[11px]">
                                            <tr>
                                                <td className="px-2 py-2 text-left border-r uppercase tracking-[0.08em] text-muted-foreground whitespace-nowrap" colSpan={5}>Grand Totals</td>
                                                <td className="px-1.5 py-2 text-right border-r tabular-nums whitespace-nowrap">{formatCompactNumber(totals.areaTotal)}</td>
                                                <td className="px-1.5 py-2 text-right border-r tabular-nums whitespace-nowrap">{formatCompactNumber(totals.totalWeight)}</td>
                                                {showDirectBreakdown && (
                                                    <>
                                                        <td className="px-1 py-2 text-right border-r tabular-nums whitespace-nowrap">{formatCompactNumber(totals.patternMould)}</td>
                                                        <td className="px-1 py-2 text-right border-r tabular-nums whitespace-nowrap">{formatCompactNumber(totals.production)}</td>
                                                        <td className="px-1 py-2 text-right border-r tabular-nums whitespace-nowrap">{formatCompactNumber(totals.bomCost)}</td>
                                                        <td className="px-1 py-2 text-right border-r tabular-nums whitespace-nowrap">{formatCompactNumber(totals.install)}</td>
                                                    </>
                                                )}
                                                <td className="px-1.5 py-2 text-right border-r bg-primary/10 font-bold tabular-nums text-primary whitespace-nowrap">{formatCompactNumber(totals.patternMould + totals.production + totals.bomCost + totals.install)}</td>
                                                {showOtherCosts && (
                                                    <>
                                                        <td className="px-1 py-2 text-right border-r tabular-nums whitespace-nowrap">{formatCompactNumber(totals.packing)}</td>
                                                        <td className="px-1 py-2 text-right border-r tabular-nums whitespace-nowrap">{formatCompactNumber(totals.freight)}</td>
                                                        <td className="px-1 py-2 text-right border-r tabular-nums whitespace-nowrap">{formatCompactNumber(totals.project)}</td>
                                                        <td className="px-1 py-2 text-right border-r tabular-nums whitespace-nowrap">{formatCompactNumber(totals.admin)}</td>
                                                        <td className="px-1 py-2 text-right border-r tabular-nums whitespace-nowrap">{formatCompactNumber(totals.overhead)}</td>
                                                    </>
                                                )}
                                                <td className="px-1.5 py-2 text-right border-r text-primary font-bold tabular-nums whitespace-nowrap">{formatCompactNumber(persistedCommercials.totalCost)}</td>
                                                <td className="px-1.5 py-2 border-r" />
                                                <td className="px-1 py-2 text-center border-r">
                                                    <span className="text-muted-foreground text-[10px] whitespace-nowrap">
                                                        Avg: {formatCompactNumber(formatPercent(averageDesignPremiumPercent))}%
                                                    </span>
                                                </td>
                                                <td className="px-1.5 py-2 border-r" />
                                                <td className="px-1.5 py-2 border-r" />
                                                <td className="px-1.5 py-2 text-right border-r font-bold tabular-nums text-primary whitespace-nowrap">{formatCompactNumber(persistedCommercials.sellingPrice, true)}</td>
                                                <td className="px-1.5 py-2 text-right font-bold bg-primary/5 text-green-700 tabular-nums whitespace-nowrap">{formatCompactNumber(totals.roundOff, true)}</td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                )}

                                {/* Final Summary Section */}
                                <div className="mt-8 p-6 flex justify-end bg-muted/10 border-t">
                                        <div className="w-full max-w-sm space-y-2">
                                            <div className="flex justify-between items-center py-2 border-b border-muted">
                                                <span className="text-sm font-medium text-muted-foreground">Subtotal (Rounded)</span>
                                                <span className="font-mono font-bold">{(detailedOverview?.roundedSellingTotal || totals.roundOff).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                                            </div>
                                            {detailedOverview && (
                                                    <>
                                                    <div className="flex justify-between items-center py-2 border-b border-muted">
                                                        <span className="text-sm font-medium text-muted-foreground">Discount ({formatPercent(estimate.discount_percent).toFixed(2)}%)</span>
                                                        <span className="font-mono text-red-600">
                                                            -{detailedOverview.discountAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                                        </span>
                                                    </div>
                                                    <div className="flex justify-between items-center py-2 border-b border-muted">
                                                        <span className="text-sm font-medium text-muted-foreground">Amount After Disc.</span>
                                                        <span className="font-mono font-semibold">
                                                            {detailedOverview.amountAfterDiscount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                                        </span>
                                                    </div>
                                                    <div className="flex justify-between items-center py-2 border-b border-muted">
                                                        <span className="text-sm font-medium text-muted-foreground">VAT ({formatPercent(estimate.vat_percent).toFixed(2)}%)</span>
                                                        <span className="font-mono">
                                                            {detailedOverview.vatAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                                        </span>
                                                    </div>
                                                    <div className="flex justify-between items-center py-4 px-4 bg-primary text-primary-foreground rounded-lg mt-4 shadow-lg">
                                                        <span className="text-lg font-bold">Grand Total</span>
                                                        <div className="text-right">
                                                            <div className="text-2xl font-black font-mono">
                                                                AED {detailedOverview.grandTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </>
                                            )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )
                }

                {/* Overview Tab */}
                {
                    openModal === 'overview' && estimate && detailedOverview && (
                        <div className="bg-white border rounded shadow-sm overflow-hidden pt-1">
                            <div className="border-b bg-slate-50/80 px-6 py-5">
                                <div className="flex flex-wrap items-start justify-between gap-3">
                                    <div>
                                        <div className="text-lg font-bold text-slate-900">Detailed Overview</div>
                                        <div className="text-sm text-slate-500">Audit-style estimate breakdown with every available metric, driver, and final commercial reconciliation.</div>
                                    </div>
                                    <div className="rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                                        {estimateNumber}
                                    </div>
                                </div>
                                <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-6">
                                    {overviewCardMetrics.map((metric) => (
                                        <div key={metric.label} className="rounded-lg border bg-white px-3 py-3 shadow-sm">
                                            <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">{metric.label}</div>
                                            <div className={`mt-2 text-lg font-semibold tabular-nums ${metric.accent}`}>{metric.value}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="space-y-8 bg-muted/5 p-6">
                                <section className="overflow-hidden rounded-xl border bg-white shadow-sm">
                                    <div className="border-b bg-muted/40 px-5 py-3">
                                        <div className="text-sm font-semibold text-slate-900">Project Totals</div>
                                        <div className="text-xs text-slate-500">Rolled-up cost composition, project fixed-cost audit, and primary commercial outputs.</div>
                                    </div>
                                    <div className="grid gap-6 p-5 xl:grid-cols-2">
                                        <div className="rounded-lg border border-slate-200">
                                            <div className="border-b bg-slate-50 px-4 py-3 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">Cost Composition</div>
                                            <div className="divide-y">
                                                {[
                                                    { label: 'Tooling', amount: detailedOverview.tooling },
                                                    { label: 'Production', amount: detailedOverview.production },
                                                    { label: 'BOM', amount: detailedOverview.bom },
                                                    { label: 'Installation', amount: detailedOverview.installation },
                                                    { label: 'Packing', amount: detailedOverview.packing },
                                                    { label: 'Freight', amount: detailedOverview.freight },
                                                    { label: 'Engineering / Project', amount: detailedOverview.engg },
                                                    { label: 'Administrative', amount: detailedOverview.admin },
                                                    { label: 'Overhead', amount: detailedOverview.overhead },
                                                    { label: 'Cost Subtotal', amount: detailedOverview.costSubtotal, accent: 'text-primary font-semibold' },
                                                ].map((row) => (
                                                    <div key={row.label} className="flex items-center justify-between gap-4 px-4 py-3 text-sm">
                                                        <div>
                                                            <div className="font-medium text-slate-900">{row.label}</div>
                                                            <div className="text-xs text-slate-500">{detailedOverview.shareOfCost(row.amount).toFixed(1)}% of cost subtotal</div>
                                                        </div>
                                                        <div className={`text-right font-mono tabular-nums text-slate-900 ${row.accent || ''}`}>
                                                            {formatCurrency(row.amount)} AED
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="space-y-4">
                                            <div className="rounded-lg border border-slate-200">
                                                <div className="border-b bg-slate-50 px-4 py-3 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">Project Fixed Costs</div>
                                                <div className="divide-y">
                                                    {detailedOverview.projectFixedComponents.map((item) => (
                                                        <div key={item.label} className="flex items-center justify-between gap-4 px-4 py-3 text-sm">
                                                            <span className="font-medium text-slate-900">{item.label}</span>
                                                            <span className="font-mono tabular-nums text-slate-900">{formatCurrency(item.value)} AED</span>
                                                        </div>
                                                    ))}
                                                    <div className="flex items-center justify-between gap-4 px-4 py-3 text-sm font-semibold text-primary">
                                                        <span>Project Fixed Total</span>
                                                        <span className="font-mono tabular-nums">{formatCurrency(detailedOverview.projectFixedTotal)} AED</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="grid gap-4 md:grid-cols-2">
                                                <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-4">
                                                    <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">Production Snapshot</div>
                                                    <div className="mt-3 space-y-2 text-sm">
                                                        <div className="flex items-center justify-between gap-3">
                                                            <span className="text-slate-600">Labour cost inside production</span>
                                                            <span className="font-mono tabular-nums text-slate-900">{formatCurrency(detailedOverview.labourCost)} AED</span>
                                                        </div>
                                                        <div className="flex items-center justify-between gap-3">
                                                            <span className="text-slate-600">Design premium amount</span>
                                                            <span className="font-mono tabular-nums text-slate-900">{formatCurrency(detailedOverview.designPremiumAmount)} AED</span>
                                                        </div>
                                                        <div className="flex items-center justify-between gap-3">
                                                            <span className="text-slate-600">Average rounded price / SQM</span>
                                                            <span className="font-mono tabular-nums text-slate-900">{formatCurrency(detailedOverview.averageRoundedPerSqm)} AED</span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-4">
                                                    <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">Commercial Outputs</div>
                                                    <div className="mt-3 space-y-2 text-sm">
                                                        <div className="flex items-center justify-between gap-3">
                                                            <span className="text-slate-600">Amount after discount</span>
                                                            <span className="font-mono tabular-nums text-slate-900">{formatCurrency(detailedOverview.amountAfterDiscount)} AED</span>
                                                        </div>
                                                        <div className="flex items-center justify-between gap-3">
                                                            <span className="text-slate-600">Grand total</span>
                                                            <span className="font-mono tabular-nums text-blue-700">{formatCurrency(detailedOverview.grandTotal)} AED</span>
                                                        </div>
                                                        <div className="flex items-center justify-between gap-3">
                                                            <span className="text-slate-600">Net profit</span>
                                                            <span className={`font-mono tabular-nums ${detailedOverview.netProfit >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{formatCurrency(detailedOverview.netProfit)} AED</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </section>
                                <section className="overflow-hidden rounded-xl border bg-white shadow-sm">
                                    <div className="border-b bg-muted/40 px-5 py-3">
                                        <div className="text-sm font-semibold text-slate-900">Commercial Drivers</div>
                                        <div className="text-xs text-slate-500">Saved pricing, labour, logistics, overhead, project-management, and quotation inputs that drive the estimate math.</div>
                                    </div>
                                    <div className="grid gap-4 p-5 xl:grid-cols-2 2xl:grid-cols-4">
                                        <div className="rounded-lg border border-slate-200">
                                            <div className="border-b bg-slate-50 px-4 py-3 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">Pricing Controls</div>
                                            <div className="divide-y">
                                                {[
                                                    { label: 'Profit Margin', value: `${formatPercent(estimate.profit_margin || 0).toFixed(2)}%` },
                                                    { label: 'Discount', value: `${formatPercent(estimate.discount_percent || 0).toFixed(2)}%` },
                                                    { label: 'VAT Rate', value: `${formatPercent(estimate.vat_percent || 0).toFixed(2)}%` },
                                                    { label: 'Average Base / SQM', value: `${formatCurrency(detailedOverview.averageBasePerSqm)} AED` },
                                                    { label: 'Average Sell / SQM', value: `${formatCurrency(detailedOverview.averageSellingPerSqm)} AED` },
                                                ].map((item) => (
                                                    <div key={item.label} className="flex items-center justify-between gap-4 px-4 py-3 text-sm">
                                                        <span className="font-medium text-slate-900">{item.label}</span>
                                                        <span className="font-mono tabular-nums text-slate-900">{item.value}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="rounded-lg border border-slate-200">
                                            <div className="border-b bg-slate-50 px-4 py-3 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">Labour & Logistics Drivers</div>
                                            <div className="divide-y">
                                                {[
                                                    { label: 'Labour Rate / Hour', value: `${formatCurrency(estimate.per_hour_labour_rate || 0)} AED` },
                                                    { label: 'Skilled Labour Split', value: `${Number(estimate.skilled_manhours_perc || 0).toFixed(2)}%` },
                                                    { label: 'Semi-skilled Labour Split', value: `${Number(estimate.unskilled_manhours_perc || 0).toFixed(2)}%` },
                                                    { label: 'Non-working Hours / Week', value: `${Number((estimate as any).non_working_hours_per_week || 0).toFixed(2)}` },
                                                    { label: 'Packing & Loading', value: `${formatPercent(estimate.logistics_packing_loading_perc || 0).toFixed(2)}%` },
                                                    { label: 'Freight Cost', value: `${formatPercent(estimate.logistics_freight_cost_perc || 0).toFixed(2)}%` },
                                                ].map((item) => (
                                                    <div key={item.label} className="flex items-center justify-between gap-4 px-4 py-3 text-sm">
                                                        <span className="font-medium text-slate-900">{item.label}</span>
                                                        <span className="font-mono tabular-nums text-slate-900">{item.value}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="rounded-lg border border-slate-200">
                                            <div className="border-b bg-slate-50 px-4 py-3 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">Admin & Overheads</div>
                                            <div className="divide-y">
                                                {[
                                                    { label: 'Contingency', value: `${formatPercent((estimate as any).admin_contigency_perc || 0).toFixed(2)}%` },
                                                    { label: 'Warranty', value: `${formatPercent((estimate as any).admin_warranty_perc || 0).toFixed(2)}%` },
                                                    { label: 'Retention', value: `${formatPercent((estimate as any).admin_retention_perc || 0).toFixed(2)}%` },
                                                    { label: 'Banking', value: `${formatPercent((estimate as any).admin_banking_finance_perc || 0).toFixed(2)}%` },
                                                    { label: 'Other Costs', value: `${formatPercent((estimate as any).admin_other_cost_perc || 0).toFixed(2)}%` },
                                                    { label: 'OH / SQM', value: `${formatCurrency(estimate.oh_per_sqm || 0)} AED` },
                                                    { label: 'OH Sheet Reference', value: ohSheetRatePerSqm ? `${formatCurrency(ohSheetRatePerSqm)} AED` : 'Unavailable' },
                                                ].map((item) => (
                                                    <div key={item.label} className="flex items-center justify-between gap-4 px-4 py-3 text-sm">
                                                        <span className="font-medium text-slate-900">{item.label}</span>
                                                        <span className="font-mono tabular-nums text-slate-900">{item.value}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="rounded-lg border border-slate-200">
                                            <div className="border-b bg-slate-50 px-4 py-3 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">Project Setup & Quotation</div>
                                            <div className="divide-y">
                                                {[
                                                    { label: 'Design & Engineering', value: `${formatCurrency(estimate.pm_design_cost || 0)} AED` },
                                                    { label: 'Project Manager Cost', value: `${formatCurrency(estimate.pm_project_manager_cost || 0)} AED` },
                                                    { label: 'Site Intervention', value: `${formatCurrency(estimate.pm_site_intervention_cost || 0)} AED` },
                                                    { label: 'Documentation / Drawings', value: `${formatCurrency(estimate.pm_documentation_cost || 0)} AED` },
                                                    { label: 'Travel Expenses', value: `${formatCurrency(estimate.pm_travel_cost || 0)} AED` },
                                                    { label: 'Attention', value: estimate.attention?.trim() || 'Not set' },
                                                    { label: 'Validity', value: estimate.validity?.trim() || 'Not set' },
                                                    { label: 'Annexure A', value: estimate.annexure_a?.trim() ? 'Configured' : 'Not set' },
                                                    { label: 'Annexure B', value: estimate.annexure_b?.trim() ? 'Configured' : 'Not set' },
                                                ].map((item) => (
                                                    <div key={item.label} className="flex items-center justify-between gap-4 px-4 py-3 text-sm">
                                                        <span className="font-medium text-slate-900">{item.label}</span>
                                                        <span className="max-w-[55%] text-right font-mono tabular-nums text-slate-900">{item.value}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </section>
                                <section className="overflow-hidden rounded-xl border bg-white shadow-sm">
                                    <div className="border-b bg-muted/40 px-5 py-3">
                                        <div className="text-sm font-semibold text-slate-900">Per-Part Calculation Matrix</div>
                                        <div className="text-xs text-slate-500">Full calculation chain for each part, including direct cost, other cost buckets, design premium, revised base, selling, and round-off.</div>
                                    </div>
                                    <div className="overflow-x-auto">
                                        <table className="w-max min-w-full table-fixed border-collapse text-[10px] text-left leading-tight">
                                            <colgroup>
                                                <col className="w-[170px]" />
                                                <col className="w-[72px]" />
                                                <col className="w-[56px]" />
                                                <col className="w-[54px]" />
                                                <col className="w-[66px]" />
                                                <col className="w-[74px]" />
                                                <col className="w-[74px]" />
                                                <col className="w-[60px]" />
                                                <col className="w-[60px]" />
                                                <col className="w-[58px]" />
                                                <col className="w-[58px]" />
                                                <col className="w-[68px]" />
                                                <col className="w-[58px]" />
                                                <col className="w-[58px]" />
                                                <col className="w-[58px]" />
                                                <col className="w-[58px]" />
                                                <col className="w-[58px]" />
                                                <col className="w-[74px]" />
                                                <col className="w-[74px]" />
                                                <col className="w-[56px]" />
                                                <col className="w-[74px]" />
                                                <col className="w-[74px]" />
                                                <col className="w-[74px]" />
                                                <col className="w-[78px]" />
                                                <col className="w-[78px]" />
                                            </colgroup>
                                            <thead>
                                                <tr className="bg-muted/50 text-muted-foreground border-b uppercase tracking-[0.08em] font-semibold text-[9px]">
                                                    <th className="px-2 py-2 border-r text-center whitespace-nowrap">Part</th>
                                                    <th className="px-1.5 py-2 border-r text-center whitespace-nowrap">Material</th>
                                                    <th className="px-1.5 py-2 border-r text-center whitespace-nowrap">UOM</th>
                                                    <th className="px-1.5 py-2 border-r text-center whitespace-nowrap">Qty</th>
                                                    <th className="px-1.5 py-2 border-r text-center whitespace-nowrap">Area</th>
                                                    <th className="px-1.5 py-2 border-r text-center whitespace-nowrap">Total Area</th>
                                                    <th className="px-1.5 py-2 border-r text-center whitespace-nowrap">Weight</th>
                                                    <th className="px-1 py-2 border-r text-center whitespace-nowrap">Pattern</th>
                                                    <th className="px-1 py-2 border-r text-center whitespace-nowrap">Prod.</th>
                                                    <th className="px-1 py-2 border-r text-center whitespace-nowrap">BOM</th>
                                                    <th className="px-1 py-2 border-r text-center whitespace-nowrap">Install</th>
                                                    <th className="px-1 py-2 border-r bg-primary/5 text-center font-bold text-primary whitespace-nowrap">Direct</th>
                                                    <th className="px-1 py-2 border-r text-center whitespace-nowrap">Packing</th>
                                                    <th className="px-1 py-2 border-r text-center whitespace-nowrap">Freight</th>
                                                    <th className="px-1 py-2 border-r text-center whitespace-nowrap">Proj</th>
                                                    <th className="px-1 py-2 border-r text-center whitespace-nowrap">Admin</th>
                                                    <th className="px-1 py-2 border-r text-center whitespace-nowrap">OH</th>
                                                    <th className="px-1.5 py-2 border-r text-center whitespace-nowrap">Total Cost</th>
                                                    <th className="px-1.5 py-2 border-r text-center whitespace-nowrap">Base/SQM</th>
                                                    <th className="px-1.5 py-2 border-r text-center whitespace-nowrap">DP %</th>
                                                    <th className="px-1.5 py-2 border-r text-center whitespace-nowrap">DP Amt</th>
                                                    <th className="px-1.5 py-2 border-r text-center whitespace-nowrap">Rev Base</th>
                                                    <th className="px-1.5 py-2 border-r text-center whitespace-nowrap">Sell/Unit</th>
                                                    <th className="px-1.5 py-2 border-r text-center whitespace-nowrap">Sell Total</th>
                                                    <th className="px-1.5 py-2 text-center whitespace-nowrap">Round Off</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y">
                                                {costRows.map((row) => {
                                                    const directCost = row.patternMould + row.production + row.bomCost + row.installation;
                                                    return (
                                                        <tr key={row.part.estimate_part_id} className="hover:bg-muted/30 transition-colors text-[10px]">
                                                            <td className="px-2 py-1.5 border-r font-medium">
                                                                <div className="truncate whitespace-nowrap">{row.part.part_name}</div>
                                                            </td>
                                                            <td className="px-1.5 py-1.5 border-r bg-muted/10 font-mono text-muted-foreground whitespace-nowrap">{row.materialType}</td>
                                                            <td className="px-1.5 py-1.5 border-r text-muted-foreground whitespace-nowrap">{row.unit}</td>
                                                            <td className="px-1.5 py-1.5 border-r text-right tabular-nums whitespace-nowrap">{formatCompactNumber(row.quantity, true)}</td>
                                                            <td className="px-1.5 py-1.5 border-r text-right tabular-nums whitespace-nowrap">{formatCompactNumber(row.part.area_m2)}</td>
                                                            <td className="px-1.5 py-1.5 border-r text-right tabular-nums whitespace-nowrap">{formatCompactNumber(row.areaTotal)}</td>
                                                            <td className="px-1.5 py-1.5 border-r text-right tabular-nums whitespace-nowrap">{formatCompactNumber(row.totalWeight)}</td>
                                                            <td className="px-1 py-1.5 border-r text-right tabular-nums whitespace-nowrap">{formatCompactNumber(row.patternMould)}</td>
                                                            <td className="px-1 py-1.5 border-r text-right tabular-nums whitespace-nowrap">{formatCompactNumber(row.production)}</td>
                                                            <td className="px-1 py-1.5 border-r text-right tabular-nums whitespace-nowrap">{formatCompactNumber(row.bomCost)}</td>
                                                            <td className="px-1 py-1.5 border-r text-right tabular-nums whitespace-nowrap">{formatCompactNumber(row.installation)}</td>
                                                            <td className="px-1 py-1.5 border-r bg-primary/5 text-right font-bold tabular-nums text-primary whitespace-nowrap">{formatCompactNumber(directCost)}</td>
                                                            <td className="px-1 py-1.5 border-r text-right tabular-nums whitespace-nowrap">{formatCompactNumber(row.packing)}</td>
                                                            <td className="px-1 py-1.5 border-r text-right tabular-nums whitespace-nowrap">{formatCompactNumber(row.freight)}</td>
                                                            <td className="px-1 py-1.5 border-r text-right tabular-nums whitespace-nowrap">{formatCompactNumber(row.project)}</td>
                                                            <td className="px-1 py-1.5 border-r text-right tabular-nums whitespace-nowrap">{formatCompactNumber(row.admin)}</td>
                                                            <td className="px-1 py-1.5 border-r text-right tabular-nums whitespace-nowrap">{formatCompactNumber(row.overhead)}</td>
                                                            <td className="px-1.5 py-1.5 border-r text-right font-bold tabular-nums text-primary whitespace-nowrap">{formatCompactNumber(row.totalCost)}</td>
                                                            <td className="px-1.5 py-1.5 border-r text-right tabular-nums whitespace-nowrap">{formatCompactNumber(row.basePerSqm)}</td>
                                                            <td className="px-1.5 py-1.5 border-r text-right tabular-nums whitespace-nowrap">{formatPercent(row.designPremiumPerc).toFixed(2)}%</td>
                                                            <td className="px-1.5 py-1.5 border-r text-right tabular-nums whitespace-nowrap">{formatCompactNumber(row.designPremiumAmount)}</td>
                                                            <td className="px-1.5 py-1.5 border-r text-right tabular-nums whitespace-nowrap">{formatCompactNumber(row.revisedPerUnit)}</td>
                                                            <td className="px-1.5 py-1.5 border-r text-right tabular-nums whitespace-nowrap">{formatCompactNumber(row.sellingPerUnit, true)}</td>
                                                            <td className="px-1.5 py-1.5 border-r text-right tabular-nums whitespace-nowrap">{formatCompactNumber(row.sellingPriceTotal, true)}</td>
                                                            <td className="px-1.5 py-1.5 text-right font-bold tabular-nums text-green-700 whitespace-nowrap">{formatCompactNumber(row.roundOff, true)}</td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                            <tfoot className="bg-muted/50 font-bold border-t-2 text-[10px]">
                                                <tr>
                                                    <td className="px-2 py-2 border-r uppercase tracking-[0.08em] text-muted-foreground" colSpan={3}>Grand Totals</td>
                                                    <td className="px-1.5 py-2 border-r text-right tabular-nums whitespace-nowrap">{formatCompactNumber(detailedOverview.totalQuantity, true)}</td>
                                                    <td className="px-1.5 py-2 border-r" />
                                                    <td className="px-1.5 py-2 border-r text-right tabular-nums whitespace-nowrap">{formatCompactNumber(detailedOverview.totalArea)}</td>
                                                    <td className="px-1.5 py-2 border-r text-right tabular-nums whitespace-nowrap">{formatCompactNumber(detailedOverview.totalWeight)}</td>
                                                    <td className="px-1 py-2 border-r text-right tabular-nums whitespace-nowrap">{formatCompactNumber(totals.patternMould)}</td>
                                                    <td className="px-1 py-2 border-r text-right tabular-nums whitespace-nowrap">{formatCompactNumber(totals.production)}</td>
                                                    <td className="px-1 py-2 border-r text-right tabular-nums whitespace-nowrap">{formatCompactNumber(totals.bomCost)}</td>
                                                    <td className="px-1 py-2 border-r text-right tabular-nums whitespace-nowrap">{formatCompactNumber(totals.install)}</td>
                                                    <td className="px-1 py-2 border-r bg-primary/10 text-right font-bold tabular-nums text-primary whitespace-nowrap">{formatCompactNumber(detailedOverview.directTotal)}</td>
                                                    <td className="px-1 py-2 border-r text-right tabular-nums whitespace-nowrap">{formatCompactNumber(totals.packing)}</td>
                                                    <td className="px-1 py-2 border-r text-right tabular-nums whitespace-nowrap">{formatCompactNumber(totals.freight)}</td>
                                                    <td className="px-1 py-2 border-r text-right tabular-nums whitespace-nowrap">{formatCompactNumber(totals.project)}</td>
                                                    <td className="px-1 py-2 border-r text-right tabular-nums whitespace-nowrap">{formatCompactNumber(totals.admin)}</td>
                                                    <td className="px-1 py-2 border-r text-right tabular-nums whitespace-nowrap">{formatCompactNumber(totals.overhead)}</td>
                                                    <td className="px-1.5 py-2 border-r text-right font-bold tabular-nums text-primary whitespace-nowrap">{formatCompactNumber(detailedOverview.costSubtotal)}</td>
                                                    <td className="px-1.5 py-2 border-r text-right tabular-nums whitespace-nowrap">{formatCompactNumber(detailedOverview.averageBasePerSqm)}</td>
                                                    <td className="px-1.5 py-2 border-r text-right tabular-nums whitespace-nowrap">{formatPercent(detailedOverview.averageDesignPremiumPercent).toFixed(2)}%</td>
                                                    <td className="px-1.5 py-2 border-r text-right tabular-nums whitespace-nowrap">{formatCompactNumber(detailedOverview.designPremiumAmount)}</td>
                                                    <td className="px-1.5 py-2 border-r text-right tabular-nums whitespace-nowrap">{formatCompactNumber(detailedOverview.averageRevisedBasePerSqm)}</td>
                                                    <td className="px-1.5 py-2 border-r text-right tabular-nums whitespace-nowrap">{formatCompactNumber(detailedOverview.averageSellingPerUnit, true)}</td>
                                                    <td className="px-1.5 py-2 border-r text-right tabular-nums whitespace-nowrap">{formatCompactNumber(totals.selling, true)}</td>
                                                    <td className="px-1.5 py-2 text-right font-bold tabular-nums text-green-700 whitespace-nowrap">{formatCompactNumber(detailedOverview.roundedSellingTotal, true)}</td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>
                                </section>
                                <section className="overflow-hidden rounded-xl border bg-white shadow-sm">
                                    <div className="border-b bg-muted/40 px-5 py-3">
                                        <div className="text-sm font-semibold text-slate-900">Final Commercial Rollup</div>
                                        <div className="text-xs text-slate-500">Quotation-facing reconciliation from rounded selling subtotal through discount, VAT, final total, and profitability.</div>
                                    </div>
                                    <div className="grid gap-6 p-5 xl:grid-cols-2">
                                        <div className="rounded-lg border border-slate-200">
                                            <div className="border-b bg-slate-50 px-4 py-3 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">Commercial Reconciliation</div>
                                            <div className="divide-y">
                                                {[
                                                    { label: 'Subtotal (Rounded)', value: detailedOverview.roundedSellingTotal, accent: 'text-slate-900' },
                                                    { label: `Discount (${formatPercent(estimate.discount_percent || 0).toFixed(2)}%)`, value: detailedOverview.discountAmount, accent: 'text-rose-700', prefix: '-' },
                                                    { label: 'Amount After Discount', value: detailedOverview.amountAfterDiscount, accent: 'text-slate-900' },
                                                    { label: `VAT (${formatPercent(estimate.vat_percent || 0).toFixed(2)}%)`, value: detailedOverview.vatAmount, accent: 'text-slate-900' },
                                                    { label: 'Grand Total', value: detailedOverview.grandTotal, accent: 'text-blue-700 font-semibold' },
                                                    { label: 'Total Selling Before Tax', value: detailedOverview.amountAfterDiscount, accent: 'text-slate-900' },
                                                    { label: 'Net Profit', value: detailedOverview.netProfit, accent: detailedOverview.netProfit >= 0 ? 'text-emerald-700 font-semibold' : 'text-rose-700 font-semibold' },
                                                ].map((row) => (
                                                    <div key={row.label} className="flex items-center justify-between gap-4 px-4 py-3 text-sm">
                                                        <span className="font-medium text-slate-900">{row.label}</span>
                                                        <span className={`font-mono tabular-nums ${row.accent}`}>{row.prefix || ''}{formatCurrency(row.value)} AED</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="space-y-4">
                                            <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-4">
                                                <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">Profitability Checkpoint</div>
                                                <div className="mt-3 grid gap-3 md:grid-cols-2">
                                                    <div className="rounded-lg border bg-white px-3 py-3">
                                                        <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">Cost Subtotal</div>
                                                        <div className="mt-2 font-mono text-lg font-semibold text-slate-900">{formatCurrency(detailedOverview.costSubtotal)} AED</div>
                                                    </div>
                                                    <div className="rounded-lg border bg-white px-3 py-3">
                                                        <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">Net Margin on Cost</div>
                                                        <div className={`mt-2 font-mono text-lg font-semibold ${detailedOverview.costSubtotal > 0 && detailedOverview.netProfit >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                                                            {detailedOverview.costSubtotal > 0 ? ((detailedOverview.netProfit / detailedOverview.costSubtotal) * 100).toFixed(2) : '0.00'}%
                                                        </div>
                                                    </div>
                                                    <div className="rounded-lg border bg-white px-3 py-3">
                                                        <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">Avg Rounded / SQM</div>
                                                        <div className="mt-2 font-mono text-lg font-semibold text-slate-900">{formatCurrency(detailedOverview.averageRoundedPerSqm)} AED</div>
                                                    </div>
                                                    <div className="rounded-lg border bg-white px-3 py-3">
                                                        <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">Avg Sell / SQM</div>
                                                        <div className="mt-2 font-mono text-lg font-semibold text-blue-700">{formatCurrency(detailedOverview.averageSellingPerSqm)} AED</div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-600">
                                                The detailed overview uses saved estimate totals for after-discount, pre-VAT selling and grand total, then reconciles the pre-discount rounded selling total from the part-level round-off values so the discount and VAT lines stay explicit.
                                            </div>
                                        </div>
                                    </div>
                                </section>
                            </div>
                        </div>
                    )
                }

                {/* Parts Tab */}
                {
                    openModal === 'parts' && (
                        <div className="bg-white border rounded shadow-sm overflow-hidden pt-1">
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm text-left border-collapse">
                                    <thead>
                                        <tr className="bg-muted/50 text-muted-foreground border-b uppercase tracking-wider font-semibold text-xs">
                                            <th className="px-4 py-3">Part #</th>
                                            <th className="px-4 py-3">Material</th>
                                            <th className="px-4 py-3">Part Name</th>
                                            <th className="px-4 py-3">Dimension</th>
                                            <th className="px-4 py-3 text-right">Quantity</th>
                                            <th className="px-4 py-3 text-center">Unit</th>
                                            <th className="px-4 py-3 text-right">Area (m2)</th>
                                            <th className="px-4 py-3 text-right">Total Area</th>
                                            <th className="px-4 py-3 text-right">Thickness</th>
                                            <th className="px-4 py-3 text-right">Weight (Kg)</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-muted/50">
                                        {estimate.parts.map((part, idx) => (
                                            <tr key={part.estimate_part_id} className="hover:bg-muted/30 transition-colors">
                                                <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{`HC/${estimate.estimate_id}/039${idx + 1}`}</td>
                                                <td className="px-4 py-3"><span className="bg-muted text-muted-foreground px-1.5 py-0.5 rounded text-[10px] font-bold">GRP</span></td>
                                                <td className="px-4 py-3 font-medium">{part.part_name}</td>
                                                <td className="px-4 py-3 text-muted-foreground">{part.dimension || '-'}</td>
                                                <td className="px-4 py-3 text-right tabular-nums">{part.quantity}</td>
                                                <td className="px-4 py-3 text-center text-muted-foreground">Nos.</td>
                                                <td className="px-4 py-3 text-right tabular-nums">{part.area_m2.toFixed(2)}</td>
                                                <td className="px-4 py-3 text-right tabular-nums">{(part.area_m2 * part.quantity).toFixed(2)}</td>
                                                <td className="px-4 py-3 text-right tabular-nums">{part.thickness || 4} mm</td>
                                                <td className="px-4 py-3 text-right tabular-nums font-semibold">{(part.weight_kgs || 0).toFixed(2)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot className="bg-muted/50 font-bold border-t-2">
                                        <tr>
                                            <td colSpan={7} className="px-4 py-3 text-left uppercase tracking-wider text-muted-foreground text-[10px]">Grand Totals</td>
                                            <td className="px-4 py-3 text-right tabular-nums">{totals.areaTotal.toFixed(2)}</td>
                                            <td className="px-4 py-3"></td>
                                            <td className="px-4 py-3 text-right tabular-nums text-primary">{(totals.totalWeight || 0).toFixed(2)}</td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>
                    )
                }

                {/* Tooling Tab */}
                {
                    openModal === 'tooling' && (
                        <div className="bg-white border rounded shadow-sm overflow-hidden pt-1">
                            <div className="bg-muted/50 px-6 py-3 border-b flex items-center justify-between">
                                <div>
                                    <div className="font-bold text-sm text-gray-900">Tooling Inputs</div>
                                    <div className="text-xs text-gray-500">Mould Area / Qty / Cost per m2 are legacy inputs.</div>
                                    <div className="mt-2 flex flex-wrap gap-3 text-xs">
                                        <label className="flex items-center gap-2">
                                            <span className="text-muted-foreground font-bold uppercase tracking-wider">Tooling Consumables %</span>
                                            <input
                                                type="number"
                                                className="w-20 border rounded px-2 py-1 text-right tabular-nums disabled:opacity-60"
                                                value={((((estimate as any).tooling_consumable_perc ?? 0.01) * 100)).toFixed(2)}
                                                onChange={(e) => setEstimate({ ...estimate, tooling_consumable_perc: (parseFloat(e.target.value) || 0) / 100 })}
                                                disabled={!canEdit}
                                            />
                                        </label>
                                        <label className="flex items-center gap-2">
                                            <span className="text-muted-foreground font-bold uppercase tracking-wider">Jigs & Fixtures %</span>
                                            <input
                                                type="number"
                                                className="w-20 border rounded px-2 py-1 text-right tabular-nums disabled:opacity-60"
                                                value={((((estimate as any).tooling_jigs_fixtures_perc ?? 0.01) * 100)).toFixed(2)}
                                                onChange={(e) => setEstimate({ ...estimate, tooling_jigs_fixtures_perc: (parseFloat(e.target.value) || 0) / 100 })}
                                                disabled={!canEdit}
                                            />
                                        </label>
                                        <label className="flex items-center gap-2">
                                            <span className="text-muted-foreground font-bold uppercase tracking-wider">Misc %</span>
                                            <input
                                                type="number"
                                                className="w-20 border rounded px-2 py-1 text-right tabular-nums disabled:opacity-60"
                                                value={((((estimate as any).tooling_misc_perc ?? 0.01) * 100)).toFixed(2)}
                                                onChange={(e) => setEstimate({ ...estimate, tooling_misc_perc: (parseFloat(e.target.value) || 0) / 100 })}
                                                disabled={!canEdit}
                                            />
                                        </label>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={() => handleMarkSectionReviewed('tooling')}
                                        disabled={!canEdit || busy}
                                        className="border border-emerald-300 bg-emerald-50 px-4 py-2 rounded-md font-bold text-[11px] uppercase tracking-widest text-emerald-800 hover:bg-emerald-100 disabled:opacity-50"
                                    >
                                        Mark Reviewed
                                    </button>
                                    <button
                                        onClick={() => handleInputsSave()}
                                        disabled={!canEdit || savingInputs}
                                        className="bg-primary text-primary-foreground px-4 py-2 rounded-md font-bold text-xs uppercase tracking-widest hover:bg-primary/90 transition-all shadow-md disabled:opacity-50"
                                    >
                                        {savingInputs ? 'SAVING...' : 'SAVE TOOLING'}
                                    </button>
                                </div>
                            </div>

                            {!draftParts ? (
                                <div className="p-6 text-sm text-gray-500">Loading inputs...</div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm text-left border-collapse">
                                        <thead>
                                            <tr className="bg-muted/50 text-muted-foreground border-b uppercase tracking-wider font-semibold text-xs">
                                                <th className="px-4 py-3">Part #</th>
                                                <th className="px-4 py-3">Material</th>
                                                <th className="px-4 py-3">Part Name</th>
                                                <th className="px-4 py-3 text-right">Quantity</th>
                                                <th className="px-4 py-3 text-right">Total Area</th>
                                                <th className="px-4 py-3 text-left">Mould Type</th>
                                                <th className="px-4 py-3 text-right">Mould Area</th>
                                                <th className="px-4 py-3 text-right">Mould Qty</th>
                                                <th className="px-4 py-3 text-right">Cost / m2</th>
                                                <th className="px-4 py-3 text-right">Total Cost</th>
                                                <th className="px-4 py-3 text-right">Mold Cost/Part</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-muted/50">
                                            {draftParts.map((part: any, idx: number) => {
                                                const mouldQty = parseFloat(part.mould_quantity || 1);
                                                const partQuantity = parseFloat(part.quantity || 0) || 0;
                                                const totalArea = (parseFloat(part.area_m2 || 0) * partQuantity) || 0;
                                                const netMouldCost = (parseFloat(part.mould_area || 0) * parseFloat(part.mould_cost_m2 || 0) * mouldQty) || 0;
                                                const costPerM2Part = partQuantity > 0 ? (netMouldCost / partQuantity) : 0;
                                                const material = estimate.project_type_id === 2 ? 'GRP' : 'GRC';

                                                return (
                                                    <tr key={part.estimate_part_id ?? `tool-${idx}`} className="hover:bg-muted/30 transition-colors">
                                                        <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{`HC/${estimate.estimate_id}/039${idx + 1}`}</td>
                                                        <td className="px-4 py-3 font-mono text-xs">{material}</td>
                                                        <td className="px-4 py-3 font-medium">{part.part_name}</td>
                                                        <td className="px-4 py-3 text-right tabular-nums">{part.quantity}</td>
                                                        <td className="px-4 py-3 text-right tabular-nums">{totalArea.toFixed(2)}</td>
                                                        <td className="px-4 py-3">
                                                            <select
                                                                className="w-32 border rounded px-2 py-1 text-sm"
                                                                value={part.mould_type_id ?? ''}
                                                                onChange={(e) => {
                                                                    const value = parseInt(e.target.value || '0', 10);
                                                                    const cost = getMouldTypeCost(value);
                                                                    setDraftParts(prev => prev!.map((row: any, i: number) => i === idx ? ({
                                                                        ...row,
                                                                        mould_type_id: value || undefined,
                                                                        mould_cost_m2: cost || row.mould_cost_m2
                                                                    }) : row));
                                                                }}
                                                                disabled={!canEdit}
                                                            >
                                                                <option value="">Select type</option>
                                                                {mouldTypes.map(mt => (
                                                                    <option key={mt.mould_type_id} value={mt.mould_type_id}>
                                                                        {mt.mould_type} ({mt.mould_cost.toFixed(0)})
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <input
                                                                type="number"
                                                                className="w-28 ml-auto block border-muted rounded py-1.5 px-2 text-right tabular-nums focus:ring-primary focus:border-primary disabled:opacity-60"
                                                                value={part.mould_area ?? 0}
                                                                onChange={(e) => setDraftParts(prev => prev!.map((row: any, i: number) => i === idx ? ({ ...row, mould_area: e.target.value }) : row))}
                                                                disabled={!canEdit}
                                                            />
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <input
                                                                type="number"
                                                                className="w-20 ml-auto block border-muted rounded py-1.5 px-2 text-right tabular-nums focus:ring-primary focus:border-primary disabled:opacity-60"
                                                                value={part.mould_quantity ?? 1}
                                                                onChange={(e) => setDraftParts(prev => prev!.map((row: any, i: number) => i === idx ? ({ ...row, mould_quantity: e.target.value }) : row))}
                                                                disabled={!canEdit}
                                                            />
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <input
                                                                type="number"
                                                                className="w-28 ml-auto block border-muted rounded py-1.5 px-2 text-right tabular-nums focus:ring-primary focus:border-primary disabled:opacity-60"
                                                                value={part.mould_cost_m2 ?? 0}
                                                                onChange={(e) => setDraftParts(prev => prev!.map((row: any, i: number) => i === idx ? ({ ...row, mould_cost_m2: e.target.value }) : row))}
                                                                disabled={!canEdit}
                                                            />
                                                        </td>
                                                        <td className="px-4 py-3 text-right tabular-nums font-semibold">{netMouldCost.toFixed(2)}</td>
                                                        <td className="px-4 py-3 text-right tabular-nums bg-primary/5 font-bold text-primary">{costPerM2Part.toFixed(2)}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>

                                    <div className="flex justify-end p-6 bg-muted/10 border-t">
                                        {(() => {
                                            const totalMouldCost = draftParts.reduce((sum: number, p: any) => {
                                                const qty = parseFloat(p.mould_quantity || 1);
                                                return sum + (parseFloat(p.mould_area || 0) * parseFloat(p.mould_cost_m2 || 0) * qty);
                                            }, 0);
                                            const totalArea = draftParts.reduce((sum: number, p: any) => sum + (parseFloat(p.area_m2 || 0) * parseFloat(p.quantity || 0)), 0);
                                            const totalWeight = draftParts.reduce((sum: number, p: any) => sum + (recomputeLineWeight(p) || 0), 0);
                                            return (
                                                <div className="w-full max-w-xs space-y-2">
                                                    <div className="flex justify-between items-center py-1.5 border-b border-muted">
                                                        <span className="text-xs font-medium text-muted-foreground uppercase">Total Mould Cost</span>
                                                        <span className="font-mono font-bold text-lg">
                                                            {totalMouldCost.toLocaleString(undefined, { minimumFractionDigits: 2 })} <span className="text-[10px]">AED</span>
                                                        </span>
                                                    </div>
                                                    <div className="flex justify-between items-center py-1 border-b border-muted text-xs">
                                                        <span className="text-muted-foreground">Total Area</span>
                                                        <span className="font-mono">{totalArea.toFixed(2)} m2</span>
                                                    </div>
                                                    <div className="flex justify-between items-center py-1 border-b border-muted text-xs">
                                                        <span className="text-muted-foreground">Total Weight</span>
                                                        <span className="font-mono">{totalWeight.toFixed(2)} Kg</span>
                                                    </div>
                                                    <div className="flex gap-4 mt-4">
                                                        <div className="flex-1 flex justify-between items-center py-2 px-3 bg-primary text-primary-foreground rounded-md shadow-sm">
                                                            <span className="text-[10px] font-bold uppercase">Cost / M2</span>
                                                            <span className="font-mono font-bold">
                                                                {totalArea > 0 ? (totalMouldCost / totalArea).toFixed(2) : '0.00'}
                                                            </span>
                                                        </div>
                                                        <div className="flex-1 flex justify-between items-center py-2 px-3 bg-blue-600 text-white rounded-md shadow-sm">
                                                            <span className="text-[10px] font-bold uppercase">Cost / KG</span>
                                                            <span className="font-mono font-bold">
                                                                {totalWeight > 0 ? (totalMouldCost / totalWeight).toFixed(2) : '0.00'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                </div>
                            )}
                        </div>
                    )
                }

                {/* Production Cost Tab */}
                {
                    openModal === 'production' && (
                        <div className="bg-white border rounded shadow-sm overflow-hidden w-full pt-1">
                            <div className="border-b bg-muted/40 px-6 py-3 flex items-center justify-between gap-4">
                                <div>
                                    <div className="font-bold text-sm text-gray-900">Production Cost</div>
                                    <div className="text-xs text-gray-500">Classic aggregate production summary for all parts.</div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => handleMarkSectionReviewed('production')}
                                    disabled={!canEdit || busy}
                                    className="border border-emerald-300 bg-emerald-50 px-4 py-2 rounded-md font-bold text-[11px] uppercase tracking-widest text-emerald-800 hover:bg-emerald-100 disabled:opacity-50"
                                >
                                    Mark Reviewed
                                </button>
                            </div>

                            <div className="p-6 grid grid-cols-1 gap-6 xl:grid-cols-2 items-start">
                                {/* Legacy production tab doesn't edit design premium or productivity here */}
                                {/* Left Column: GRC */}
                                <div className="min-w-0 space-y-4">
                                    {/* Labour Cost / KG Input (Above GRC) */}
                                    <div className="bg-white border rounded shadow-sm p-4 flex items-center justify-between">
                                        <label className="text-sm font-bold text-muted-foreground uppercase tracking-wide">
                                            Labour Cost - AED / KG
                                        </label>
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="number"
                                                className="w-24 border rounded py-1 px-2 text-right font-mono font-bold text-lg tabular-nums focus:ring-2 focus:ring-primary outline-none"
                                                value={estimate.prod_aed_per_kg ?? 0}
                                                onChange={(e) => setEstimate({ ...estimate, prod_aed_per_kg: parseFloat(e.target.value) || 0 })}
                                                disabled={!canEdit}
                                            />
                                            <span className="text-xs font-bold text-muted-foreground">AED</span>
                                            {canEdit && (
                                                <button
                                                    onClick={handleSaveProductionSettings}
                                                    className="ml-2 bg-primary text-primary-foreground px-3 py-1 rounded text-xs font-bold uppercase tracking-wide hover:bg-primary/90 transition-colors"
                                                >
                                                    Save
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    {/* GRC Card */}
                                    <div className="border rounded shadow-sm bg-white flex flex-col overflow-hidden">
                                        <div className="bg-primary/5 px-4 py-3 border-b border-primary/10 flex justify-between items-center">
                                            <span className="font-bold text-sm tracking-tight text-primary">GRC (GLASS REINFORCED CONCRETE)</span>
                                            <div className="flex gap-2">
                                                <button onClick={() => setGrcPartMode((prev) => !prev)} className={`text-[10px] px-2 py-1 rounded transition-colors font-bold uppercase ${grcPartMode ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}>{grcPartMode ? 'Part Mode On' : 'Optional Part Mode'}</button>
                                                <button onClick={() => setShowDesignModal(true)} className="text-[10px] bg-primary/10 text-primary px-2 py-1 rounded hover:bg-primary/20 transition-colors font-bold uppercase">Design Mix</button>
                                                <button onClick={() => setShowLabourModal(true)} className="text-[10px] bg-primary/10 text-primary px-2 py-1 rounded hover:bg-primary/20 transition-colors font-bold uppercase">Labour rate</button>
                                            </div>
                                        </div>
                                        <div className="overflow-x-auto">
                                            <table className="w-full text-[11px] text-left border-collapse">
                                                <thead>
                                                    <tr className="bg-muted/50 text-muted-foreground border-b uppercase tracking-wider font-semibold text-[10px]">
                                                        <th className="px-3 py-2">Material</th>
                                                        <th className="px-3 py-2 text-right">AED Cost</th>
                                                        <th className="px-3 py-2 text-center">Unit</th>
                                                        <th className="px-3 py-2 text-right">Conv. Ratio</th>
                                                        <th className="px-3 py-2 text-right">Req (Kgs)</th>
                                                        <th className="px-3 py-2 text-right">Total Cost</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-muted/50">
                                                    {estimate.production_materials?.map((mat, i) => {
                                                        const matReq = totals.totalWeight * mat.conversion_ratio;
                                                        const matCost = matReq * mat.aed_cost;
                                                        return (
                                                            <tr key={i} className="hover:bg-muted/30 transition-colors">
                                                                <td className="px-3 py-2 font-medium">{mat.material_name}</td>
                                                                <td className="px-3 py-2 text-right tabular-nums">{mat.aed_cost.toFixed(2)}</td>
                                                                <td className="px-3 py-2 text-center text-muted-foreground">{mat.unit}</td>
                                                                <td className="px-3 py-2 text-right tabular-nums">{mat.conversion_ratio.toFixed(3)}</td>
                                                                <td className="px-3 py-2 text-right tabular-nums font-mono">{matReq.toFixed(2)}</td>
                                                                <td className="px-3 py-2 text-right tabular-nums font-bold text-primary">{matCost.toFixed(2)}</td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                                {/* Total Row for Materials Cost */}
                                                <tfoot className="bg-muted/20 font-semibold border-t">
                                                    <tr>
                                                        <td colSpan={5} className="px-3 py-2 text-right text-xs uppercase tracking-wider text-muted-foreground">Total Material Cost</td>
                                                        <td className="px-3 py-2 text-right tabular-nums font-bold text-primary">
                                                            {(estimate.production_materials?.reduce((sum, m) => sum + (totals.totalWeight * m.conversion_ratio * m.aed_cost), 0) || 0).toFixed(2)}
                                                        </td>
                                                    </tr>
                                                </tfoot>
                                            </table>
                                        </div>
                                        {(() => {
                                            const sumRatios = estimate.production_materials?.reduce((sum, m) => sum + (m.conversion_ratio || 0), 0) || 0;
                                            const totalMixWeight = totals.totalWeight * sumRatios;
                                            const grcParts = (estimate.parts || []).filter((part) => Number(part.material_type_id) === 1);
                                            const totalMaterialCost = grcParts.reduce((sum, part) => sum + ((part.grc_material_cost_unit || 0) * (part.quantity || 0)), 0);
                                            const labourRate = estimate.prod_aed_per_kg || 0;
                                            const totalLabourCost = grcParts.reduce((sum, part) => sum + ((part.grc_labour_cost_unit || 0) * (part.quantity || 0)), 0);
                                            const totalAuxiliary = grcParts.reduce((sum, part) => sum + (part.grc_auxiliary_allocation || 0), 0);
                                            const grcTotal = grcParts.reduce((sum, part) => sum + (part.grc_total_cost_all || 0), 0);
                                            const auxiliaryDisabled = estimate.apply_auxiliary_to_grc === false;

                                            return (
                                                <div className="mt-auto bg-muted/20 p-2 border-t space-y-3 text-xs">
                                                    <div className="grid grid-cols-3 gap-1">
                                                        <div className="bg-slate-200 p-1 flex items-center justify-center font-bold text-slate-700">Totals</div>
                                                        <div className="bg-slate-100 p-1 flex items-center justify-end font-mono tabular-nums">{totalMixWeight.toFixed(3)}</div>
                                                        <div className="bg-slate-100 p-1 flex items-center justify-end font-mono tabular-nums">{totalMaterialCost.toFixed(2)}</div>

                                                        <div className="bg-slate-200 p-1 flex items-center justify-center font-bold text-slate-700">Labour Cost</div>
                                                        <div className="flex">
                                                            <div className="bg-slate-300 px-1 flex items-center justify-center font-bold text-[10px] text-slate-600 w-1/2">AED/Kg</div>
                                                            <div className="bg-slate-100 px-1 flex items-center justify-center font-mono tabular-nums w-1/2">{labourRate.toFixed(2)}</div>
                                                        </div>
                                                        <div className="bg-slate-100 p-1 flex items-center justify-end font-mono tabular-nums">{totalLabourCost.toFixed(2)}</div>

                                                        <div className="bg-slate-200 p-1 flex items-center justify-center font-bold text-slate-700">GRC Auxiliary Total</div>
                                                        <div className="col-span-2 bg-slate-100 p-1 flex items-center justify-between font-mono tabular-nums">
                                                            <span>{totalAuxiliary.toFixed(2)}</span>
                                                            {auxiliaryDisabled && <span className="text-[10px] uppercase tracking-wide text-slate-500">Auxiliary disabled for GRC</span>}
                                                        </div>

                                                        <div className="col-span-2 bg-slate-700 text-white p-1 flex items-center justify-center font-bold uppercase text-[10px] tracking-wide">Production & Labour Cost</div>
                                                        <div className="bg-slate-700 text-white p-1 flex items-center justify-end font-mono font-bold tabular-nums">{grcTotal.toFixed(2)}</div>

                                                        <div className="bg-slate-200 p-1 flex items-center justify-center font-bold text-slate-700">Total Weight</div>
                                                        <div className="bg-slate-100 p-1 flex items-center justify-center font-mono tabular-nums">{totals.totalWeight.toFixed(2)}</div>
                                                        <div className="grid grid-cols-2">
                                                            <div className="bg-slate-600 text-white p-1 flex items-center justify-center font-bold text-[10px]">Cost / KG</div>
                                                            <div className="bg-slate-500 text-white p-1 flex items-center justify-center font-mono font-bold tabular-nums">
                                                                {totals.totalWeight > 0 ? (grcTotal / totals.totalWeight).toFixed(2) : '0.00'}
                                                            </div>
                                                        </div>

                                                        <div className="bg-slate-200 p-1 flex items-center justify-center font-bold text-slate-700">Total Area</div>
                                                        <div className="bg-slate-100 p-1 flex items-center justify-center font-mono tabular-nums">{totals.areaTotal.toFixed(2)}</div>
                                                        <div className="grid grid-cols-2">
                                                            <div className="bg-slate-600 text-white p-1 flex items-center justify-center font-bold text-[10px]">Cost / M2</div>
                                                            <div className="bg-slate-500 text-white p-1 flex items-center justify-center font-mono font-bold tabular-nums">
                                                                {totals.areaTotal > 0 ? (grcTotal / totals.areaTotal).toFixed(2) : '0.00'}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {grcPartMode && (
                                                        <div className="border rounded-md overflow-hidden bg-white">
                                                            <div className="px-3 py-2 border-b bg-slate-50 flex items-center justify-between">
                                                                <div>
                                                                    <div className="font-semibold text-slate-900">Optional Part Mode</div>
                                                                    <div className="text-[11px] text-slate-500">Estimate-level design mix, GRC type, and labour remain defaults. Set overrides only where needed.</div>
                                                                </div>
                                                            </div>
                                                            <div className="overflow-x-auto">
                                                                <table className="w-full text-[11px] text-left border-collapse">
                                                                    <thead>
                                                                        <tr className="bg-muted/50 text-muted-foreground border-b uppercase tracking-wider font-semibold text-[10px]">
                                                                            <th className="px-3 py-2">Part Name</th>
                                                                            <th className="px-3 py-2 text-right">Quantity</th>
                                                                            <th className="px-3 py-2 text-right">Total Weight</th>
                                                                            <th className="px-3 py-2">Resolved Design Mix</th>
                                                                            <th className="px-3 py-2">Resolved GRC Type</th>
                                                                            <th className="px-3 py-2 text-right">Resolved Labour Rate</th>
                                                                            <th className="px-3 py-2 text-right">Material Cost</th>
                                                                            <th className="px-3 py-2 text-right">Labour</th>
                                                                            <th className="px-3 py-2 text-right">Auxiliary</th>
                                                                            <th className="px-3 py-2 text-right">Total</th>
                                                                            <th className="px-3 py-2 text-right">Overrides</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody className="divide-y divide-muted/50">
                                                                        {grcParts.map((part) => (
                                                                            <tr key={part.estimate_part_id} className="hover:bg-muted/30 transition-colors">
                                                                                <td className="px-3 py-2 font-medium">{part.part_name}</td>
                                                                                <td className="px-3 py-2 text-right tabular-nums">{Number(part.quantity || 0).toFixed(2)}</td>
                                                                                <td className="px-3 py-2 text-right tabular-nums">{Number((part.weight_kgs || 0) * (part.quantity || 0)).toFixed(2)}</td>
                                                                                <td className="px-3 py-2">{part.grc_resolved_design_mix_name || estimate.design_mix_name || 'Default'}</td>
                                                                                <td className="px-3 py-2">{part.grc_resolved_grc_type_name || estimate.grc_type_name || 'Default'}</td>
                                                                                <td className="px-3 py-2 text-right tabular-nums">{Number(part.grc_resolved_labour_rate_per_kg ?? estimate.prod_aed_per_kg ?? 0).toFixed(2)}</td>
                                                                                <td className="px-3 py-2 text-right tabular-nums">{Number((part.grc_material_cost_unit || 0) * (part.quantity || 0)).toFixed(2)}</td>
                                                                                <td className="px-3 py-2 text-right tabular-nums">{Number((part.grc_labour_cost_unit || 0) * (part.quantity || 0)).toFixed(2)}</td>
                                                                                <td className="px-3 py-2 text-right tabular-nums">{Number(part.grc_auxiliary_allocation || 0).toFixed(2)}</td>
                                                                                <td className="px-3 py-2 text-right tabular-nums font-bold text-primary">{Number(part.grc_total_cost_all || 0).toFixed(2)}</td>
                                                                                <td className="px-3 py-2 text-right">
                                                                                    <button
                                                                                        onClick={() => openGrcOverrideModal(part)}
                                                                                        className="bg-slate-700 hover:bg-slate-800 text-white text-[10px] px-3 py-1 rounded font-semibold uppercase tracking-wide transition-colors"
                                                                                    >
                                                                                        Overrides
                                                                                    </button>
                                                                                </td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })()}
                                    </div>
                                </div>

                                {/* Right Column: GRP */}
                                <div className="min-w-0 space-y-4">
                                    {/* Labour Cost / M2 Input (Above GRP) */}
                                    <div className="bg-white border rounded shadow-sm p-4 flex items-center justify-between">
                                        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr_auto_auto] gap-3 items-center w-full">
                                            <label className="text-sm font-bold text-muted-foreground uppercase tracking-wide">
                                                Default Labour Cost - AED / M2
                                            </label>
                                            <input
                                                type="number"
                                                className="w-32 border rounded py-1.5 px-3 text-right font-mono font-bold text-lg tabular-nums focus:ring-2 focus:ring-primary outline-none"
                                                value={estimate.prod_aed_per_m2_grp ?? 0}
                                                onChange={(e) => setEstimate({ ...estimate, prod_aed_per_m2_grp: parseFloat(e.target.value) || 0 })}
                                                disabled={!canEdit}
                                            />
                                            <label className="text-sm font-bold text-muted-foreground uppercase tracking-wide md:text-right">
                                                GRP Misc Consumables %
                                            </label>
                                            <input
                                                type="number"
                                                className="w-24 border rounded py-1.5 px-3 text-right font-mono font-bold text-lg tabular-nums focus:ring-2 focus:ring-primary outline-none"
                                                value={formatPercent(estimate.grp_misc_consumables_perc ?? 0).toFixed(2)}
                                                onChange={(e) => setEstimate({ ...estimate, grp_misc_consumables_perc: (parseFloat(e.target.value) || 0) / 100 })}
                                                disabled={!canEdit}
                                            />
                                            {canEdit && (
                                                <button
                                                    onClick={handleSaveProductionSettings}
                                                    className="ml-2 bg-primary text-primary-foreground px-3 py-1 rounded text-xs font-bold uppercase tracking-wide hover:bg-primary/90 transition-colors"
                                                >
                                                    Save
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    {/* GRP Card */}
                                    <div className="border rounded shadow-sm bg-white flex flex-col overflow-hidden">
                                        <div className="bg-blue-50 px-4 py-3 border-b border-blue-100 flex justify-between items-center">
                                            <span className="font-bold text-sm tracking-tight text-blue-900">GRP (GLASS REINFORCED PLASTIC)</span>
                                        </div>
                                        <div className="overflow-x-auto">
                                            <table className="w-full text-[11px] text-left border-collapse">
                                                <thead>
                                                    <tr className="bg-muted/50 text-muted-foreground border-b uppercase tracking-wider font-semibold text-[10px]">
                                                        <th className="px-3 py-2">Action</th>
                                                        <th className="px-3 py-2">Part Name</th>
                                                        <th className="px-3 py-2 text-right">Quantity</th>
                                                        <th className="px-3 py-2 text-right">Total Area</th>
                                                        <th className="px-3 py-2 text-right">Material Cost</th>
                                                        <th className="px-3 py-2 text-right">Labour Rate</th>
                                                        <th className="px-3 py-2 text-right">Labour</th>
                                                        <th className="px-3 py-2 text-right">Misc</th>
                                                        <th className="px-3 py-2 text-right">Auxiliary</th>
                                                        <th className="px-3 py-2 text-right">Total</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-muted/50">
                                                    {estimate.parts.filter((part) => Number(part.material_type_id) === 2).map((part) => {
                                                        const materialCost = part.grp_material_cost_total ?? 0;
                                                        const labourRate = part.grp_resolved_labour_rate_per_m2 ?? part.grp_labour_rate_per_m2 ?? estimate.prod_aed_per_m2_grp ?? 0;
                                                        const totalArea = part.area_m2 * part.quantity;
                                                        const labourCost = part.labour_cost_calc ?? (totalArea * labourRate);
                                                        const miscCost = part.grp_misc_allocation ?? 0;
                                                        const auxiliaryCost = part.grp_auxiliary_allocation ?? 0;
                                                        const total = part.production_cost ?? (materialCost + labourCost + miscCost + auxiliaryCost);
                                                        return (
                                                            <tr key={part.estimate_part_id} className="hover:bg-muted/30 transition-colors">
                                                                <td className="px-3 py-2">
                                                                    <button
                                                                        onClick={() => {
                                                                            setSelectedPartForMaterials(part.estimate_part_id);
                                                                            setShowGRPMaterialsModal(true);
                                                                        }}
                                                                        className="bg-slate-700 hover:bg-slate-800 text-white text-[10px] px-3 py-1 rounded font-semibold uppercase tracking-wide transition-colors"
                                                                    >
                                                                        Materials
                                                                    </button>
                                                                </td>
                                                                <td className="px-3 py-2 font-medium truncate max-w-[150px]">{part.part_name}</td>
                                                                <td className="px-3 py-2 text-right tabular-nums">{part.quantity}</td>
                                                                <td className="px-3 py-2 text-right tabular-nums">{totalArea.toFixed(2)}</td>
                                                                <td className="px-3 py-2 text-right tabular-nums">{materialCost.toFixed(2)}</td>
                                                                <td className="px-3 py-2 text-right tabular-nums">{labourRate.toFixed(2)}</td>
                                                                <td className="px-3 py-2 text-right tabular-nums">{labourCost.toFixed(2)}</td>
                                                                <td className="px-3 py-2 text-right tabular-nums">{miscCost.toFixed(2)}</td>
                                                                <td className="px-3 py-2 text-right tabular-nums">{auxiliaryCost.toFixed(2)}</td>
                                                                <td className="px-3 py-2 text-right tabular-nums font-bold text-blue-900">{total.toFixed(2)}</td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                        <div className="mt-auto bg-blue-50 p-2 border-t border-blue-100 space-y-1 text-xs">
                                            {(() => {
                                                const grpParts = (estimate.parts || []).filter((part) => Number(part.material_type_id) === 2);
                                                const totalArea = grpParts.reduce((sum, p) => sum + (p.area_m2 * p.quantity), 0);
                                                const totalMaterialCost = grpParts.reduce((sum, p) => sum + (p.grp_material_cost_total ?? 0), 0);
                                                const totalLabourCost = grpParts.reduce((sum, p) => sum + (p.labour_cost_calc ?? 0), 0);
                                                const totalMisc = grpParts.reduce((sum, p) => sum + (p.grp_misc_allocation ?? 0), 0);
                                                const totalAuxiliary = grpParts.reduce((sum, p) => sum + (p.grp_auxiliary_allocation ?? 0), 0);
                                                const productionTotal = grpParts.reduce((sum, p) => sum + (p.production_cost ?? 0), 0);
                                                const costPerM2 = totalArea > 0 ? (productionTotal / totalArea) : 0;

                                                return (
                                                    <div className="grid grid-cols-2 gap-1 max-w-sm ml-auto">
                                                        <div className="bg-slate-200 p-1 flex items-center justify-center font-bold text-slate-700">Total Area</div>
                                                        <div className="bg-slate-100 p-1 flex items-center justify-end font-mono tabular-nums">{totalArea.toFixed(2)}</div>

                                                        <div className="bg-slate-200 p-1 flex items-center justify-center font-bold text-slate-700">Total Material Cost</div>
                                                        <div className="bg-slate-100 p-1 flex items-center justify-end font-mono tabular-nums">{totalMaterialCost.toFixed(2)}</div>

                                                        <div className="bg-slate-200 p-1 flex items-center justify-center font-bold text-slate-700">Default AED / M2</div>
                                                        <div className="bg-slate-100 p-1 flex items-center justify-end font-mono tabular-nums">{(estimate.prod_aed_per_m2_grp || 0).toFixed(2)}</div>

                                                        <div className="bg-slate-200 p-1 flex items-center justify-center font-bold text-slate-700">Total Labour Cost</div>
                                                        <div className="bg-slate-100 p-1 flex items-center justify-end font-mono tabular-nums">{totalLabourCost.toFixed(2)}</div>

                                                        <div className="bg-slate-200 p-1 flex items-center justify-center font-bold text-slate-700">GRP Misc Total</div>
                                                        <div className="bg-slate-100 p-1 flex items-center justify-end font-mono tabular-nums">{totalMisc.toFixed(2)}</div>

                                                        <div className="bg-slate-200 p-1 flex items-center justify-center font-bold text-slate-700">GRP Auxiliary Total</div>
                                                        <div className="bg-slate-100 p-1 flex items-center justify-between font-mono tabular-nums px-2">
                                                            <span>{totalAuxiliary.toFixed(2)}</span>
                                                            {estimate.apply_auxiliary_to_grp === false && <span className="text-[10px] uppercase tracking-wide text-slate-500">Disabled</span>}
                                                        </div>

                                                        <div className="bg-slate-700 text-white p-1 flex items-center justify-center font-bold text-[10px] tracking-wide uppercase">Final Production Total</div>
                                                        <div className="bg-slate-700 text-white p-1 flex items-center justify-end font-mono font-bold tabular-nums">
                                                            {productionTotal.toFixed(2)}
                                                        </div>

                                                        <div className="bg-slate-600 text-white p-1 flex items-center justify-center font-bold text-[10px] uppercase">Cost / M2</div>
                                                        <div className="bg-slate-500 text-white p-1 flex items-center justify-end font-mono font-bold tabular-nums">{costPerM2.toFixed(2)}</div>
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    </div>

                                </div>
                            </div>
                            <div className="p-6 space-y-6">
                                {/* GRC Calculation Settings Card */}
                                <div className="border rounded shadow-sm bg-white overflow-hidden">
                                    <div className="bg-amber-50 px-4 py-3 border-b border-amber-100 flex justify-between items-start gap-4">
                                        <div>
                                            <span className="font-bold text-sm tracking-tight text-amber-900 uppercase">Shared Auxiliary Settings</span>
                                            <p className="mt-1 text-[11px] text-amber-900/80">These percentages are common inputs. They can be applied separately to GRC and GRP using the toggles below.</p>
                                        </div>
                                        {canEdit && (
                                            <button
                                                onClick={handleSaveProductionSettings}
                                                className="text-[10px] bg-amber-600 hover:bg-amber-700 text-white px-2 py-0.5 rounded font-bold uppercase transition-colors"
                                            >
                                                Save Settings
                                            </button>
                                        )}
                                    </div>
                                    <div className="px-4 pt-4 flex flex-wrap gap-4 text-xs">
                                        <label className="inline-flex items-center gap-2 font-semibold text-slate-700">
                                            <input
                                                type="checkbox"
                                                checked={estimate.apply_auxiliary_to_grc !== false}
                                                onChange={(e) => setEstimate({ ...estimate, apply_auxiliary_to_grc: e.target.checked })}
                                                disabled={!canEdit}
                                            />
                                            Apply to GRC
                                        </label>
                                        <label className="inline-flex items-center gap-2 font-semibold text-slate-700">
                                            <input
                                                type="checkbox"
                                                checked={estimate.apply_auxiliary_to_grp !== false}
                                                onChange={(e) => setEstimate({ ...estimate, apply_auxiliary_to_grp: e.target.checked })}
                                                disabled={!canEdit}
                                            />
                                            Apply to GRP
                                        </label>
                                    </div>
                                    <div className="p-4 grid grid-cols-2 gap-x-6 gap-y-3">
                                        <div className="flex justify-between items-center text-xs">
                                            <span className="text-muted-foreground uppercase font-semibold">Electricity</span>
                                            <div className="w-24 relative">
                                                <input
                                                    type="number"
                                                    className="w-full text-right border rounded px-1 py-0.5 font-mono font-bold focus:ring-1 focus:ring-primary outline-none"
                                                    value={formatPercent(estimate.direct_electricity_perc || 0).toFixed(2)}
                                                    onChange={(e) => setEstimate({ ...estimate, direct_electricity_perc: (parseFloat(e.target.value) || 0) / 100 })}
                                                    disabled={!canEdit}
                                                />
                                                <span className="absolute right-7 top-0.5 text-[10px] text-muted-foreground pointer-events-none">%</span>
                                            </div>
                                        </div>
                                        <div className="flex justify-between items-center text-xs">
                                            <span className="text-muted-foreground uppercase font-semibold">Wastage</span>
                                            <div className="w-24 relative">
                                                <input
                                                    type="number"
                                                    className="w-full text-right border rounded px-1 py-0.5 font-mono font-bold focus:ring-1 focus:ring-primary outline-none"
                                                    value={formatPercent(estimate.direct_wastage_perc || 0).toFixed(2)}
                                                    onChange={(e) => setEstimate({ ...estimate, direct_wastage_perc: (parseFloat(e.target.value) || 0) / 100 })}
                                                    disabled={!canEdit}
                                                />
                                                <span className="absolute right-7 top-0.5 text-[10px] text-muted-foreground pointer-events-none">%</span>
                                            </div>
                                        </div>
                                        <div className="flex justify-between items-center text-xs">
                                            <span className="text-muted-foreground uppercase font-semibold">Painting With Labour</span>
                                            <div className="w-24 relative">
                                                <input
                                                    type="number"
                                                    className="w-full text-right border rounded px-1 py-0.5 font-mono font-bold focus:ring-1 focus:ring-primary outline-none"
                                                    value={formatPercent(estimate.direct_painting_labour_perc || 0).toFixed(2)}
                                                    onChange={(e) => setEstimate({ ...estimate, direct_painting_labour_perc: (parseFloat(e.target.value) || 0) / 100 })}
                                                    disabled={!canEdit}
                                                />
                                                <span className="absolute right-7 top-0.5 text-[10px] text-muted-foreground pointer-events-none">%</span>
                                            </div>
                                        </div>
                                        <div className="flex justify-between items-center text-xs">
                                            <span className="text-muted-foreground uppercase font-semibold">Acid Wash with Labour</span>
                                            <div className="w-24 relative">
                                                <input
                                                    type="number"
                                                    className="w-full text-right border rounded px-1 py-0.5 font-mono font-bold focus:ring-1 focus:ring-primary outline-none"
                                                    value={formatPercent(estimate.direct_acid_wash_labour_perc || 0).toFixed(2)}
                                                    onChange={(e) => setEstimate({ ...estimate, direct_acid_wash_labour_perc: (parseFloat(e.target.value) || 0) / 100 })}
                                                    disabled={!canEdit}
                                                />
                                                <span className="absolute right-7 top-0.5 text-[10px] text-muted-foreground pointer-events-none">%</span>
                                            </div>
                                        </div>
                                        <div className="flex justify-between items-center text-xs">
                                            <span className="text-muted-foreground uppercase font-semibold">Styro Foam</span>
                                            <div className="w-24 relative">
                                                <input
                                                    type="number"
                                                    className="w-full text-right border rounded px-1 py-0.5 font-mono font-bold focus:ring-1 focus:ring-primary outline-none"
                                                    value={formatPercent(estimate.direct_styro_foam || 0).toFixed(2)}
                                                    onChange={(e) => setEstimate({ ...estimate, direct_styro_foam: (parseFloat(e.target.value) || 0) / 100 })}
                                                    disabled={!canEdit}
                                                />
                                                <span className="absolute right-7 top-0.5 text-[10px] text-muted-foreground pointer-events-none">%</span>
                                            </div>
                                        </div>
                                        <div className="flex justify-between items-center text-xs">
                                            <span className="text-muted-foreground uppercase font-semibold">Production Accessories</span>
                                            <div className="w-24 relative">
                                                <input
                                                    type="number"
                                                    className="w-full text-right border rounded px-1 py-0.5 font-mono font-bold focus:ring-1 focus:ring-primary outline-none"
                                                    value={formatPercent(estimate.direct_production_accessories || 0).toFixed(2)}
                                                    onChange={(e) => setEstimate({ ...estimate, direct_production_accessories: (parseFloat(e.target.value) || 0) / 100 })}
                                                    disabled={!canEdit}
                                                />
                                                <span className="absolute right-7 top-0.5 text-[10px] text-muted-foreground pointer-events-none">%</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Common Controls Row */}
                            <div className="px-6 py-4 bg-muted/20 border-t grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div className="flex flex-col">
                                    <span className="text-[10px] uppercase text-muted-foreground font-bold font-mono tracking-widest">Selected Mix</span>
                                    <span className="font-bold text-sm tracking-tight">{estimate.design_mix_name || 'N/A'}</span>
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-[10px] uppercase text-muted-foreground font-bold font-mono tracking-widest">Labour Cost / KG</span>
                                    <span className="font-bold text-sm tracking-tight">{(estimate.prod_aed_per_kg || 0).toFixed(3)} AED</span>
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-[10px] uppercase text-muted-foreground font-bold font-mono tracking-widest">Total Weight</span>
                                    <span className="font-bold text-sm tracking-tight">{totals.totalWeight.toFixed(2)} KG</span>
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-[10px] uppercase text-muted-foreground font-bold font-mono tracking-widest">Total Area</span>
                                    <span className="font-bold text-sm tracking-tight">{totals.areaTotal.toFixed(2)} M2</span>
                                </div>
                            </div>
                        </div>
                    )
                }

                {/* BOM Tab */}
                {
                    openModal === 'bom' && (
                        <div className="bg-white border rounded shadow-sm overflow-hidden pt-1">
                            <div className="bg-muted/40 px-6 py-3 border-b flex items-center justify-between gap-4">
                                <div>
                                    <div className="font-bold text-sm text-gray-900">BOM</div>
                                    <div className="text-xs text-gray-500">Classic BOM view showing all estimate parts together.</div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => handleMarkSectionReviewed('bom')}
                                    disabled={!canEdit || busy}
                                    className="border border-emerald-300 bg-emerald-50 px-4 py-2 rounded-md font-bold text-[11px] uppercase tracking-widest text-emerald-800 hover:bg-emerald-100 disabled:opacity-50"
                                >
                                    Mark Reviewed
                                </button>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-xs text-left border-collapse">
                                    <thead>
                                        <tr className="bg-muted/50 text-muted-foreground border-b uppercase tracking-wider font-bold text-[10px]">
                                            <th className="px-5 py-3 border-r">Action</th>
                                            <th className="px-5 py-3 border-r">Part #</th>
                                            <th className="px-5 py-3 border-r">Material Type</th>
                                            <th className="px-5 py-3 border-r">Part</th>
                                            <th className="px-5 py-3 text-right border-r">Quantity</th>
                                            <th className="px-5 py-3 text-right border-r">Material Quantity</th>
                                            <th className="px-5 py-3 text-right border-r">Material Cost</th>
                                            <th className="px-5 py-3 text-right bg-primary/5 text-primary">Part Total Cost</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y">
                                        {estimate.parts.length > 0 ? (
                                            estimate.parts.map((part, idx) => {
                                                const partBOMTotal = part.bom_items?.reduce((sum, item) => {
                                                    const qty = item.quantity || 0;
                                                    const rate = item.unit_rate || 0;
                                                    const total = item.total_cost ?? (qty * rate);
                                                    return sum + total;
                                                }, 0) || 0;
                                                const partBOMQty = part.bom_items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0;

                                                return (
                                                    <tr key={part.estimate_part_id} className="hover:bg-muted/30 transition-colors">
                                                        <td className="px-5 py-3 border-r">
                                                            <button
                                                                onClick={() => {
                                                                    setSelectedPartForBOM(part.estimate_part_id);
                                                                    setShowBOMMaterialsModal(true);
                                                                }}
                                                                className="bg-slate-700 hover:bg-slate-800 text-white text-[10px] px-3 py-1 rounded font-semibold uppercase tracking-wide transition-colors"
                                                            >
                                                                Materials
                                                            </button>
                                                        </td>
                                                        <td className="px-5 py-3 font-mono text-xs text-muted-foreground border-r">{`HC/${estimate.estimate_id || '0000'}/038${idx + 2}`}</td>
                                                        <td className="px-5 py-3 border-r">
                                                            <span className="bg-blue-100 text-blue-900 px-2 py-0.5 rounded text-[10px] font-bold">GRP</span>
                                                        </td>
                                                        <td className="px-5 py-3 font-medium border-r">{part.part_name}</td>
                                                        <td className="px-5 py-3 text-right tabular-nums border-r">{part.quantity}</td>
                                                        <td className="px-5 py-3 text-right tabular-nums border-r">{partBOMQty.toFixed(2)}</td>
                                                        <td className="px-5 py-3 text-right tabular-nums border-r">{(part.bom_cost || 0).toFixed(2)}</td>
                                                        <td className="px-5 py-3 text-right tabular-nums font-bold text-primary bg-primary/5">{partBOMTotal.toFixed(2)}</td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td className="px-5 py-16 text-center text-muted-foreground italic" colSpan={8}>
                                                    <div className="flex flex-col items-center gap-2">
                                                        <Search size={40} className="text-muted-foreground/30" />
                                                        <span>No parts found for this estimate.</span>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )
                }

                {/* Installations Tab */}
                {
                    openModal === 'installations' && (
                        <div className="bg-white border rounded shadow-sm overflow-hidden pt-1">
                            <div className="bg-muted/50 px-6 py-3 border-b flex justify-between items-center">
                                <div className="flex items-center space-x-4">
                                    <span className="bg-white px-2 py-1 border rounded font-mono text-xs text-muted-foreground shadow-sm">
                                        REF: {estimateNumber}
                                    </span>
                                    <button
                                        onClick={handleSaveInstallationDistribution}
                                        className="border border-slate-300 bg-white px-4 py-1.5 rounded-md hover:bg-slate-50 text-xs font-bold transition-all shadow-sm"
                                        disabled={!canEdit || busy}
                                    >
                                        {busy ? 'SAVING...' : 'SAVE SPLIT'}
                                    </button>
                                    <button
                                        onClick={handleLoadInstallationFactors}
                                        className="bg-primary text-primary-foreground px-4 py-1.5 rounded-md hover:bg-primary/90 text-xs font-bold transition-all flex items-center shadow-sm"
                                        disabled={!canEdit || loadingFactors}
                                    >
                                        {loadingFactors ? (
                                            <><Loader2 className="w-3 h-3 mr-2 animate-spin" /> LOADING...</>
                                        ) : (
                                            'LOAD FACTORS'
                                        )}
                                    </button>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => handleMarkSectionReviewed('installations')}
                                    disabled={!canEdit || busy}
                                    className="border border-emerald-300 bg-emerald-50 px-4 py-2 rounded-md font-bold text-[11px] uppercase tracking-widest text-emerald-800 hover:bg-emerald-100 disabled:opacity-50"
                                >
                                    Mark Reviewed
                                </button>
                            </div>

                            <div className="border-b bg-white px-6 py-5">
                                <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)]">
                                    <div className="rounded-lg border bg-muted/10 p-4">
                                        <div className="mb-3 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Labour Distribution</div>
                                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                            <div className="space-y-1">
                                                <label className="text-[10px] font-bold text-muted-foreground uppercase">Skilled Labor (%)</label>
                                                <input
                                                    type="number"
                                                    className={adminInputClass}
                                                    value={estimate.skilled_manhours_perc ?? 50}
                                                    onChange={(e) => setEstimate({ ...estimate, skilled_manhours_perc: parseFloat(e.target.value) || 0 })}
                                                    disabled={!canEdit}
                                                />
                                            </div>
                                            <div className="space-y-1">
                                                <label className="text-[10px] font-bold text-muted-foreground uppercase">Semi-skilled Labor (%)</label>
                                                <input
                                                    type="number"
                                                    className={adminInputClass}
                                                    value={estimate.unskilled_manhours_perc ?? 50}
                                                    onChange={(e) => setEstimate({ ...estimate, unskilled_manhours_perc: parseFloat(e.target.value) || 0 })}
                                                    disabled={!canEdit}
                                                />
                                            </div>
                                            <div className="space-y-1 md:col-span-2">
                                                <label className="text-[10px] font-bold text-muted-foreground uppercase">Non-working Hours / Week</label>
                                                <input
                                                    type="number"
                                                    className={adminInputClass}
                                                    value={(estimate as any).non_working_hours_per_week ?? 8}
                                                    onChange={(e) => setEstimate({ ...estimate, non_working_hours_per_week: parseFloat(e.target.value) || 0 } as any)}
                                                    disabled={!canEdit}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="rounded-lg border bg-muted/10 p-4">
                                        <div className="mb-3 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Distribution Preview</div>
                                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                                            <div className="rounded border bg-white px-3 py-2">
                                                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Total Manhours</div>
                                                <div className="mt-1 text-lg font-bold text-slate-900">{totalInstallationManhours.toFixed(2)}</div>
                                            </div>
                                            <div className="rounded border bg-white px-3 py-2">
                                                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Non-working Hours</div>
                                                <div className="mt-1 text-lg font-bold text-slate-900">{installationLabourSplitPreview.nonWorkingHours.toFixed(2)}</div>
                                            </div>
                                            <div className="rounded border bg-white px-3 py-2">
                                                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Skilled Allocation</div>
                                                <div className="mt-1 text-lg font-bold text-slate-900">{installationLabourSplitPreview.skilledHours.toFixed(2)}</div>
                                            </div>
                                            <div className="rounded border bg-white px-3 py-2">
                                                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Semi-skilled Allocation</div>
                                                <div className="mt-1 text-lg font-bold text-slate-900">{installationLabourSplitPreview.semiSkilledHours.toFixed(2)}</div>
                                            </div>
                                        </div>
                                        <div className="mt-3 text-xs text-slate-500">
                                            Load Factors seeds the full installation sheet for editing. When part manhours are entered, the first three labour rows are refreshed from the labour split while the remaining installation rows stay manual.
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Sub-tabs Navigation */}
                            <div className="flex bg-muted/30 border-b p-1 gap-1">
                                <button
                                    className={`flex-1 py-2 rounded-sm font-bold text-xs uppercase tracking-wider transition-all ${showInstallationModal ? 'bg-white text-primary shadow-sm' : 'text-muted-foreground hover:bg-white/50'}`}
                                    onClick={() => setShowInstallationModal(true)}
                                >
                                    Man Hours Breakdown
                                </button>
                                <button
                                    className={`flex-1 py-2 rounded-sm font-bold text-xs uppercase tracking-wider transition-all ${!showInstallationModal ? 'bg-white text-primary shadow-sm' : 'text-muted-foreground hover:bg-white/50'}`}
                                    onClick={() => setShowInstallationModal(false)}
                                >
                                    Installation Cost Detail
                                </button>
                            </div>

                            {/* Man Hours Content */}
                            {showInstallationModal && (
                                <div className="p-0">
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-sm text-left border-collapse">
                                            <thead>
                                                <tr className="bg-muted/50 text-muted-foreground border-b uppercase tracking-wider font-semibold text-[10px]">
                                                    <th className="px-4 py-3">Part #</th>
                                                    <th className="px-4 py-3">Material</th>
                                                    <th className="px-4 py-3">Part Name</th>
                                                    <th className="px-4 py-3 text-right">Quantity</th>
                                                    <th className="px-4 py-3 text-right">Area (m2)</th>
                                                    <th className="px-4 py-3 text-right">Weight (Kg)</th>
                                                    <th className="px-4 py-3 text-right bg-primary/5 text-primary">Manhours Required</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-muted/50">
                                                {(draftParts || estimate.parts).map((part: any, idx: number) => {
                                                    const material = estimate.project_type_id === 2 ? 'GRP' : 'GRC';
                                                    const weight = parseFloat(part.weight_kgs || 0);

                                                    return (
                                                        <tr key={part.estimate_part_id ?? `mh-${idx}`} className="hover:bg-muted/30 transition-colors">
                                                            <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{`HC/${estimate.estimate_id}/039${idx + 1}`}</td>
                                                            <td className="px-4 py-3 font-mono text-xs"><span className="bg-muted text-muted-foreground px-1.5 py-0.5 rounded text-[10px] font-bold">{material}</span></td>
                                                            <td className="px-4 py-3 font-medium">{part.part_name}</td>
                                                            <td className="px-4 py-3 text-right tabular-nums">{part.quantity}</td>
                                                            <td className="px-4 py-3 text-right tabular-nums">{(part.area_m2 || 0).toFixed(2)}</td>
                                                            <td className="px-4 py-3 text-right tabular-nums">{weight.toFixed(2)}</td>
                                                            <td className="px-4 py-3 text-right tabular-nums font-bold text-primary bg-primary/5">
                                                                <input
                                                                    type="number"
                                                                    className="w-32 ml-auto block rounded border border-slate-300 bg-white py-1 px-2 text-right tabular-nums font-bold text-primary focus:ring-primary focus:border-primary disabled:bg-slate-100 disabled:opacity-60"
                                                                    value={part.man_hours_required ?? 0}
                                                                    onChange={(e) => {
                                                                        if (!draftParts) return;
                                                                        const next = parseFloat(e.target.value || '0') || 0;
                                                                        setDraftParts(prev => prev!.map((row: any, i: number) => {
                                                                            if (i !== idx) return row;
                                                                            const qty = parseFloat(row.quantity || 0) || 0;
                                                                            return {
                                                                                ...row,
                                                                                man_hours_required: next,
                                                                                man_hours_per_unit: qty > 0 ? next / qty : 0,
                                                                            };
                                                                        }));
                                                                    }}
                                                                    disabled={!canEdit || !draftParts}
                                                                />
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                            {draftParts && (
                                                <tfoot className="bg-muted/30 font-bold">
                                                    <tr>
                                                        <td className="px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground" colSpan={6}>Total Manhours</td>
                                                        <td className="px-4 py-3 text-right tabular-nums text-primary bg-primary/5">
                                                            {draftParts.reduce((sum: number, p: any) => sum + (parseFloat(p.man_hours_required || 0) || 0), 0).toFixed(2)}
                                                        </td>
                                                    </tr>
                                                </tfoot>
                                            )}
                                        </table>
                                    </div>

                                    {draftParts && (
                                        <div className="p-4 border-t bg-muted/10 flex justify-end">
                                            <button
                                                onClick={() => handleInputsSave()}
                                                disabled={!canEdit || savingInputs}
                                                className="bg-primary text-primary-foreground px-4 py-2 rounded-md font-bold text-xs uppercase tracking-widest hover:bg-primary/90 transition-all shadow-md disabled:opacity-50"
                                            >
                                                {savingInputs ? 'SAVING...' : 'SAVE MANHOURS INPUTS'}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Installation Cost Content */}
                            {!showInstallationModal && (
                                <div className="p-0">
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-sm text-left border-collapse">
                                            <thead>
                                                <tr className="bg-muted/50 text-muted-foreground border-b uppercase tracking-wider font-semibold text-[10px]">
                                                    <th className="px-4 py-3">Category</th>
                                                    <th className="px-4 py-3">Activity / Item</th>
                                                    <th className="px-4 py-3 text-right">Man Hours</th>
                                                    <th className="px-4 py-3 text-right">Qty</th>
                                                    <th className="px-4 py-3 text-center">Unit</th>
                                                    <th className="px-4 py-3 text-right">Unit Rate</th>
                                                    <th className="px-4 py-3 text-right bg-primary/5 text-primary">Cost</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-muted/50">
                                                {effectiveDraftInstallItems && effectiveDraftInstallItems.length > 0 ? (
                                                    effectiveDraftInstallItems.map((item, idx) => {
                                                        const cost = calculateInstallationLineCost(item.man_hours, item.quantity, item.unit_rate);
                                                        const isAutoLabourRow = isInstallationLabourAutoRow(item.sub_category);
                                                        return (
                                                            <tr key={item.estimate_inst_id} className="hover:bg-muted/30 transition-colors">
                                                                <td className="px-4 py-3 font-semibold text-xs text-muted-foreground uppercase">{item.main_category}</td>
                                                                <td className="px-4 py-3 font-medium">{item.sub_category}</td>
                                                                <td className="px-4 py-3 text-right tabular-nums">
                                                                    <input
                                                                        type="number"
                                                                        className="w-28 ml-auto block border-muted rounded py-1 px-2 text-right tabular-nums focus:ring-primary focus:border-primary disabled:opacity-60"
                                                                        value={item.man_hours ?? 0}
                                                                        onChange={(e) => setDraftInstallItems(prev => prev!.map((r: any, i: number) => i === idx ? ({ ...r, man_hours: e.target.value }) : r))}
                                                                        disabled={!canEdit || isAutoLabourRow}
                                                                    />
                                                                </td>
                                                                <td className="px-4 py-3 text-right tabular-nums">
                                                                    <input
                                                                        type="number"
                                                                        className="w-24 ml-auto block border-muted rounded py-1 px-2 text-right tabular-nums focus:ring-primary focus:border-primary disabled:opacity-60"
                                                                        value={item.quantity ?? 0}
                                                                        onChange={(e) => setDraftInstallItems(prev => prev!.map((r: any, i: number) => i === idx ? ({ ...r, quantity: e.target.value }) : r))}
                                                                        disabled={!canEdit}
                                                                    />
                                                                </td>
                                                                <td className="px-4 py-3 text-center text-[10px] font-bold text-muted-foreground">{item.unit || '-'}</td>
                                                                <td className="px-4 py-3 text-right tabular-nums">
                                                                    <input
                                                                        type="number"
                                                                        className="w-28 ml-auto block border-muted rounded py-1 px-2 text-right tabular-nums focus:ring-primary focus:border-primary disabled:opacity-60"
                                                                        value={item.unit_rate ?? 0}
                                                                        onChange={(e) => setDraftInstallItems(prev => prev!.map((r: any, i: number) => i === idx ? ({ ...r, unit_rate: e.target.value }) : r))}
                                                                        disabled={!canEdit}
                                                                    />
                                                                </td>
                                                                <td className="px-4 py-3 text-right tabular-nums font-bold text-primary bg-primary/5">{cost.toFixed(2)}</td>
                                                            </tr>
                                                        );
                                                    })
                                                ) : (
                                                    <tr>
                                                        <td className="px-4 py-12 text-center text-muted-foreground italic" colSpan={7}>
                                                            No installation cost items defined. Click "LOAD FACTORS" to populate.
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                            {effectiveDraftInstallItems && effectiveDraftInstallItems.length > 0 && (
                                                <tfoot className="bg-muted font-bold text-sm">
                                                    <tr>
                                                        <td className="px-4 py-4 uppercase tracking-tighter text-xs text-muted-foreground" colSpan={6}>Total Installation Cost</td>
                                                        <td className="px-4 py-4 text-right tabular-nums text-lg text-primary">
                                                            {effectiveDraftInstallItems.reduce((sum: number, it: any) => {
                                                                return sum + calculateInstallationLineCost(it.man_hours, it.quantity, it.unit_rate);
                                                            }, 0).toLocaleString(undefined, { minimumFractionDigits: 2 })} AED
                                                        </td>
                                                    </tr>
                                                </tfoot>
                                            )}
                                        </table>
                                    </div>

                                    <div className="p-4 border-t bg-muted/10 flex justify-end">
                                        <button
                                            onClick={handleSaveInstallationItems}
                                            disabled={!canEdit || savingInstallItems}
                                            className="bg-primary text-primary-foreground px-4 py-2 rounded-md font-bold text-xs uppercase tracking-widest hover:bg-primary/90 transition-all shadow-md disabled:opacity-50"
                                        >
                                            {savingInstallItems ? 'SAVING...' : 'SAVE INSTALLATION ITEMS'}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )
                }

                {/* Pricing Controls Tab */}
                {
                    openModal === 'pricing' && (
                        <div className="bg-white border rounded shadow-sm overflow-hidden pt-1">
                            <div className="bg-muted/50 px-6 py-3 border-b flex flex-wrap justify-end items-center gap-3 shadow-sm z-10">
                                <button
                                    onClick={() => handleMarkSectionReviewed('pricing')}
                                    disabled={!canEdit || busy}
                                    className={`rounded border px-4 py-2 text-xs font-bold uppercase tracking-widest ${getSectionCellClass(resolveSectionReviewState('pricing'))} disabled:opacity-50`}
                                >
                                    Mark Reviewed
                                </button>
                                <button
                                    onClick={handleSaveAdminControls}
                                    disabled={busy || estimate.is_freezed}
                                    className="bg-primary text-primary-foreground px-6 py-2 rounded-md font-bold text-xs uppercase tracking-widest hover:bg-primary/90 transition-all shadow-md disabled:opacity-50"
                                >
                                    {busy ? 'SAVING...' : 'SAVE CONTROLS'}
                                </button>
                            </div>

                            <div className="p-6 space-y-8 bg-muted/5">
                                <div className="rounded-lg border bg-white p-4 shadow-sm">
                                    <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">Pricing Controls Review</div>
                                    <div className="mt-2 text-sm font-semibold text-slate-900">
                                        {resolveSectionReviewState('pricing') ? 'Reviewed' : 'Pending review'}
                                    </div>
                                    <div className="mt-1 text-xs text-slate-500">Profit margin, discount, VAT, and the final design premium review before quotation conclusion.</div>
                                </div>

                                <div className="bg-white p-4 rounded-lg border shadow-sm grid grid-cols-1 md:grid-cols-3 gap-6">
                                    <div className="space-y-1">
                                        <label className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">Profit Margin (%)</label>
                                        <div className="relative">
                                            <input
                                                type="number"
                                                className={`${adminInputClass} font-bold text-primary tabular-nums`}
                                                value={formatPercent(estimate.profit_margin || 0).toFixed(2)}
                                                onChange={(e) => setEstimate({ ...estimate, profit_margin: (parseFloat(e.target.value) || 0) / 100 })}
                                                disabled={!canEdit}
                                            />
                                            <span className="absolute right-3 top-3 text-primary/40 font-bold">%</span>
                                        </div>
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">VAT Rate (%)</label>
                                        <input
                                            type="number"
                                            className={`${adminInputClass} font-bold tabular-nums`}
                                            value={formatPercent(estimate.vat_percent || 0).toFixed(2)}
                                            onChange={(e) => setEstimate({ ...estimate, vat_percent: (parseFloat(e.target.value) || 0) / 100 })}
                                            disabled={!canEdit}
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">Discount (%)</label>
                                        <input
                                            type="number"
                                            className={`${adminInputClass} font-bold tabular-nums`}
                                            value={formatPercent(estimate.discount_percent || 0).toFixed(2)}
                                            onChange={(e) => setEstimate({ ...estimate, discount_percent: (parseFloat(e.target.value) || 0) / 100 })}
                                            disabled={!canEdit}
                                        />
                                    </div>
                                </div>

                                {renderDesignPremiumReviewCard()}

                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                    <div className="bg-white border rounded-lg overflow-hidden shadow-sm">
                                        <div className="bg-muted px-4 py-2 border-b font-bold text-[10px] text-muted-foreground uppercase tracking-wider">Proposal Header</div>
                                        <div className="p-4 space-y-4">
                                            <div className="space-y-1">
                                                <label className="text-[10px] font-bold text-muted-foreground uppercase">Attention</label>
                                                <input
                                                    type="text"
                                                    className={adminInputClass}
                                                    value={estimate.attention || ''}
                                                    onChange={(e) => setEstimate({ ...estimate, attention: e.target.value })}
                                                    placeholder="Contact person name..."
                                                    disabled={!canEdit}
                                                />
                                            </div>
                                            <div className="space-y-1">
                                                <label className="text-[10px] font-bold text-muted-foreground uppercase">Validity</label>
                                                <input
                                                    type="text"
                                                    className={adminInputClass}
                                                    value={estimate.validity || ''}
                                                    onChange={(e) => setEstimate({ ...estimate, validity: e.target.value })}
                                                    disabled={!canEdit}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-white border rounded-lg overflow-hidden shadow-sm">
                                        <div className="bg-muted px-4 py-2 border-b font-bold text-[10px] text-muted-foreground uppercase tracking-wider">Quotation Notes</div>
                                        <div className="p-4 text-sm text-slate-500">
                                            Save these pricing controls, then use the header Preview action to confirm the final commercial wording before sending.
                                        </div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                    <div className="bg-white border rounded-lg overflow-hidden shadow-sm">
                                        <div className="bg-muted px-4 py-2 border-b font-bold text-[10px] text-muted-foreground uppercase tracking-wider">Technical Annexure (Annexure A)</div>
                                        <div className="p-4">
                                            <textarea
                                                className={`${adminInputClass} min-h-[220px]`}
                                                value={estimate.annexure_a || ''}
                                                onChange={(e) => setEstimate({ ...estimate, annexure_a: e.target.value })}
                                                placeholder="Standard Technical Annexure..."
                                                disabled={!canEdit}
                                            />
                                        </div>
                                    </div>

                                    <div className="bg-white border rounded-lg overflow-hidden shadow-sm">
                                        <div className="bg-muted px-4 py-2 border-b font-bold text-[10px] text-muted-foreground uppercase tracking-wider">Terms & Conditions (Annexure B)</div>
                                        <div className="p-4">
                                            <textarea
                                                className={`${adminInputClass} min-h-[220px]`}
                                                value={estimate.annexure_b || ''}
                                                onChange={(e) => setEstimate({ ...estimate, annexure_b: e.target.value })}
                                                placeholder="Standard Terms & Conditions..."
                                                disabled={!canEdit}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )
                }

                {/* Administrative Controls Tab */}
                {
                    openModal === 'admin' && (
                        <div className="bg-white border rounded shadow-sm overflow-hidden pt-1">
                            <div className="bg-muted/50 px-6 py-3 border-b flex flex-wrap justify-end items-center gap-3 shadow-sm z-10">
                                <div className="flex items-center space-x-3">
                                    <button
                                        onClick={() => handleMarkSectionReviewed('admin')}
                                        disabled={!canEdit || busy}
                                        className={`rounded border px-4 py-2 text-xs font-bold uppercase tracking-widest ${getSectionCellClass(resolveSectionReviewState('admin'))} disabled:opacity-50`}
                                    >
                                        Mark Reviewed
                                    </button>
                                    <button
                                        onClick={handleSaveAdminControls}
                                        disabled={busy || estimate.is_freezed}
                                        className="bg-primary text-primary-foreground px-6 py-2 rounded-md font-bold text-xs uppercase tracking-widest hover:bg-primary/90 transition-all shadow-md disabled:opacity-50"
                                    >
                                        {busy ? 'SAVING...' : 'SAVE CONTROLS'}
                                    </button>
                                </div>
                            </div>

                            <div className="p-6 space-y-8 bg-muted/5">
                                <div className="rounded-lg border bg-white p-4 shadow-sm">
                                    <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">Administrative Controls Review</div>
                                    <div className="mt-2 text-sm font-semibold text-slate-900">
                                        {resolveSectionReviewState('admin') ? 'Reviewed' : 'Pending review'}
                                    </div>
                                    <div className="mt-1 text-xs text-slate-500">Logistics, engineering costs, admin percentages, and OH per SQM.</div>
                                </div>

                                {/* Pricing Strategy Row */}
                                <div className="bg-white p-4 rounded-lg border shadow-sm grid grid-cols-1 md:grid-cols-3 gap-6">
                                    <div className="space-y-1">
                                        <label className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">Profit Margin (%)</label>
                                        <div className="relative">
                                            <input
                                                type="number"
                                                className={`${adminInputClass} font-bold text-primary tabular-nums`}
                                                value={formatPercent(estimate.profit_margin || 0).toFixed(2)}
                                                onChange={(e) => setEstimate({ ...estimate, profit_margin: (parseFloat(e.target.value) || 0) / 100 })}
                                                disabled={!canEdit}
                                            />
                                            <span className="absolute right-3 top-3 text-primary/40 font-bold">%</span>
                                        </div>
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">VAT Rate (%)</label>
                                        <input
                                            type="number"
                                            className={`${adminInputClass} font-bold tabular-nums`}
                                            value={formatPercent(estimate.vat_percent || 0).toFixed(2)}
                                            onChange={(e) => setEstimate({ ...estimate, vat_percent: (parseFloat(e.target.value) || 0) / 100 })}
                                            disabled={!canEdit}
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">Discount (%)</label>
                                        <input
                                            type="number"
                                            className={`${adminInputClass} font-bold tabular-nums`}
                                            value={formatPercent(estimate.discount_percent || 0).toFixed(2)}
                                            onChange={(e) => setEstimate({ ...estimate, discount_percent: (parseFloat(e.target.value) || 0) / 100 })}
                                            disabled={!canEdit}
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                    {/* Left Column: Direct Costs & Logistics */}
                                    <div className="space-y-6">
                                        <div className="bg-white border rounded-lg overflow-hidden shadow-sm">
                                            <div className="bg-muted px-4 py-2 border-b font-bold text-[10px] text-muted-foreground uppercase tracking-wider">Logistics & Allowances</div>
                                            <div className="p-4 grid grid-cols-2 gap-4">
                                                <div className="space-y-1">
                                                    <label className="text-[10px] font-bold text-muted-foreground uppercase">Packing & Loading (%)</label>
                                                    <input
                                                        type="number"
                                                        className={`${adminInputClass} tabular-nums`}
                                                        value={formatPercent(estimate.logistics_packing_loading_perc || 0).toFixed(2)}
                                                        onChange={(e) => setEstimate({ ...estimate, logistics_packing_loading_perc: (parseFloat(e.target.value) || 0) / 100 })}
                                                        disabled={!canEdit}
                                                    />
                                                </div>
                                                <div className="space-y-1">
                                                    <label className="text-[10px] font-bold text-muted-foreground uppercase">Freight Cost (%)</label>
                                                    <input
                                                        type="number"
                                                        className={`${adminInputClass} tabular-nums`}
                                                        value={formatPercent(estimate.logistics_freight_cost_perc || 0).toFixed(2)}
                                                        onChange={(e) => setEstimate({ ...estimate, logistics_freight_cost_perc: (parseFloat(e.target.value) || 0) / 100 })}
                                                        disabled={!canEdit}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="bg-white border rounded-lg overflow-hidden shadow-sm">
                                            <div className="bg-muted px-4 py-2 border-b font-bold text-[10px] text-muted-foreground uppercase tracking-wider">Project Management (Fixed Costs)</div>
                                            <div className="p-4 space-y-3">
                                                {[
                                                    { label: 'Design & Engineering', field: 'pm_design_cost' },
                                                    { label: 'Project Manager Cost', field: 'pm_project_manager_cost' },
                                                    { label: 'Site Intervention', field: 'pm_site_intervention_cost' },
                                                    { label: 'Documentation / Drawings', field: 'pm_documentation_cost' },
                                                    { label: 'Travel Expenses', field: 'pm_travel_cost' },
                                                ].map(item => (
                                                    <div key={item.field} className="flex items-center justify-between border-b border-muted/30 pb-2">
                                                        <label className="text-xs text-muted-foreground font-medium">{item.label}</label>
                                                        <div className="relative w-32">
                                                            <input
                                                                type="number"
                                                                className={`${adminInputClass} text-right font-mono font-bold`}
                                                                value={(estimate as any)[item.field] || 0}
                                                                onChange={(e) => setEstimate({ ...estimate, [item.field]: parseFloat(e.target.value) || 0 })}
                                                                disabled={!canEdit}
                                                            />
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Right Column: Overheads & Setup Content */}
                                    <div className="space-y-6">
                                        <div className="bg-white border rounded-lg overflow-hidden shadow-sm">
                                            <div className="bg-muted px-4 py-2 border-b font-bold text-[10px] text-muted-foreground uppercase tracking-wider">Administrative Overheads</div>
                                            <div className="p-4 grid grid-cols-2 gap-4">
                                                {[
                                                    { label: 'Contingency (%)', field: 'admin_contigency_perc' },
                                                    { label: 'Warranty (%)', field: 'admin_warranty_perc' },
                                                    { label: 'Retention (%)', field: 'admin_retention_perc' },
                                                    { label: 'Banking (%)', field: 'admin_banking_finance_perc' },
                                                    { label: 'Other Costs (%)', field: 'admin_other_cost_perc' },
                                                ].map(item => (
                                                    <div key={item.field} className="space-y-1">
                                                        <label className="text-[10px] font-bold text-muted-foreground uppercase">{item.label}</label>
                                                        <input
                                                            type="number"
                                                            className={`${adminInputClass} tabular-nums`}
                                                            value={formatPercent((estimate as any)[item.field] || 0).toFixed(2)}
                                                            onChange={(e) => {
                                                                const val = parseFloat(e.target.value) || 0;
                                                                const newVal = val / 100;
                                                                setEstimate({ ...estimate, [item.field]: newVal });
                                                            }}
                                                            disabled={!canEdit}
                                                        />
                                                    </div>
                                                ))}
                                                <div className="col-span-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-3">
                                                    <div className="space-y-3">
                                                        <div className="flex flex-wrap items-end justify-between gap-3">
                                                            <div className="flex min-w-[220px] flex-1 flex-col gap-2">
                                                                <div className="flex flex-wrap items-center gap-2">
                                                                    <label className="text-[10px] font-bold text-muted-foreground uppercase">OH / SQM (AED)</label>
                                                                    {ohSourceStatus && (
                                                                        <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] ${ohSourceStatus.badgeClass}`}>
                                                                            {ohSourceStatus.label}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <input
                                                                    type="number"
                                                                    className={`${adminInputClass} w-full max-w-[220px] tabular-nums`}
                                                                    value={estimate.oh_per_sqm || 0}
                                                                    onChange={(e) => setEstimate({ ...estimate, oh_per_sqm: parseFloat(e.target.value) || 0 })}
                                                                    disabled={!canEdit}
                                                                />
                                                            </div>
                                                            <div className="flex flex-wrap gap-2">
                                                                <button
                                                                    type="button"
                                                                    onClick={handleOpenOverheadSheet}
                                                                    className="rounded border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                                                                >
                                                                    Open Overhead Sheet
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    onClick={handleApplyOhSheetValue}
                                                                    disabled={!canEdit || !ohSheetRatePerSqm}
                                                                    className="rounded border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                                                                >
                                                                    Use OH Sheet Value
                                                                </button>
                                                            </div>
                                                        </div>
                                                        <div className="space-y-1 text-xs text-slate-500">
                                                            <div>
                                                                Overhead sheet default: {ohSheetRatePerSqm ? `${formatCurrency(ohSheetRatePerSqm)} AED / SQM` : 'Unavailable'}
                                                            </div>
                                                            {ohSourceStatus && (
                                                                <div className="max-w-3xl">
                                                                    {ohSourceStatus.detail}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )
                }

                {/* Quotation Preview Tab */}
                {
                    openModal === 'overheadsheet' && (
                        <div className="rounded border bg-white shadow-sm overflow-hidden min-h-[72vh]">
                            <OverheadCostManager embedded />
                        </div>
                    )
                }

                {
                    openModal === 'quotationpreview' && estimate && (
                        <div className="rounded border bg-white shadow-sm overflow-hidden">
                            <iframe
                                key={`quotation-preview-html-${estimate.estimate_id}`}
                                title={`Quotation Preview ${estimate.estimate_id}`}
                                src={`${API_BASE_URL}/estimates/${estimate.estimate_id}/print`}
                                scrolling="auto"
                                className="block w-full bg-white"
                                style={{ height: '88vh' }}
                            />
                        </div>
                    )
                }

                {/* Generate Tab */}
                {
                    openModal === 'generate' && (
                        <div className="bg-white border rounded shadow-sm overflow-hidden pt-1">
                            <div className="p-6">
                                <h3 className="text-lg font-bold text-gray-800 mb-4">Generate Documents</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                                    {/* Quotation */}
                                    <div className="bg-slate-50 border rounded-lg p-6 flex flex-col items-center text-center hover:bg-slate-100 transition-colors cursor-pointer"
                                        onClick={handlePreviewQuotation}
                                    >
                                        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4 text-blue-600">
                                            <Eye size={32} />
                                        </div>
                                        <h4 className="font-bold text-gray-900 mb-2">Quotation</h4>
                                        <p className="text-sm text-gray-500 mb-4">Preview the live quotation HTML, then open the PDF/print output.</p>
                                        <div className="flex w-full flex-col gap-2">
                                            <button
                                                type="button"
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    handlePreviewQuotation();
                                                }}
                                                className="w-full rounded bg-white px-4 py-2 text-sm font-semibold text-blue-700 border border-blue-200 hover:bg-blue-50"
                                            >
                                                Preview Quotation
                                            </button>
                                            <button
                                                type="button"
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    handleDownloadPDF();
                                                }}
                                                className="w-full rounded bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
                                            >
                                                Generate PDF
                                            </button>
                                        </div>
                                    </div>

                                    {/* Sale Orders */}
                                    <div className="bg-slate-50 border rounded-lg p-6 flex flex-col items-center text-center hover:bg-slate-100 transition-colors">
                                        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4 text-green-600">
                                            <Search size={32} />
                                        </div>
                                        <h4 className="font-bold text-gray-900 mb-2">Sale Orders</h4>

                                        {/* Create Action (Top) */}
                                        <div className="w-full mb-2">
                                            {estimate.is_freezed ? (
                                                <button
                                                    onClick={() => navigate(`/legacy/orders/create?estimate_id=${estimate.estimate_id}`)}
                                                    className="bg-green-600 text-white px-4 py-2 rounded text-sm font-semibold hover:bg-green-700 w-full"
                                                >
                                                    Create Sale Order
                                                </button>
                                            ) : (
                                                <div className="group relative w-full">
                                                    <button
                                                        disabled
                                                        className="bg-green-400 text-white px-4 py-2 rounded text-sm font-semibold w-full opacity-50 cursor-not-allowed"
                                                    >
                                                        Create Sale Order
                                                    </button>
                                                    <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded hidden group-hover:block whitespace-nowrap z-10">
                                                        Freeze estimate first
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* View Action (Below Create) */}
                                        <button
                                            onClick={() => navigate(`/legacy/orders?estimate_id=${estimate.estimate_id}`)}
                                            className="text-green-600 border border-green-200 px-4 py-2 rounded text-sm font-medium hover:bg-green-50 w-full mb-4"
                                        >
                                            View All Orders
                                        </button>

                                        {/* List Orders (Bottom) */}
                                        <div className="w-full space-y-2 flex-grow overflow-y-auto max-h-40">
                                            {(estimate.related_orders || []).length > 0 ? (
                                                (estimate.related_orders || []).map((so: any) => (
                                                    <button
                                                        key={so.so_id}
                                                        onClick={() => navigate(`/legacy/orders/${so.so_id}`)}
                                                        className="w-full text-xs flex justify-between items-center bg-white border px-3 py-2 rounded hover:border-green-400 text-gray-700 shadow-sm"
                                                    >
                                                        <span className="font-mono font-medium">{so.so_number}</span>
                                                        <span className="text-gray-400">{new Date(so.so_date).toLocaleDateString()}</span>
                                                    </button>
                                                ))
                                            ) : (
                                                <p className="text-sm text-gray-400 italic py-2">No sale orders yet</p>
                                            )}
                                        </div>
                                    </div>

                                    {/* Invoices */}
                                    <div className="bg-slate-50 border rounded-lg p-6 flex flex-col items-center text-center hover:bg-slate-100 transition-colors">
                                        <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-4 text-purple-600">
                                            <Search size={32} />
                                        </div>
                                        <h4 className="font-bold text-gray-900 mb-2">Invoices</h4>

                                        {/* Create Action (Top) */}
                                        <div className="w-full mb-2">
                                            {(() => {
                                                const orders = estimate.related_orders || [];
                                                if (orders.length === 0) {
                                                    return (
                                                        <div className="w-full relative group">
                                                            <button className="bg-purple-400 text-white px-4 py-2 rounded text-sm font-semibold w-full opacity-50 cursor-not-allowed">
                                                                Create Invoice
                                                            </button>
                                                            <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded hidden group-hover:block whitespace-nowrap z-10">
                                                                Convert to Sale Order first
                                                            </div>
                                                        </div>
                                                    );
                                                } else if (orders.length === 1) {
                                                    return (
                                                        <button
                                                            onClick={() => navigate(`/legacy/invoices/create/${orders[0].so_id}`)}
                                                            className="bg-purple-600 text-white px-4 py-2 rounded text-sm font-semibold hover:bg-purple-700 w-full"
                                                        >
                                                            Create Invoice
                                                        </button>
                                                    );
                                                } else {
                                                    return (
                                                        <div className="w-full flex flex-col gap-2">
                                                            <span className="text-xs text-gray-500 mb-1">Create for:</span>
                                                            {orders.map((so: any) => (
                                                                <button
                                                                    key={so.so_id}
                                                                    onClick={() => navigate(`/legacy/invoices/create/${so.so_id}`)}
                                                                    className="bg-purple-600 text-white px-3 py-1.5 rounded text-xs font-semibold hover:bg-purple-700 w-full"
                                                                >
                                                                    {so.so_number}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    );
                                                }
                                            })()}
                                        </div>

                                        {/* View Action (Below Create) */}
                                        <button
                                            onClick={() => navigate(`/legacy/invoices?estimate_id=${estimate.estimate_id}`)}
                                            className="text-purple-600 border border-purple-200 px-4 py-2 rounded text-sm font-medium hover:bg-purple-50 w-full mb-4"
                                        >
                                            View All Invoices
                                        </button>

                                        {/* List Invoices (Bottom) */}
                                        <div className="w-full space-y-2 flex-grow overflow-y-auto max-h-40">
                                            {(estimate.related_invoices || []).length > 0 ? (
                                                (estimate.related_invoices || []).map((inv: any) => (
                                                    <button
                                                        key={inv.invoice_id}
                                                        onClick={() => navigate(`/legacy/invoices/${inv.invoice_id}`)}
                                                        className="w-full text-xs flex justify-between items-center bg-white border px-3 py-2 rounded hover:border-purple-400 text-gray-700 shadow-sm"
                                                    >
                                                        <span className="font-mono font-medium">{inv.invoice_number}</span>
                                                        <span className="text-gray-400 font-mono">{inv.amount?.toLocaleString()} AED</span>
                                                    </button>
                                                ))
                                            ) : (
                                                <p className="text-sm text-gray-400 italic py-2">No invoices yet</p>
                                            )}
                                        </div>
                                    </div>

                                    {/* Delivery Note */}
                                    <div className="bg-slate-50 border rounded-lg p-6 flex flex-col items-center text-center hover:bg-slate-100 transition-colors">
                                        <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-4 text-orange-600">
                                            <Truck size={32} />
                                        </div>
                                        <h4 className="font-bold text-gray-900 mb-2">Delivery Notes</h4>

                                        {/* Create Action (Top) */}
                                        <div className="w-full mb-2">
                                            {(() => {
                                                const orders = estimate.related_orders || [];
                                                if (orders.length === 0) {
                                                    return (
                                                        <div className="w-full relative group">
                                                            <button className="bg-orange-400 text-white px-4 py-2 rounded text-sm font-semibold w-full opacity-50 cursor-not-allowed">
                                                                Create Delivery Note
                                                            </button>
                                                            <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded hidden group-hover:block whitespace-nowrap z-10">
                                                                Convert to Sale Order first
                                                            </div>
                                                        </div>
                                                    );
                                                } else if (orders.length === 1) {
                                                    return (
                                                        <button
                                                            onClick={() => navigate(`/legacy/delivery/create/${orders[0].so_id}`)}
                                                            className="bg-orange-600 text-white px-4 py-2 rounded text-sm font-semibold hover:bg-orange-700 w-full"
                                                        >
                                                            Create Delivery Note
                                                        </button>
                                                    );
                                                } else {
                                                    return (
                                                        <div className="w-full flex flex-col gap-2">
                                                            <span className="text-xs text-gray-500 mb-1">Create for:</span>
                                                            {orders.map((so: any) => (
                                                                <button
                                                                    key={so.so_id}
                                                                    onClick={() => navigate(`/legacy/delivery/create/${so.so_id}`)}
                                                                    className="bg-orange-600 text-white px-3 py-1.5 rounded text-xs font-semibold hover:bg-orange-700 w-full"
                                                                >
                                                                    {so.so_number}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    );
                                                }
                                            })()}
                                        </div>

                                        {/* View Action (Below Create) */}
                                        <button
                                            onClick={() => navigate(`/legacy/delivery`)}
                                            className="text-orange-600 border border-orange-200 px-4 py-2 rounded text-sm font-medium hover:bg-orange-50 w-full mb-4"
                                        >
                                            View All Delivery Notes
                                        </button>

                                        {/* List existing DNs (Bottom) */}
                                        <div className="w-full space-y-2 flex-grow overflow-y-auto max-h-40">
                                            {(estimate.related_delivery_notes || []).length > 0 ? (
                                                (estimate.related_delivery_notes || []).map((dn: any) => (
                                                    <button
                                                        key={dn.dn_id}
                                                        onClick={() => navigate(`/legacy/delivery/${dn.dn_id}`)}
                                                        className="w-full text-xs flex justify-between items-center bg-white border px-3 py-2 rounded hover:border-orange-400 text-gray-700 shadow-sm"
                                                    >
                                                        <span className="font-mono font-medium">{dn.dn_number}</span>
                                                        <span className="text-gray-400">{new Date(dn.dn_date).toLocaleDateString()}</span>
                                                    </button>
                                                ))
                                            ) : (
                                                <p className="text-sm text-gray-400 italic py-2">No delivery notes yet</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )
                }
            </div >

            <LabourCostChartModal
                isOpen={showLabourModal}
                onClose={() => setShowLabourModal(false)}
                onSelectRate={async ({ aedKg }) => {
                    if (!estimate) return;
                    setBusy(true);
                    try {
                        await legacyApi.estimates.updateProductionSettings(estimate.estimate_id, {
                            prod_aed_per_kg: aedKg,
                            direct_electricity_perc: (estimate.direct_electricity_perc || 0) || 0.06,
                            direct_wastage_perc: (estimate.direct_wastage_perc || 0) || 0.10,
                        });
                        const updated = await legacyApi.estimates.getDetail(estimate.estimate_id);
                        setEstimate(updated);
                        toast.success(`Labour cost updated to ${aedKg} AED/Kg successfully.`);
                    } catch (e: any) {
                        toast.error("Failed to update labour rate: " + e.message);
                    } finally {
                        setBusy(false);
                        setShowLabourModal(false);
                    }
                }}
            />


            {/* GRP Materials Modal */}
            {
                showGRPMaterialsModal && selectedPartForMaterials && estimate && (() => {
                    const selectedPart = estimate.parts.find(p => p.estimate_part_id === selectedPartForMaterials);
                    if (!selectedPart) return null;

                    const areaTotal = (selectedPart.area_total ?? (selectedPart.area_m2 * selectedPart.quantity)) || 0;
                    const totalMaterialCost = (grpDraft || []).reduce((sum: number, m: any) => {
                        const cost =
                            areaTotal
                            * (parseFloat(m.area_coverage || 0) || 0)
                            * (parseFloat(m.application_layer || 0) || 0)
                            * (parseFloat(m.aed_cost || 0) || 0);
                        return sum + cost;
                    }, 0);
                    const modalLabourRate = parseFloat(grpDraftLabourRate || '0') || 0;
                    const modalLabourCost = areaTotal * modalLabourRate;

                    return (
                        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                            <div className="bg-white rounded-lg shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                                <div className="bg-slate-700 px-6 py-4 flex justify-between items-center">
                                    <div>
                                        <h2 className="text-xl font-bold text-white">Production Cost (GRP Materials)</h2>
                                        <p className="text-slate-300 text-sm mt-1">
                                            Estimate: E-{String(estimate.estimate_id).padStart(4, '0')} &gt; {selectedPart.part_name}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={async () => {
                                                if (!selectedPartForMaterials) return;
                                                try {
                                                    setSavingGrpDraft(true);
                                                    const items = (grpDraft || [])
                                                        .filter((r: any) => (parseInt(r.grp_material_id || '0', 10) || 0) > 0)
                                                        .map((r: any) => ({
                                                            grp_material_id: parseInt(r.grp_material_id, 10),
                                                            aed_cost: parseFloat(r.aed_cost || 0) || 0,
                                                            area_coverage: parseFloat(r.area_coverage || 0) || 0,
                                                            application_layer: parseInt(r.application_layer || '0', 10) || 0,
                                                        }));
                                                    await legacyApi.estimates.updateGrpProduction(
                                                        selectedPartForMaterials,
                                                        parseFloat(grpDraftLabourRate || '0') || 0,
                                                        items as any
                                                    );
                                                    const updated = await legacyApi.estimates.getDetail(estimate.estimate_id);
                                                    setEstimate(updated);
                                                    toast.success('GRP production saved.');
                                                    setShowGRPMaterialsModal(false);
                                                    setSelectedPartForMaterials(null);
                                                    setGrpDraft(null);
                                                    setGrpDraftLabourRate('');
                                                } catch (e: any) {
                                                    toast.error(e?.message || 'Failed to save GRP production');
                                                } finally {
                                                    setSavingGrpDraft(false);
                                                }
                                            }}
                                            disabled={!canEdit || savingGrpDraft}
                                            className="bg-slate-800 hover:bg-slate-900 text-white px-4 py-2 rounded font-semibold transition-colors disabled:opacity-50"
                                        >
                                            {savingGrpDraft ? 'SAVING...' : 'Save'}
                                        </button>
                                        <button
                                            onClick={() => {
                                                setShowGRPMaterialsModal(false);
                                                setSelectedPartForMaterials(null);
                                                setGrpDraft(null);
                                                setGrpDraftLabourRate('');
                                            }}
                                            className="bg-slate-600 hover:bg-slate-800 text-white px-4 py-2 rounded font-semibold transition-colors"
                                        >
                                            Close
                                        </button>
                                    </div>
                                </div>

                                <div className="flex-1 overflow-auto p-6">
                                    <div className="flex items-center justify-between mb-3">
                                        <div className="text-sm text-slate-600">
                                            Area total: <span className="font-mono font-semibold tabular-nums">{areaTotal.toFixed(2)}</span> m2
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <div className="text-sm text-slate-600">
                                                Default labour: <span className="font-mono font-semibold tabular-nums">{(estimate.prod_aed_per_m2_grp || 0).toFixed(2)}</span> AED/m2
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className="text-sm text-slate-600">Part labour AED/m2</span>
                                                <input
                                                    type="number"
                                                    className="w-28 border rounded px-2 py-1 text-right font-mono"
                                                    value={grpDraftLabourRate}
                                                    disabled={!canEdit}
                                                    onChange={(e) => setGrpDraftLabourRate(e.target.value)}
                                                    placeholder="Blank = default"
                                                />
                                            </div>
                                        </div>
                                        <button
                                            className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-3 py-2 rounded font-semibold transition-colors disabled:opacity-50"
                                            disabled={!canEdit}
                                            onClick={() => {
                                                setGrpDraft((prev) => ([
                                                    ...(prev || []),
                                                    { grp_material_id: '', aed_cost: 0, area_coverage: 0, application_layer: 1 }
                                                ]));
                                            }}
                                        >
                                            + Add Material
                                        </button>
                                    </div>

                                    <table className="w-full text-sm border-collapse">
                                        <thead>
                                            <tr className="bg-slate-100 border-b-2 border-slate-300">
                                                <th className="px-4 py-3 text-left font-semibold text-slate-700">Material</th>
                                                <th className="px-4 py-3 text-right font-semibold text-slate-700">AED Cost</th>
                                                <th className="px-4 py-3 text-right font-semibold text-slate-700">Area, m2</th>
                                                <th className="px-4 py-3 text-right font-semibold text-slate-700">Area Coverage (kg/m2)</th>
                                                <th className="px-4 py-3 text-right font-semibold text-slate-700">Layers</th>
                                                <th className="px-4 py-3 text-right font-semibold text-slate-700 bg-blue-50">Material Cost</th>
                                                <th className="px-4 py-3 text-center font-semibold text-slate-700"></th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-200">
                                            {(grpDraft || []).length == 0 ? (
                                                <tr>
                                                    <td colSpan={7} className="px-4 py-8 text-center text-slate-500 italic">
                                                        No GRP production materials yet. Click "+ Add Material" to start.
                                                    </td>
                                                </tr>
                                            ) : (grpDraft || []).map((row: any, idx: number) => {
                                                const cost =
                                                    areaTotal
                                                    * (parseFloat(row.area_coverage || 0) || 0)
                                                    * (parseFloat(row.application_layer || 0) || 0)
                                                    * (parseFloat(row.aed_cost || 0) || 0);

                                                return (
                                                    <tr key={idx} className="hover:bg-slate-50 transition-colors">
                                                        <td className="px-4 py-3">
                                                            <select
                                                                className="w-full border rounded px-2 py-1 text-sm"
                                                                value={row.grp_material_id ?? ''}
                                                                disabled={!canEdit}
                                                                onChange={(e) => {
                                                                    const nextId = parseInt(e.target.value || '0', 10) || '';
                                                                    const found = (grpCatalog || []).find((m: any) => m.grp_material_id === nextId);
                                                                    setGrpDraft((prev) => (prev || []).map((p: any, i: number) => {
                                                                        if (i !== idx) return p;
                                                                        return {
                                                                            ...p,
                                                                            grp_material_id: nextId,
                                                                            aed_cost: found?.aed_cost ?? p.aed_cost,
                                                                            area_coverage: found?.area_coverage ?? p.area_coverage,
                                                                            material_name: found?.material_name ?? p.material_name,
                                                                            unit: found?.unit ?? p.unit,
                                                                        };
                                                                    }));
                                                                }}
                                                            >
                                                                <option value="">Select material...</option>
                                                                {(grpCatalog || []).map((m: any) => (
                                                                    <option key={m.grp_material_id} value={m.grp_material_id}>
                                                                        {m.material_name}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        </td>
                                                        <td className="px-4 py-3 text-right tabular-nums">
                                                            <input
                                                                type="number"
                                                                className="w-24 border rounded px-2 py-1 text-right"
                                                                value={row.aed_cost ?? 0}
                                                                disabled={!canEdit}
                                                                onChange={(e) => {
                                                                    const v = parseFloat(e.target.value) || 0;
                                                                    setGrpDraft((prev) => (prev || []).map((p: any, i: number) => (i === idx ? { ...p, aed_cost: v } : p)));
                                                                }}
                                                            />
                                                        </td>
                                                        <td className="px-4 py-3 text-right tabular-nums">{areaTotal.toFixed(2)}</td>
                                                        <td className="px-4 py-3 text-right tabular-nums">
                                                            <input
                                                                type="number"
                                                                className="w-28 border rounded px-2 py-1 text-right"
                                                                value={row.area_coverage ?? 0}
                                                                disabled={!canEdit}
                                                                onChange={(e) => {
                                                                    const v = parseFloat(e.target.value) || 0;
                                                                    setGrpDraft((prev) => (prev || []).map((p: any, i: number) => (i === idx ? { ...p, area_coverage: v } : p)));
                                                                }}
                                                            />
                                                        </td>
                                                        <td className="px-4 py-3 text-right tabular-nums">
                                                            <input
                                                                type="number"
                                                                className="w-20 border rounded px-2 py-1 text-right"
                                                                value={row.application_layer ?? 0}
                                                                disabled={!canEdit}
                                                                onChange={(e) => {
                                                                    const v = parseInt(e.target.value || '0', 10) || 0;
                                                                    setGrpDraft((prev) => (prev || []).map((p: any, i: number) => (i === idx ? { ...p, application_layer: v } : p)));
                                                                }}
                                                            />
                                                        </td>
                                                        <td className="px-4 py-3 text-right tabular-nums font-bold text-blue-900 bg-blue-50">{cost.toFixed(2)}</td>
                                                        <td className="px-4 py-3 text-center">
                                                            <button
                                                                disabled={!canEdit}
                                                                onClick={() => setGrpDraft((prev) => (prev || []).filter((_: any, i: number) => i !== idx))}
                                                                className="bg-red-500 hover:bg-red-600 text-white text-xs px-2 py-1 rounded font-semibold transition-colors disabled:opacity-50"
                                                            >
                                                                Delete
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                                <div className="bg-slate-100 px-6 py-4 border-t flex justify-between items-center gap-8">
                                    <div className="text-xs text-slate-600">
                                        Labour is maintained per part. Leave the part labour blank or zero to inherit the estimate default.
                                    </div>
                                    <div className="text-right">
                                        <div className="text-slate-600 text-sm font-medium">Material: <span className="font-mono">{totalMaterialCost.toFixed(2)}</span></div>
                                        <div className="text-slate-600 text-sm font-medium">Labour: <span className="font-mono">{modalLabourCost.toFixed(2)}</span></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })()
            }


            {/* BOM Materials Modal */}
            {
                showBOMMaterialsModal && selectedPartForBOM && estimate && (() => {
                    const selectedPart = estimate.parts.find(p => p.estimate_part_id === selectedPartForBOM);
                    if (!selectedPart) return null;

                    const bomItems = bomDraft || [];
                    const totalQuantity = bomItems.reduce((sum: number, item: any) => sum + (parseFloat(item.quantity || 0) || 0), 0);
                    const totalCost = bomItems.reduce((sum: number, item: any) => {
                        const qty = parseFloat(item.quantity || 0) || 0;
                        const rate = parseFloat(item.unit_rate || 0) || 0;
                        return sum + (qty * rate);
                    }, 0);

                    return (
                        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                            <div className="bg-white rounded-lg shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                                {/* Header */}
                                <div className="bg-slate-700 px-6 py-4 flex justify-between items-center">
                                    <div>
                                        <h2 className="text-xl font-bold text-white">BOM Materials</h2>
                                        <p className="text-slate-300 text-sm mt-1">
                                            Estimate ID: {estimate.estimate_id ? `E-${String(estimate.estimate_id).padStart(4, '0')}` : 'N/A'} &gt; {selectedPart.part_name}, Dim: {selectedPart.dimension || 'N/A'}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={async () => {
                                                if (!selectedPartForBOM) return;
                                                try {
                                                    setSavingBomDraft(true);
                                                    const items = (bomDraft || [])
                                                        .filter((r: any) => ((parseInt(r.material_id || '0', 10) || 0) > 0) || ((parseInt(r.bom_material_id || '0', 10) || 0) > 0))
                                                        .map((r: any) => ({
                                                            bom_material_id: (parseInt(r.bom_material_id || '0', 10) || 0) > 0 ? parseInt(r.bom_material_id, 10) : undefined,
                                                            material_id: (parseInt(r.material_id || '0', 10) || 0) > 0 ? parseInt(r.material_id, 10) : undefined,
                                                            quantity: parseFloat(r.quantity || 0) || 0,
                                                            unit_rate: parseFloat(r.unit_rate || 0) || 0,
                                                        }));
                                                    await legacyApi.bom.updatePart(selectedPartForBOM, items);
                                                    const updated = await legacyApi.estimates.getDetail(estimate.estimate_id);
                                                    setEstimate(updated);
                                                    toast.success('BOM materials saved.');
                                                    setShowBOMMaterialsModal(false);
                                                    setSelectedPartForBOM(null);
                                                    setBomDraft(null);
                                                } catch (e: any) {
                                                    toast.error(e?.message || 'Failed to save BOM materials');
                                                } finally {
                                                    setSavingBomDraft(false);
                                                }
                                            }}
                                            disabled={!canEdit || savingBomDraft}
                                            className="bg-slate-800 hover:bg-slate-900 text-white px-4 py-2 rounded font-semibold transition-colors disabled:opacity-50"
                                        >
                                            {savingBomDraft ? 'SAVING...' : 'Save'}
                                        </button>
                                        <button
                                            onClick={() => {
                                                setShowBOMMaterialsModal(false);
                                                setSelectedPartForBOM(null);
                                                setBomDraft(null);
                                            }}
                                            className="bg-slate-600 hover:bg-slate-800 text-white px-4 py-2 rounded font-semibold transition-colors"
                                        >
                                            Close
                                        </button>
                                    </div>
                                </div>

                                {/* Table */}
                                <div className="flex-1 overflow-auto p-6">
                                    <div className="flex items-center justify-between mb-3">
                                        <div className="text-sm text-slate-600">
                                            Select materials and enter quantity/unit rate.
                                        </div>
                                        <button
                                            className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-3 py-2 rounded font-semibold transition-colors disabled:opacity-50"
                                            disabled={!canEdit}
                                            onClick={() => {
                                                setBomDraft((prev) => ([
                                                    ...(prev || []),
                                                    { bom_material_id: '', material_id: '', unit_rate: 0, quantity: 1, material_name: '' }
                                                ]));
                                            }}
                                        >
                                            + Add Material
                                        </button>
                                    </div>
                                    <table className="w-full text-sm border-collapse">
                                        <thead>
                                            <tr className="bg-slate-100 border-b-2 border-slate-300">
                                                <th className="px-4 py-3 text-left font-semibold text-slate-700">Material</th>
                                                <th className="px-4 py-3 text-right font-semibold text-slate-700">Unit Rate</th>
                                                <th className="px-4 py-3 text-right font-semibold text-slate-700">Quantity</th>
                                                <th className="px-4 py-3 text-right font-semibold text-slate-700 bg-blue-50">Total Cost</th>
                                                <th className="px-4 py-3 text-center font-semibold text-slate-700"></th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-200">
                                            {bomItems.length > 0 ? (
                                                bomItems.map((item: any, idx: number) => {
                                                    const qty = parseFloat(item.quantity || 0) || 0;
                                                    const rate = parseFloat(item.unit_rate || 0) || 0;
                                                    const cost = qty * rate;
                                                    return (
                                                        <tr key={idx} className="hover:bg-slate-50 transition-colors">
                                                            <td className="px-4 py-3">
                                                                {(() => {
                                                                    const selectedMaterialId = item.material_id
                                                                        ?? ((bomCatalog || []).find((m: any) => m.bom_material_id === item.bom_material_id)?.material_id ?? '');
                                                                    return (
                                                                <select
                                                                    className="w-full border rounded px-2 py-1 text-sm"
                                                                    value={selectedMaterialId}
                                                                    disabled={!canEdit}
                                                                    onChange={(e) => {
                                                                        const nextMaterialId = parseInt(e.target.value || '0', 10) || '';
                                                                        const found = (bomCatalog || []).find((m: any) => m.material_id === nextMaterialId);
                                                                        setBomDraft((prev) => (prev || []).map((p: any, i: number) => {
                                                                            if (i !== idx) return p;
                                                                            return {
                                                                                ...p,
                                                                                material_id: nextMaterialId,
                                                                                bom_material_id: found?.bom_material_id ?? '',
                                                                                unit_rate: found?.unit_rate ?? p.unit_rate,
                                                                                material_name: found?.material_name ?? p.material_name,
                                                                            };
                                                                        }));
                                                                    }}
                                                                >
                                                                    <option value="">Select material...</option>
                                                                    {(bomCatalog || []).map((m: any) => (
                                                                        <option key={m.material_id} value={m.material_id}>
                                                                            {m.material_name}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                                    );
                                                                })()}
                                                            </td>
                                                            <td className="px-4 py-3 text-right tabular-nums">
                                                                <input
                                                                    type="number"
                                                                    className="w-24 border rounded px-2 py-1 text-right"
                                                                    value={item.unit_rate ?? 0}
                                                                    disabled={!canEdit}
                                                                    onChange={(e) => {
                                                                        const v = parseFloat(e.target.value) || 0;
                                                                        setBomDraft((prev) => (prev || []).map((p: any, i: number) => (i === idx ? { ...p, unit_rate: v } : p)));
                                                                    }}
                                                                />
                                                            </td>
                                                            <td className="px-4 py-3 text-right tabular-nums">
                                                                <input
                                                                    type="number"
                                                                    className="w-24 border rounded px-2 py-1 text-right"
                                                                    value={item.quantity ?? 0}
                                                                    disabled={!canEdit}
                                                                    onChange={(e) => {
                                                                        const v = parseFloat(e.target.value) || 0;
                                                                        setBomDraft((prev) => (prev || []).map((p: any, i: number) => (i === idx ? { ...p, quantity: v } : p)));
                                                                    }}
                                                                />
                                                            </td>
                                                            <td className="px-4 py-3 text-right tabular-nums font-bold text-blue-900 bg-blue-50">{cost.toFixed(2)}</td>
                                                            <td className="px-4 py-3 text-center">
                                                                <button
                                                                    disabled={!canEdit}
                                                                    onClick={() => setBomDraft((prev) => (prev || []).filter((_: any, i: number) => i !== idx))}
                                                                    className="bg-red-500 hover:bg-red-600 text-white text-xs px-2 py-1 rounded font-semibold transition-colors disabled:opacity-50"
                                                                >
                                                                    Delete
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    );
                                                })
                                            ) : (
                                                <tr>
                                                    <td colSpan={5} className="px-4 py-8 text-center text-slate-500 italic">
                                                        No BOM materials found for this part
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Footer */}
                                <div className="bg-slate-100 px-6 py-4 border-t">
                                    <div className="grid grid-cols-2 gap-8">
                                        <div className="text-right">
                                            <span className="text-slate-600 text-sm font-medium mr-4">Total Quantity</span>
                                            <span className="text-xl font-black text-slate-900 tabular-nums">{totalQuantity.toFixed(0)}</span>
                                        </div>
                                        <div className="text-right">
                                            <span className="text-slate-600 text-sm font-medium mr-4">Total Cost</span>
                                            <span className="text-2xl font-black text-slate-900 tabular-nums">{totalCost.toFixed(2)}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })()
            }

            <DesignMixModal
                isOpen={showDesignModal}
                onClose={() => setShowDesignModal(false)}
                estimateId={estimateNumber}
                currentDesignMixId={estimate.design_mix_model_id}
                currentGrcTypeId={estimate.grc_type_id}
                onSave={async (designMixId, grcTypeId) => {
                    if (!estimate) return;
                    setBusy(true);
                    try {
                        await legacyApi.estimates.updateDesignMix(estimate.estimate_id, designMixId, grcTypeId);
                        const updated = await legacyApi.estimates.getDetail(estimate.estimate_id);
                        setEstimate(updated);
                        toast.success("Design Mix updated successfully.");
                    } catch (e: any) {
                        toast.error("Failed to update Design Mix: " + e.message);
                    } finally {
                        setBusy(false);
                        setShowDesignModal(false);
                    }
                }}
            />

            {/* Design Premium Modal (legacy DP button from Cost Breakdown) */}
            {
                showGrcOverrideModal && estimate && (() => {
                    const selectedPart = (estimate.parts || []).find((part) => part.estimate_part_id === selectedGrcOverridePartId);
                    if (!selectedPart) return null;
                    return (
                        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                            <div className="bg-white rounded-lg shadow-lg w-full max-w-lg p-6">
                                <div className="flex justify-between items-center mb-4">
                                    <div>
                                        <h3 className="text-lg font-bold">GRC Part Override</h3>
                                        <p className="text-sm text-gray-500 mt-1">
                                            Leave any field blank to inherit the estimate default for this part.
                                        </p>
                                    </div>
                                    <button onClick={() => setShowGrcOverrideModal(false)} className="text-gray-500 hover:text-gray-700">
                                        &times;
                                    </button>
                                </div>
                                <div className="space-y-4">
                                    <div className="text-sm text-gray-600">
                                        Estimate: <span className="font-mono">{estimateNumber}</span> &gt; <span className="font-semibold">{selectedPart.part_name}</span>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Design Mix</label>
                                        <select
                                            className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 outline-none"
                                            value={grcOverrideDraft.design_mix_model_id}
                                            onChange={(e) => setGrcOverrideDraft((prev) => ({ ...prev, design_mix_model_id: e.target.value }))}
                                            disabled={!canEdit}
                                        >
                                            <option value="">Use estimate default</option>
                                            {designMixModels.map((mix: any) => (
                                                <option key={mix.id} value={mix.id}>
                                                    {mix.name || `Mix ${mix.id}`}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">GRC Type</label>
                                        <select
                                            className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 outline-none"
                                            value={grcOverrideDraft.grc_type_id}
                                            onChange={(e) => setGrcOverrideDraft((prev) => ({ ...prev, grc_type_id: e.target.value }))}
                                            disabled={!canEdit}
                                        >
                                            <option value="">Use estimate default</option>
                                            {grcTypes.map((type: any) => (
                                                <option key={type.id} value={type.id}>
                                                    {type.name || `Type ${type.id}`}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Labour AED / KG</label>
                                        <input
                                            type="number"
                                            className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 outline-none"
                                            value={grcOverrideDraft.labour_rate_per_kg}
                                            onChange={(e) => setGrcOverrideDraft((prev) => ({ ...prev, labour_rate_per_kg: e.target.value }))}
                                            disabled={!canEdit}
                                            placeholder="Use estimate default"
                                        />
                                    </div>
                                    <div className="flex justify-end gap-2 pt-2">
                                        <button
                                            onClick={() => setShowGrcOverrideModal(false)}
                                            className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={handleSaveGrcOverride}
                                            disabled={!canEdit || savingGrcOverride}
                                            className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 font-medium disabled:opacity-50"
                                        >
                                            {savingGrcOverride ? 'Saving...' : 'Save'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })()
            }
            {/* Convert Modal */}
            {
                showOrdersModal && estimate && (
                    <EstimateOrdersModal
                isOpen={showOrdersModal}
                onClose={() => setShowOrdersModal(false)}
                estimateId={estimate.estimate_id}
                estimateRef={estimateNumber}
                customerName={estimate.customer_name}
                customerId={Number(estimate.customer_id || 0) || undefined}
                revisionNo={estimate.revision_no}
            />
                )
            }
        </div >
    );
};
